/**
 * Property panel UI (mounted in the left sidebar’s Design tab). Shows context-sensitive
 * properties for the selected section or element. Includes theme editing (colors, font,
 * section-level overrides, and swap variant UI).
 */

import { eventBus } from '../core/events.js';
import { SITE_EFFECT_DEFAULTS, store } from '../core/state.js';
import { reportError } from '../core/report-error.js';
import { createButton, setButtonBusy } from './components.js';
import { icon } from './icons.js';
import { illustration } from './illustrations.js';
import { kindLabel } from '../shared/element-kind.js';
import {
  isUnknownCategory,
  shouldUseTemplateSectionThumbnail
} from '../shared/section-categories.js';
import { assembleLiveTemplateCSS } from '../shared/template-support-css.js';
import {
  getCategorySections,
  swapSection,
  isTemplateSpecificSectionVariant,
  resetMobileMenuToStock
} from '../sections/section-manager.js';
import { checkNavHtmlEligibility, MARKER_ATTRS } from '../shared/mobile-menu-contract.js';
import { swapSvgToImage } from '../editing/image-editor.js';
import { openReplaceDialog } from '../editing/replace-dispatcher.js';
import {
  getThemeVars,
  updateThemeVar,
  commitThemeChange,
  getCurrentTheme,
  hasOpenTransaction,
  setThemeVar
} from '../theme/theme-editor.js';
import {
  FONT_OPTIONS,
  RECOMMENDED_FONTS,
  buildFontUpdatePayload,
  getCurrentFont,
  updateFont,
  getCurrentHeadingFont,
  updateHeadingFont,
  getCurrentHeadingFontWeight,
  updateHeadingFontWeight,
  ensureUIFontsLoaded,
  ensureUIFontLoaded
} from '../theme/font-selector.js';
import { upgradeToFontCombobox } from './font-combobox.js';
import {
  COLOR_PALETTES,
  FONT_PAIRINGS,
  THEME_PRESETS,
  applyPalette,
  applyThemePreset,
  applyFontPairing,
  getActivePaletteId,
  getActiveThemePresetId,
  paletteWouldOverwriteCustomColors
} from '../theme/theme-presets.js';
import { bridge } from '../editing/bridge.js';
import {
  RESPONSIVE_VIEWPORTS,
  applyElementOverrides,
  getElementOverridesForViewport,
  resolveSectionByInstance
} from '../editing/section-utils.js';
import { getActiveViewportName, getResponsiveOverrideViewport } from './viewport-controls.js';
import { rafGate } from '../shared/timing.js';
import { SPACING_PRESETS } from '../shared/spacing-presets.js';
import { expandPaletteColors, humanizeThemeToken } from '../shared/theme-tokens.js';
import {
  colorToHex,
  colorToPickerHex,
  isValidCssColor,
  parseCssVarReference,
  resolveCssColorValue
} from '../shared/color-utils.js';
import {
  collectInlineCustomProperties,
  collectStylesheetCustomProperties,
  collectStylesheetPaintValues,
  replaceStylesheetPaintValues
} from '../shared/css-declaration-utils.js';

import {
  normalizeHex,
  colorValueToHex,
  escapeHTML,
  escapeAttr,
  matchCSSDeclaration,
  isDisplayNoneRule,
  getContrastAssessment,
  getDesignColorOptions,
  rememberRecentColor,
  renderDesignColorSwatches,
  renderTokenFirstColorInput,
  syncColorSwatches
} from './design-drawers/_internals.js';
import { renderColorsContent } from './design-drawers/colors-drawer.js';
import { renderFontsContent } from './design-drawers/fonts-drawer.js';
import { renderFormsContent } from './design-drawers/forms-drawer.js';
import { renderLayoutContent } from './design-drawers/layout-drawer.js';
import {
  summarizeColors,
  summarizeFonts,
  summarizeForms,
  summarizeLayout
} from './design-drawers/row-summaries.js';
import { flashError } from './feedback.js';
import { showConfirmModal, showUndoToast } from './shell.js';
import {
  escapeForAttrSelector,
  injectEditableAttrs,
  stripTempAttrs,
  deleteEditableFromHTML,
  pruneOrphanOverrides
} from '../editing/editable-utils.js';
import { hasPanelFor, hasOpenPanels } from '../editing/glass-panels.js';
import {
  CLIENT_LINK_BINDINGS,
  makeClientLinkMarker,
  parseClientLinkMarker,
  isPlaceholderTextForBinding,
  getDisplayTextForBinding
} from '../shared/client-info.js';
import {
  renderSectionCard,
  renderThemePresetCard,
  renderPalettePresetCard,
  renderFontPresetCard,
  renderFormPresetCard,
  renderSegmented,
  themePresetSnapshot,
  paletteSnapshot,
  pairingSnapshot,
  wirePreviewOnHover,
  revertActivePreviewIfAny
} from './design-widgets.js';
import {
  FORM_SHAPES,
  FORM_STYLES,
  FORM_SUBMIT_STYLES,
  formShapeSnapshot,
  formStyleSnapshot,
  formSubmitSnapshot
} from './forms-presets.js';
import {
  catalogSectionThumbnail,
  installThumbnailFallback,
  templateSectionThumbnail,
  thumbnailAttrs
} from '../shared/thumbnail-url.js';

/** `.pb-swap-card` fills a ~150px grid track — well inside the 400w derivative. */
const SWAP_CARD_THUMB_SIZES = '150px';

/** BC-6: every drawer `_renderSectionDrawerBody` can fill. */
const SECTION_DRAWER_IDS = new Set([
  'background',
  'spacing',
  'custom-css',
  'columns',
  'entrance',
  'schedule',
  'decorative-svg',
  'form'
]);

const DESIGN_BUTTON_DEFAULTS = Object.freeze({
  borderRadius: '0.25rem',
  padding: '0 1.5rem',
  backgroundColor: '#ff6a3e',
  textColor: '#ffffff',
  hoverEffect: 'none'
});
import { SEOPanel } from './seo-panel.js';
import { sectionHasForm } from './form-builder.js';
// `renderDecorativeSvgPanel` is no longer imported directly — Phase 7's
// `svg-drawer.js` is the only consumer now (legacy inline-panel path is gone).
// `FormBuilder` is no longer imported directly — Phase 8's `form-drawer.js`
// owns its lifecycle now (legacy inline-panel path is gone).
import {
  applySectionOverride,
  getSectionOverrideCount,
  resetAllSectionOverrides,
  resetAllSectionOverridesBulk,
  summarizeOverrideAcross,
  SPACING_OVERRIDE_FIELDS
} from './section-properties/apply-section-override.js';
import { openPopoverMenu, closeActivePopoverMenu } from './popover-menu.js';
import { openReplaceBrowser, replaceLegacyEnabled } from './replace-browser.js';
import {
  openSectionDrawer,
  closeActiveSectionDrawer,
  getActiveSectionDrawerId,
  refreshActiveSectionDrawerBody,
  setActiveSectionDrawerTitle
} from './section-properties/drawer.js';
import {
  renderInspectorRow,
  renderInspectorContentDivider
} from './section-properties/inspector-row.js';
import { getOverrideState } from './section-properties/override-state.js';
import { renderBackgroundDrawer } from './section-properties/background-drawer.js';
import { renderSectionSpacingDrawer } from './section-properties/section-spacing-drawer.js';
import { renderCustomCssDrawer } from './section-properties/css-drawer.js';
import { renderColumnsDrawer } from './section-properties/columns-drawer.js';
import {
  renderEntranceDrawer,
  renderEntranceSummary
} from './section-properties/entrance-drawer.js';
import {
  renderScheduleDrawer,
  renderScheduleSummary,
  scheduleDrawerEnabled
} from './section-properties/schedule-drawer.js';
import { ENTRANCE_OVERRIDE_FIELD } from '../shared/entrance-presets.js';
import { renderSvgDrawer } from './section-properties/svg-drawer.js';
import { renderFormDrawer } from './section-properties/form-drawer.js';
import {
  renderBackgroundSummary,
  renderSpacingSummary,
  renderCustomCssSummary,
  renderColumnsSummary,
  renderDecorativeSvgSummary,
  renderFormSummary
} from './section-properties/row-summaries.js';
import { detectMultiItemGroups } from '../editing/section-group-detection.js';
import { discoverNavLinks } from '../shared/nav-link-discovery.js';
import { pageDestinationOptions, pageForInternalHref } from '../shared/page-routes.js';

/** Order-sensitive id-list comparison — both arrays are document-ordered. */
function idsChanged(previous, next) {
  if (!previous) return true;
  if (previous.length !== next.length) return true;
  return previous.some((id, i) => id !== next[i]);
}

// pb-section-panel-v2 flag removed in Phase 10 — the v2 inspector + drawers
// are now the only render path. The flag previously gated Phases 2-9 for
// regression testing during the rewrite; the legacy `<details>`-disclosure
// chrome and 3-button action row are gone.

export class PropertyPanel {
  constructor({ themeContainer, sectionContainer }) {
    this._themeEl = themeContainer;
    this._sectionEl = sectionContainer;
    this._selectedInstance = null;
    this._activeTab = 'theme';
    this._activeSvgData = null;
    // Live-fetched template CSS cache. Signature derives from sourceTemplateDomain
    // plus the sorted set of sections' templateSiteName values; when it changes we
    // kick off a fetch and refresh design panels once the new CSS resolves. The
    // token guards against an older in-flight fetch overwriting a newer one.
    this._templateCSSCache = { signature: '', css: '' };
    this._templateCSSToken = 0;
    // Preserve .pb-props-body spacing (padding, overflow)
    this._themeEl.classList.add('pb-props-body');
    this._sectionEl.classList.add('pb-props-body');
    this._renderThemePanel();
    this._listen();
    // Kick off the first fetch.
    this._refreshTemplateCSSCacheIfNeeded();

    this._sectionEl.addEventListener('click', (e) => {
      if (e.target.closest('.pb-section-back-btn')) {
        this._backToDesign();
      } else if (e.target.closest('[data-action="open-shared-design"]')) {
        this._backToDesign();
      }
    });
    this._sectionEl.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!this._navEditorRefreshPending || this._sectionEl.contains(document.activeElement))
          return;
        this._navEditorRefreshPending = false;
        this._refreshNavLinkEditorIfNeeded();
      }, 0);
    });
  }

  _computeTemplateCSSSignature(state) {
    if (!state) return '';
    const primary =
      typeof state.sourceTemplateDomain === 'string' ? state.sourceTemplateDomain.trim() : '';
    const sites = new Set();
    const collectSiteName = (section) => {
      const name =
        typeof section?.templateSiteName === 'string' ? section.templateSiteName.trim() : '';
      if (name) sites.add(name);
    };
    if (state.sharedSections?.navigation) collectSiteName(state.sharedSections.navigation);
    if (state.sharedSections?.footer) collectSiteName(state.sharedSections.footer);
    if (Array.isArray(state.pages)) {
      for (const page of state.pages) {
        if (!Array.isArray(page?.sections)) continue;
        for (const section of page.sections) collectSiteName(section);
      }
    }
    return [primary, ...Array.from(sites).sort()].join('|');
  }

  _refreshTemplateCSSCacheIfNeeded() {
    const state = store.getState();
    const signature = this._computeTemplateCSSSignature(state);
    if (signature === this._templateCSSCache.signature) return;

    const token = ++this._templateCSSToken;
    assembleLiveTemplateCSS(state)
      .then((css) => {
        if (token !== this._templateCSSToken) return; // Stale fetch; newer one started.
        this._templateCSSCache = { signature, css: String(css || '') };
        // Refresh design board so values derived from template CSS (button styles,
        // CSS custom properties) reflect the freshly-fetched stylesheet.
        try {
          this._refreshAllDesignSections();
        } catch (err) {
          reportError('PropertyPanel', err, {
            user: 'status',
            message: 'Design refresh failed',
            detail: 'design refresh after template CSS load failed'
          });
        }
      })
      .catch((err) => {
        if (token !== this._templateCSSToken) return;
        reportError('PropertyPanel', err, {
          user: 'status',
          message: 'Template CSS unavailable',
          detail: 'live template CSS fetch failed'
        });
      });
  }

  // ── Theme / Section view toggling ──

  _showThemeView(focusFirst = false) {
    this._themeEl.hidden = false;
    this._themeEl.removeAttribute('aria-hidden');
    this._sectionEl.hidden = true;
    this._sectionEl.setAttribute('aria-hidden', 'true');
    if (focusFirst) {
      const target = this._themeEl.querySelector('.pb-design-card');
      if (target) requestAnimationFrame(() => target.focus());
    }
  }

  _showSectionView() {
    this._themeEl.hidden = true;
    this._themeEl.setAttribute('aria-hidden', 'true');
    this._sectionEl.hidden = false;
    this._sectionEl.removeAttribute('aria-hidden');
    requestAnimationFrame(() => {
      const backBtn = this._sectionEl.querySelector('.pb-section-back-btn');
      if (backBtn) backBtn.focus();
    });
  }

  _backToDesign() {
    this._selectedInstance = null;
    this._activeSvgData = null;
    eventBus.emit('section-deselected');
    this._activeTab = 'theme';
    eventBus.emit('sidebar:tab-changed', { tab: 'theme' });
    this._showThemeView(true);
    this._renderThemePanel();
  }

  _renderBackButton() {
    return `<button class="pb-section-back-btn" type="button">&#8249; Back to Design</button>`;
  }

  // ── Design tab: single-surface design board ──

  _renderThemePanel() {
    this._renderDesignBoard();
  }

  // Phase 0 - Design board v2 shell. Six section <article>s; bodies are
  // placeholder until Phases 2-6 fill them. _designView stays at 'landing'
  // so the existing _syncThemeInputs branch (line 1233) treats the board as
  // the landing surface - preview refreshes still call into it.
  //
  // Phase 1 - sections are built via renderSectionCard so the new primitive
  // is exercised end-to-end.
  // Phase 2 - Styles section ships with theme-preset cards + hover-preview.
  //
  // Idempotent re-render: if the board DOM already exists (back-from-
  // section-view, undo/redo, etc.), skip the rebuild and let per-section
  // refresh methods sync state. Preserves scroll position; avoids tearing
  // down hover-preview wiring on every state mutation.
  // Phase B.2 — Design board v3: compact overview with drawer rows.
  //
  // Top: Styles preset rail (theme presets stay inline).
  // Middle: four `renderInspectorRow` rows — Colors, Fonts, Buttons & Effects,
  // Forms —
  //         that open their respective drawers via `openSectionDrawer`.
  //
  // Each drawer module owns its own `state-changed` subscription via the
  // teardown function returned from renderXContent. While a drawer is
  // closed, the overview rows only refresh their summary text — no DOM
  // teardown / rebuild on every state change.
  _renderDesignBoard() {
    this._designView = 'landing';

    if (this._board && this._themeEl.contains(this._board)) {
      this._refreshAllDesignSections();
      return;
    }

    this._teardownDesignBoardCleanups();

    this._themeEl.innerHTML = `
      <section class="pb-design-board" data-board-version="v3">
        <header class="pb-design-board-header">
          <h2 class="pb-design-board-title">Design</h2>
          <button type="button" class="pb-design-board-reset-all" data-tooltip="Reset all design tokens">Reset</button>
        </header>
        <div class="pb-design-board-scroll" data-board-scroll>
          <div class="pb-design-board-styles" data-section="styles"></div>
          <div class="pb-design-board-rows" data-design-rows></div>
        </div>
        <div class="pb-design-board-floating-chip" hidden></div>
      </section>
    `;

    this._board = this._themeEl.querySelector('.pb-design-board');
    const stylesScope = this._board.querySelector('[data-section="styles"]');
    const rowsContainer = this._board.querySelector('[data-design-rows]');

    // Styles rail — theme presets, always visible.
    this._renderStylesSection(stylesScope);

    // Drawer rows. Each row's `onOpen` opens the matching drawer; the
    // drawer's render function owns its own state-changed subscription and
    // returns a teardown the drawer shell runs on close.
    const drawerSpecs = [
      {
        id: 'colors',
        title: 'Colors',
        summary: summarizeColors,
        renderer: renderColorsContent,
        width: 560
      },
      {
        id: 'fonts',
        title: 'Fonts',
        summary: summarizeFonts,
        renderer: renderFontsContent,
        width: 440
      },
      {
        id: 'layout',
        title: 'Buttons & Effects',
        summary: summarizeLayout,
        renderer: renderLayoutContent,
        width: 440
      },
      {
        id: 'forms',
        title: 'Forms',
        summary: summarizeForms,
        renderer: renderFormsContent,
        width: 440
      }
    ];

    this._designDrawerRows = new Map();
    for (const spec of drawerSpecs) {
      // Closure over the row reference — assigned after creation so
      // `triggerEl` can point back at the row's <button>.
      let row;
      row = renderInspectorRow({
        id: `design-${spec.id}`,
        label: spec.title,
        summaryText: spec.summary() || '',
        isContent: !!spec.isContent,
        onOpen: () => {
          openSectionDrawer({
            id: `design-${spec.id}`,
            title: spec.title,
            anchorEl: this._themeEl,
            triggerEl: row,
            width: spec.width,
            render: (body) => spec.renderer(body, { panel: this })
          });
        }
      });
      rowsContainer.appendChild(row);
      this._designDrawerRows.set(spec.id, { row, summary: spec.summary });
    }

    // Reset-all button.
    const resetAllBtn = this._board.querySelector('.pb-design-board-reset-all');
    resetAllBtn?.addEventListener('click', () => this._resetAllDesign());
  }

  // Single fan-out called by the idempotent re-render path and the
  // _syncThemeInputs state-changed listener.
  //
  // Post-B.2 surface: only the inline Styles rail gets a DOM-touch refresh
  // here. Drawer sections own their own state-changed subscriptions when open.
  // While closed, only their row summary updates.
  _refreshAllDesignSections() {
    if (!this._board) return;
    this._refreshStylesSection(this._activeDesignScope('styles'));
    this._refreshDesignRowSummaries();
  }

  _refreshDesignRowSummaries() {
    if (!this._designDrawerRows) return;
    for (const [, { row, summary }] of this._designDrawerRows) {
      const sumEl = row.querySelector('.pb-spi-row-summary');
      if (sumEl) sumEl.textContent = summary() || '';
    }
  }

  // ── Phase 7a — Reset semantics ──
  // Per-section resets restore that section's slice to safe defaults in a
  // single undo entry. Reset-all chains those section-level transactions.
  async _resetColorsSection({ confirm = true } = {}) {
    const theme = getCurrentTheme();
    const activePaletteId = getActivePaletteId(theme);
    const resetPalette =
      COLOR_PALETTES.find((palette) => palette.id === activePaletteId) || COLOR_PALETTES[0];
    if (confirm) {
      const ok = await showConfirmModal({
        title: 'Reset colors?',
        message: `This will restore colors to ${resetPalette?.name || 'the default palette'} and re-enable icon tinting.`,
        confirmLabel: 'Reset colors',
        cancelLabel: 'Cancel',
        danger: true
      });
      if (!ok) return;
    }
    if (!resetPalette) return;
    if (hasOpenTransaction()) commitThemeChange();
    store.beginTransaction('Reset colors');
    const currentTheme = store.getState()?.theme || {};
    for (const [key, val] of Object.entries(
      expandPaletteColors(resetPalette.colors || {}, { existingTheme: currentTheme })
    )) {
      store.update(`theme.${key}`, val, 'Reset colors');
      setThemeVar(key, val);
    }
    store.update('iconTinting', true, 'Reset icon tinting');
    store.commitTransaction();
  }

  async _resetTypographySection({ confirm = true } = {}) {
    const currentFont = getCurrentFont();
    const currentHeadingFont = getCurrentHeadingFont();
    const activePairing = FONT_PAIRINGS.find(
      (pairing) => pairing.body === currentFont && pairing.heading === currentHeadingFont
    );
    const nextFont = activePairing?.body || 'Inter';
    const nextHeadingFont = activePairing?.heading || 'Inter';
    const nextHeadingWeight = '600';
    if (confirm) {
      const ok = await showConfirmModal({
        title: 'Reset typography?',
        message: `This will restore typography to ${activePairing ? `${activePairing.heading} + ${activePairing.body}` : 'Inter + Inter'} and Semi Bold headings.`,
        confirmLabel: 'Reset typography',
        cancelLabel: 'Cancel',
        danger: true
      });
      if (!ok) return;
    }
    if (hasOpenTransaction()) commitThemeChange();
    store.beginTransaction('Reset typography');
    store.update('font', nextFont, 'Reset typography');
    bridge.send('update-font', buildFontUpdatePayload(nextFont));
    store.update('headingFont', nextHeadingFont, 'Reset typography');
    // A real choice — from here the theme outranks the template's own faces.
    store.update('fontSource', 'user', null);
    store.update('headingFontWeight', nextHeadingWeight, 'Reset typography');
    bridge.send(
      'update-heading-font',
      buildFontUpdatePayload(nextHeadingFont, {
        weight: nextHeadingWeight
      })
    );
    store.commitTransaction();
  }

  async _resetButtonsSection({ confirm = true } = {}) {
    const activeThemeId = getActiveThemePresetId(
      getCurrentTheme(),
      getCurrentFont(),
      getCurrentHeadingFont()
    );
    const activeThemePreset = THEME_PRESETS.find((preset) => preset.id === activeThemeId);
    // Explicit user gesture → origin:'user' so the styles survive the v5
    // migration and are never mistaken for silent auto-extraction.
    const nextButtonStyles = activeThemePreset?.buttonStyles
      ? { ...activeThemePreset.buttonStyles, origin: 'user' }
      : null;
    if (confirm) {
      const ok = await showConfirmModal({
        title: 'Reset buttons?',
        message: activeThemePreset
          ? `This will restore button styles to the ${activeThemePreset.name} preset defaults.`
          : 'This will restore button styles to the current template defaults.',
        confirmLabel: 'Reset buttons',
        cancelLabel: 'Cancel',
        danger: true
      });
      if (!ok) return;
    }
    if (hasOpenTransaction()) commitThemeChange();
    store.beginTransaction('Reset buttons');
    store.update('buttonStyles', nextButtonStyles, 'Reset buttons');
    eventBus.emit('theme:override-changed');
    store.commitTransaction();
  }

  async _resetFormsSection({ confirm = true } = {}) {
    if (confirm) {
      const ok = await showConfirmModal({
        title: 'Reset forms?',
        message:
          'This will restore form shape, field style, and submit button presets to the current template defaults.',
        confirmLabel: 'Reset forms',
        cancelLabel: 'Cancel',
        danger: true
      });
      if (!ok) return;
    }
    const didUpdate = store.update('formStyles', null, 'Reset forms');
    if (didUpdate) eventBus.emit('theme:override-changed');
  }

  async _resetEffectsSection({ confirm = true } = {}) {
    if (confirm) {
      const ok = await showConfirmModal({
        title: 'Reset site effects?',
        message:
          'This will restore Theme Wipe, Floating Labels, and Go-Top Ring to their default states.',
        confirmLabel: 'Reset effects',
        cancelLabel: 'Cancel',
        danger: true
      });
      if (!ok) return;
    }
    if (hasOpenTransaction()) commitThemeChange();
    store.beginTransaction('Reset site effects');
    for (const [key, defaultVal] of Object.entries(SITE_EFFECT_DEFAULTS)) {
      store.update(`siteEffects.${key}`, defaultVal, `Reset ${key}`);
    }
    store.commitTransaction();
  }

  async _resetAllDesign() {
    const ok = await showConfirmModal({
      title: 'Reset all design tokens?',
      message:
        'This will restore Styles, Colors, Typography, Buttons, Forms, and Site Effects to defaults. Cmd+Z can walk the reset back by category.',
      confirmLabel: 'Reset all',
      cancelLabel: 'Cancel',
      danger: true
    });
    if (!ok) return;
    // Skip per-section confirms — the user already confirmed at the top.
    await this._resetColorsSection({ confirm: false });
    await this._resetTypographySection({ confirm: false });
    await this._resetButtonsSection({ confirm: false });
    await this._resetFormsSection({ confirm: false });
    await this._resetEffectsSection({ confirm: false });
  }

  // Run and clear every cleanup from previously wired hover-preview triggers
  // across all v2 sections. Called before rebuilding the board / each section.
  _teardownDesignBoardCleanups(sectionId) {
    if (!this._designSectionCleanups) this._designSectionCleanups = {};
    if (sectionId) {
      const list = this._designSectionCleanups[sectionId] || [];
      for (const fn of list) {
        try {
          fn();
        } catch (err) {
          console.warn('[property-panel] cleanup failed:', err);
        }
      }
      this._designSectionCleanups[sectionId] = [];
      return;
    }
    for (const id of Object.keys(this._designSectionCleanups)) {
      const list = this._designSectionCleanups[id] || [];
      for (const fn of list) {
        try {
          fn();
        } catch (err) {
          console.warn('[property-panel] cleanup failed:', err);
        }
      }
      this._designSectionCleanups[id] = [];
    }
  }

  _registerDesignCleanup(sectionId, fn) {
    if (!this._designSectionCleanups) this._designSectionCleanups = {};
    if (!this._designSectionCleanups[sectionId]) this._designSectionCleanups[sectionId] = [];
    this._designSectionCleanups[sectionId].push(fn);
  }

  // Returns the section body element a v2 caller should render into (resolved
  // at call time so event handlers can never read a stale scope). v1 callers
  // get the legacy single-body element.
  _activeDesignScope(sectionId) {
    if (!this._board) return null;
    // v3 overview uses bare `<div data-section="…">` containers for inline
    // panels like Styles. Older v2 markup nested a
    // `.pb-design-section-body` child — keep both lookups working so any
    // late-binding caller during the lift still resolves.
    return (
      this._board.querySelector(`[data-section="${sectionId}"] .pb-design-section-body`) ||
      this._board.querySelector(`[data-section="${sectionId}"]`)
    );
  }

  // ── Phase 2 — Styles section (theme presets) ──

  // Render the Styles section body: a grid of theme-preset cards. Each card
  // is wired with hover-preview (themePresetSnapshot bypasses the store, so
  // hover/leave is invisible to the undo stack) and click-to-commit (which
  // funnels through applyThemePreset for a single-undo-entry transaction).
  _renderStylesSection(scope) {
    if (!scope) return;

    ensureUIFontsLoaded([
      ...THEME_PRESETS.map((t) => t.font),
      ...THEME_PRESETS.map((t) => t.headingFont)
    ]);

    const theme = getCurrentTheme();
    const font = getCurrentFont();
    const headingFont = getCurrentHeadingFont();
    const activeId = getActiveThemePresetId(theme, font, headingFont);

    this._teardownDesignBoardCleanups('styles');
    scope.innerHTML = '';
    const grid = document.createElement('div');
    grid.className = 'pb-design-grid pb-design-styles-grid';

    for (const preset of THEME_PRESETS) {
      const card = renderThemePresetCard(preset, { active: preset.id === activeId });
      const cleanup = wirePreviewOnHover(card, themePresetSnapshot(preset), {
        chipLabel: `Apply ${preset.name || 'theme'}`
      });
      this._registerDesignCleanup('styles', cleanup);
      grid.appendChild(card);
    }

    const help = document.createElement('p');
    help.className = 'pb-design-preset-help';
    help.textContent =
      'Applies colors, heading and body fonts, and button styles. Preview on hover; click to apply. You can undo the change.';
    scope.append(help, grid);
  }

  // Lightweight refresh: only toggles the active highlight. Avoids tearing
  // down hover-preview wiring on every state-changed tick (e.g., during
  // undo/redo or any other theme mutation).
  _refreshStylesSection(scope) {
    if (!scope) return;
    const theme = getCurrentTheme();
    const font = getCurrentFont();
    const headingFont = getCurrentHeadingFont();
    const activeId = getActiveThemePresetId(theme, font, headingFont);
    for (const card of scope.querySelectorAll('[data-theme-id]')) {
      const isActive = card.dataset.themeId === activeId;
      card.classList.toggle('pb-preset-card--active', isActive);
      card.classList.toggle('pb-design-theme-card--active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  // ── Phase 3 — Colors section ──

  // Full re-render path for the Colors section body. Used on first mount
  // and on any state-changed tick that isn't gated by an open transaction.
  // Reuses _renderColorsSubpanel(scope) so v1 and v2 share the underlying
  // form, with palette-card wiring branching on this._board inside
  // _wireColorsSubpanel.
  _refreshColorsSection(scope) {
    if (!scope) return;
    const theme = getCurrentTheme() || {};
    for (const card of scope.querySelectorAll('.pb-design-color-card[data-var]')) {
      const value = normalizeHex(theme[card.dataset.var] || '');
      if (!value) continue;
      const colorInput = card.querySelector('.pb-design-color-input');
      const hexInput = card.querySelector('.pb-design-color-hex-input');
      if (colorInput && document.activeElement !== colorInput) colorInput.value = value;
      if (hexInput && document.activeElement !== hexInput) hexInput.value = value;
      card.style.background = value;
      const readout = card.querySelector('.pb-design-color-card-hex');
      if (readout) readout.textContent = value;
      const r = parseInt(value.slice(1, 3), 16);
      const g = parseInt(value.slice(3, 5), 16);
      const b = parseInt(value.slice(5, 7), 16);
      card.style.color = r * 0.299 + g * 0.587 + b * 0.114 > 150 ? '#1a1a1a' : '#ffffff';
      this._syncColorSwatches(card, colorInput?.id, value);
    }
    const tint = scope.querySelector('[data-icon-tinting]');
    if (tint && document.activeElement !== tint)
      tint.checked = store.getState()?.iconTinting ?? true;
    this._refreshPaletteActiveStates(scope);
    this._refreshColorContrast(scope);
  }

  // ── Site effects state accessor ──
  // Used by _renderEffectsSubpanel + _resetEffectsSection. Merges current
  // overrides on top of SITE_EFFECT_DEFAULTS so missing keys default cleanly.
  _getSiteEffectsState() {
    return { ...SITE_EFFECT_DEFAULTS, ...(store.getState()?.siteEffects || {}) };
  }

  // Color helpers delegate to design-drawers/_internals.js. The methods are
  // kept on the class so the 30+ `this._fn(...)` call sites stay valid during
  // the incremental Phase B lift; the new drawer modules import the same
  // functions directly and don't need a PropertyPanel reference.
  _getDesignColorOptions() {
    return getDesignColorOptions();
  }
  _renderDesignColorSwatches(targetId, selectedValue = '', leadingOptions = []) {
    return renderDesignColorSwatches(targetId, selectedValue, leadingOptions);
  }
  _renderTokenFirstColorInput(opts) {
    return renderTokenFirstColorInput(opts);
  }
  _syncColorSwatches(scopeEl, targetId, selectedValue) {
    return syncColorSwatches(scopeEl, targetId, selectedValue);
  }

  // ── Colors sub-panel ──
  //
  // Accepts an explicit `scope` element so design-board sections render into
  // their own DOM subtree.

  _renderColorsSubpanel(scope = null) {
    if (!scope) return;
    const theme = getCurrentTheme();
    const activePaletteId = getActivePaletteId(theme);

    // Map theme vars to display cards
    const colorCards = [
      { key: '--bodyTextColorWhite', label: 'Background', row: '2col' },
      { key: '--headerColor', label: 'Neutral', row: '2col' },
      { key: '--primary', label: 'Primary', row: '3col' },
      { key: '--secondary', label: 'Secondary', row: '3col' },
      { key: '--primaryLight', label: 'Tertiary', row: '3col' }
    ];

    const advancedCardKeys = new Set(['--secondaryLight', '--bodyTextColor']);
    const visibleCardKeys = new Set(colorCards.map((card) => card.key));
    for (const def of getThemeVars()) {
      if (visibleCardKeys.has(def.key) || advancedCardKeys.has(def.key)) continue;
      if (normalizeHex(theme[def.key] || '')) advancedCardKeys.add(def.key);
    }
    for (const key of Object.keys(theme || {})) {
      if (!key.startsWith('--') || visibleCardKeys.has(key) || advancedCardKeys.has(key)) continue;
      if (normalizeHex(theme[key] || '')) advancedCardKeys.add(key);
    }
    const labelByKey = new Map(getThemeVars().map((def) => [def.key, def.label]));
    const advancedCards = Array.from(advancedCardKeys).map((key) => ({
      key,
      label: labelByKey.get(key) || humanizeThemeToken(key)
    }));

    // Determine light/dark text for each card
    const textColor = (hex) => {
      if (!hex) return '#000';
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return r * 0.299 + g * 0.587 + b * 0.114 > 150 ? '#1a1a1a' : '#ffffff';
    };

    const renderCard = (c) => {
      const val = theme[c.key] || '#000000';
      const fg = textColor(val);
      const idSeed = c.key.replace(/[^a-z0-9]/gi, '-');
      const pickerId = `pb-theme-color-${idSeed}`;
      const hexId = `${pickerId}-hex`;
      return `<div class="pb-design-color-card" data-var="${c.key}" data-testid="pb-design-color-card-${idSeed}" style="background:${val};color:${fg}">
        <div class="pb-design-color-card-label">${c.label}</div>
        <div class="pb-design-color-card-hex">${val}</div>
        <div class="pb-color-contrast" data-color-contrast aria-live="polite"></div>
        ${this._renderTokenFirstColorInput({
          colorId: pickerId,
          hexId,
          colorValue: val,
          hexValue: val,
          placeholder: '#000000',
          colorAriaLabel: `${c.label} color`,
          hexAriaLabel: `${c.label} hex value`,
          colorInputClass: 'pb-design-color-input',
          hexInputClass: 'pb-design-color-hex-input',
          wrapClass: 'pb-color-input-wrap--in-card',
          extraColorAttrs: `data-var="${c.key}"`,
          extraHexAttrs: `data-var="${c.key}"`
        })}
      </div>`;
    };

    scope.innerHTML = `
      <div class="pb-design-color-grid">
        ${colorCards
          .filter((c) => c.row === '2col')
          .map(renderCard)
          .join('')}
      </div>
      <div class="pb-design-color-grid pb-design-color-grid--3col">
        ${colorCards
          .filter((c) => c.row === '3col')
          .map(renderCard)
          .join('')}
      </div>
      <details class="pb-props-advanced" style="margin-top:var(--pb-space-4)">
        <summary class="pb-props-disclosure">Advanced</summary>
        <div class="pb-design-color-grid">
          ${advancedCards.map(renderCard).join('')}
        </div>
      </details>
      <div class="pb-design-section-title">Palettes</div>
      <div class="pb-design-palette-grid" data-palette-grid></div>
      <div class="pb-design-section-title">Options</div>
      <label class="pb-toggle-row">
        <span class="pb-props-label">Icon Tinting</span>
        <input type="checkbox" class="pb-toggle" data-icon-tinting ${(store.getState()?.iconTinting ?? true) ? 'checked' : ''}>
        <span class="pb-toggle-slider"></span>
      </label>
    `;

    // Build palette cards via the shared widget so v2 hover-preview wiring
    // can attach to real Element nodes (not innerHTML strings).
    const paletteGrid = scope.querySelector('[data-palette-grid]');
    if (paletteGrid) {
      for (const p of COLOR_PALETTES) {
        const card = renderPalettePresetCard(p, { active: p.id === activePaletteId });
        paletteGrid.appendChild(card);
      }
    }

    this._wireColorsSubpanel(scope);
    this._refreshColorContrast(scope);
  }

  _refreshColorContrast(scope = null) {
    if (!scope) return;
    const values = { ...(getCurrentTheme() || {}) };
    for (const card of scope.querySelectorAll('.pb-design-color-card[data-var]')) {
      const input = card.querySelector('.pb-design-color-input');
      if (input) values[card.dataset.var] = input.value;
    }
    const background = values['--bodyTextColorWhite'] || '#ffffff';
    const pairFor = (key) => {
      if (key === '--bodyTextColorWhite') return values['--headerColor'] || '#000000';
      return background;
    };
    for (const card of scope.querySelectorAll('.pb-design-color-card[data-var]')) {
      const output = card.querySelector('[data-color-contrast]');
      if (!output) continue;
      const key = card.dataset.var;
      const assessment = getContrastAssessment(values[key], pairFor(key));
      if (!assessment) {
        output.textContent = '';
        output.classList.remove('pb-color-contrast--warning');
        continue;
      }
      output.textContent = assessment.aa
        ? `${assessment.label} ${assessment.aaa ? 'AAA' : 'AA'}`
        : `${assessment.label} ${assessment.largeText ? 'large text only' : 'low contrast'}`;
      output.classList.toggle('pb-color-contrast--warning', !assessment.aa);
      output.dataset.tooltip =
        key === '--bodyTextColorWhite' ? 'Contrast against Neutral' : 'Contrast against Background';
    }
  }

  _wireColorsSubpanel(scope = null) {
    if (!scope) return;

    // Wire theme color cards
    for (const card of scope.querySelectorAll('.pb-design-color-card')) {
      const varName = card.dataset.var;
      const colorInput = card.querySelector('.pb-design-color-input');
      const hexInput = card.querySelector('.pb-design-color-hex-input');
      if (!colorInput || !hexInput) continue;

      const updateCardVisuals = (nextHex) => {
        const normalized = normalizeHex(nextHex || '') || colorInput.value;
        if (normalized) {
          card.style.background = normalized;
          card.querySelector('.pb-design-color-card-hex').textContent = normalized;
          const r = parseInt(normalized.slice(1, 3), 16);
          const g = parseInt(normalized.slice(3, 5), 16);
          const b = parseInt(normalized.slice(5, 7), 16);
          card.style.color = r * 0.299 + g * 0.587 + b * 0.114 > 150 ? '#1a1a1a' : '#ffffff';
          this._syncColorSwatches(card, colorInput.id, normalized);
        }
      };

      const gatedUpdate = rafGate((v, val) => updateThemeVar(v, val));
      colorInput.addEventListener('input', () => {
        card.style.background = colorInput.value;
        card.querySelector('.pb-design-color-card-hex').textContent = colorInput.value;
        const r = parseInt(colorInput.value.slice(1, 3), 16);
        const g = parseInt(colorInput.value.slice(3, 5), 16);
        const b = parseInt(colorInput.value.slice(5, 7), 16);
        card.style.color = r * 0.299 + g * 0.587 + b * 0.114 > 150 ? '#1a1a1a' : '#ffffff';
        hexInput.value = colorInput.value;
        this._syncColorSwatches(card, colorInput.id, colorInput.value);
        this._refreshColorContrast(scope);
        gatedUpdate(varName, colorInput.value);
      });

      colorInput.addEventListener('change', () => {
        gatedUpdate.flush();
        commitThemeChange();
        rememberRecentColor(colorInput.value);
        this._refreshPaletteActiveStates(scope);
      });

      hexInput.addEventListener('change', () => {
        const normalized = normalizeHex(hexInput.value || '');
        if (!normalized) {
          flashError(hexInput);
          hexInput.value = colorInput.value;
          return;
        }
        colorInput.value = normalized;
        updateCardVisuals(normalized);
        gatedUpdate(varName, normalized);
        gatedUpdate.flush();
        commitThemeChange();
        rememberRecentColor(normalized);
        this._refreshColorContrast(scope);
        this._refreshPaletteActiveStates(scope);
      });

      const eyedropper = card.querySelector(`[data-color-eyedropper="${colorInput.id}"]`);
      if (eyedropper && typeof globalThis.EyeDropper === 'function') {
        eyedropper.addEventListener('click', async () => {
          try {
            const result = await new globalThis.EyeDropper().open();
            const normalized = normalizeHex(result?.sRGBHex || '');
            if (!normalized) return;
            colorInput.value = normalized;
            hexInput.value = normalized;
            updateCardVisuals(normalized);
            gatedUpdate(varName, normalized);
            gatedUpdate.flush();
            commitThemeChange();
            rememberRecentColor(normalized);
            this._refreshColorContrast(scope);
            this._refreshPaletteActiveStates(scope);
          } catch (err) {
            if (err?.name !== 'AbortError') {
              reportError('Color eyedropper', err, {
                user: 'toast',
                message: 'Could not pick a color from the screen.'
              });
            }
          }
        });
      }

      // Hover-preview state: live-apply a swatch to the iframe on hover, revert
      // on leave. Doesn't touch the store/history — pure visual preview.
      let previewTimer = null;
      let previewActive = false;
      const cancelPreview = () => {
        if (previewTimer) {
          clearTimeout(previewTimer);
          previewTimer = null;
        }
        if (previewActive) {
          previewActive = false;
          const currentValue = getCurrentTheme()[varName];
          if (currentValue) setThemeVar(varName, currentValue);
        }
      };

      for (const swatch of card.querySelectorAll(
        `.pb-color-swatch-btn[data-color-target="${colorInput.id}"]`
      )) {
        swatch.addEventListener('mouseenter', () => {
          const swatchValue = normalizeHex(
            swatch.dataset.colorHex || swatch.dataset.colorValue || ''
          );
          if (!swatchValue) return;
          if (previewTimer) clearTimeout(previewTimer);
          previewTimer = setTimeout(() => {
            previewTimer = null;
            previewActive = true;
            setThemeVar(varName, swatchValue);
          }, 300);
        });
        swatch.addEventListener('mouseleave', cancelPreview);

        swatch.addEventListener('click', () => {
          // Click commits; clear preview state so the post-click mouseleave
          // doesn't revert the value we're about to commit.
          if (previewTimer) {
            clearTimeout(previewTimer);
            previewTimer = null;
          }
          previewActive = false;

          const swatchValue = swatch.dataset.colorHex || swatch.dataset.colorValue || '';
          const normalized = normalizeHex(swatchValue);
          if (!normalized) return;
          colorInput.value = normalized;
          hexInput.value = normalized;
          updateCardVisuals(normalized);
          gatedUpdate(varName, normalized);
          gatedUpdate.flush();
          commitThemeChange();
          rememberRecentColor(normalized);
          this._refreshColorContrast(scope);
          this._refreshPaletteActiveStates(scope);
        });
      }
    }

    // Drop any palette cleanups left over from a previous render so detached
    // nodes can be collected.
    this._teardownDesignBoardCleanups('colors');
    for (const card of scope.querySelectorAll('.pb-design-palette-card')) {
      const palette = COLOR_PALETTES.find((p) => p.id === card.dataset.paletteId);
      if (!palette) continue;
      const cleanup = wirePreviewOnHover(card, paletteSnapshot(palette), {
        chipLabel: `Apply ${palette.name || 'palette'}`
      });
      this._registerDesignCleanup('colors', cleanup);
    }

    // Wire icon tinting toggle
    const tintToggle = scope.querySelector('[data-icon-tinting]');
    if (tintToggle) {
      tintToggle.addEventListener('change', () => {
        const enabled = tintToggle.checked;
        store.update(
          'iconTinting',
          enabled,
          enabled ? 'Enable icon tinting' : 'Disable icon tinting'
        );
        bridge.send(enabled ? 'apply-icon-tints' : 'remove-icon-tints');
      });
    }
  }

  _refreshPaletteActiveStates(scope = null) {
    if (!scope) return;
    const theme = getCurrentTheme();
    const activePaletteId = getActivePaletteId(theme);
    for (const card of scope.querySelectorAll('.pb-design-palette-card')) {
      const isActive = card.dataset.paletteId === activePaletteId;
      card.classList.toggle('pb-design-palette-card--active', isActive);
      card.classList.toggle('pb-preset-card--active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  // ── Fonts sub-panel ──

  // Phase 4 — Typography section. v1 callers (legacy push/pop subpanel) get
  // the original layout; v2 callers (design board) get a search input + live
  // mini preview above the dropdowns and pairing-card hover-preview wired
  // through pairingSnapshot. Same render path for both — the v2-only chrome
  // is appended conditionally.
  _renderFontsSubpanel(scope = null) {
    if (!scope) return;

    const font = getCurrentFont();
    const headingFont = getCurrentHeadingFont();
    const headingWeight = getCurrentHeadingFontWeight();
    const allFonts = FONT_OPTIONS;
    // Eagerly load only the in-use fonts. The full catalog is fetched on
    // first dropdown-open by the combobox so we don't trigger ~80 fetches
    // on first paint.
    ensureUIFontLoaded(font);
    ensureUIFontLoaded(headingFont);

    const fontOptions = (selected) =>
      allFonts
        .map(
          (f) =>
            `<option value="${f}" style="font-family:'${f}',sans-serif" ${f === selected ? 'selected' : ''}>${f}</option>`
        )
        .join('');

    const weightOptions = ['400', '500', '600', '700', '900'];
    const weightLabels = {
      400: 'Regular',
      500: 'Medium',
      600: 'Semi Bold',
      700: 'Bold',
      900: 'Black'
    };
    const weightSelectHTML = weightOptions
      .map(
        (w) =>
          `<option value="${w}" ${w === headingWeight ? 'selected' : ''}>${weightLabels[w]}</option>`
      )
      .join('');

    scope.innerHTML = `
      <div class="pb-design-font-row">
        <label>Base</label>
        <select class="pb-font-select" id="pb-font-base" style="font-family:'${font}',sans-serif">${fontOptions(font)}</select>
      </div>
      <div class="pb-design-font-row">
        <label>Headings</label>
        <div class="pb-design-font-row-pair">
          <select class="pb-font-select" id="pb-font-heading" style="font-family:'${headingFont}',sans-serif">${fontOptions(headingFont)}</select>
          <select class="pb-font-select" id="pb-font-heading-weight">${weightSelectHTML}</select>
        </div>
      </div>
      <div class="pb-design-section-title">Pairings</div>
      <div class="pb-design-pairing-grid" data-pairing-grid></div>
    `;

    // Build pairing cards via the shared widget so v2 hover-preview wiring
    // can attach to real Element nodes (not innerHTML strings). v1 path uses
    // the same DOM shape so the legacy click handler still finds them.
    const pairingGrid = scope.querySelector('[data-pairing-grid]');
    if (pairingGrid) {
      for (const p of FONT_PAIRINGS) {
        const isActive = p.body === font && p.heading === headingFont;
        const card = renderFontPresetCard(p, { active: isActive });
        pairingGrid.appendChild(card);
      }
    }

    this._wireFontsSubpanel(scope);
  }

  _wireFontsSubpanel(scope = null) {
    if (!scope) return;

    const baseSelect = scope.querySelector('#pb-font-base');
    const headingSelect = scope.querySelector('#pb-font-heading');
    const weightSelect = scope.querySelector('#pb-font-heading-weight');
    this._teardownDesignBoardCleanups('font-comboboxes');

    const pairingCards = Array.from(scope.querySelectorAll('.pb-design-pairing-card'));
    const loadPairingCardFonts = (card) => {
      if (!card || card.dataset.fontsLoaded === 'true') return;
      card.dataset.fontsLoaded = 'true';
      ensureUIFontsLoaded([card.dataset.heading, card.dataset.body]);
    };
    for (const card of pairingCards) {
      card.addEventListener('mouseenter', () => loadPairingCardFonts(card), { once: true });
      card.addEventListener('focusin', () => loadPairingCardFonts(card), { once: true });
    }
    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            loadPairingCardFonts(entry.target);
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '120px 0px' }
      );
      for (const card of pairingCards) observer.observe(card);
      this._registerDesignCleanup('font-comboboxes', () => observer.disconnect());
    } else {
      for (const card of pairingCards.slice(0, 6)) loadPairingCardFonts(card);
    }

    // Upgrade the font-name selects + the weight select to custom comboboxes.
    // All three keep their native <select> as the source of truth (form
    // semantics + change events stay live), so the listeners below still work.
    // The weight combobox renders each weight option in its own boldness AND
    // in the heading font, so the user sees "Bold Bevellier" rendered as
    // bold Bevellier instead of a static label.
    let weightCb = null;
    if (baseSelect) {
      const cb = upgradeToFontCombobox(baseSelect);
      if (cb) this._registerDesignCleanup('font-comboboxes', () => cb.destroy());
    }
    if (headingSelect) {
      const cb = upgradeToFontCombobox(headingSelect);
      if (cb) this._registerDesignCleanup('font-comboboxes', () => cb.destroy());
    }
    if (weightSelect) {
      weightCb = upgradeToFontCombobox(weightSelect, {
        mode: 'weight',
        fontFamilyResolver: () => getCurrentHeadingFont()
      });
      if (weightCb) this._registerDesignCleanup('font-comboboxes', () => weightCb.destroy());
    }
    // Re-render the weight combobox's trigger + listbox items when the
    // heading FONT changes. Both surfaces render labels in the heading
    // font, so they need to refresh together when that font swaps.
    if (headingSelect && weightCb) {
      const onHeadingFontChange = () => weightCb.refresh();
      headingSelect.addEventListener('change', onHeadingFontChange);
      this._registerDesignCleanup('font-comboboxes', () => {
        headingSelect.removeEventListener('change', onHeadingFontChange);
      });
    }

    if (baseSelect) {
      baseSelect.addEventListener('change', () => {
        updateFont(baseSelect.value);
        baseSelect.style.fontFamily = `'${baseSelect.value}',sans-serif`;
        this._refreshPairingActiveStates(scope);
      });
    }

    if (headingSelect) {
      headingSelect.addEventListener('change', () => {
        updateHeadingFont(headingSelect.value);
        headingSelect.style.fontFamily = `'${headingSelect.value}',sans-serif`;
        this._refreshPairingActiveStates(scope);
      });
    }

    if (weightSelect) {
      weightSelect.addEventListener('change', () => {
        updateHeadingFontWeight(weightSelect.value);
      });
    }

    this._teardownDesignBoardCleanups('typography');
    for (const card of scope.querySelectorAll('.pb-design-pairing-card')) {
      const pairing = { heading: card.dataset.heading, body: card.dataset.body };
      const cleanup = wirePreviewOnHover(card, pairingSnapshot(pairing), {
        chipLabel: `Apply ${pairing.heading} + ${pairing.body}`
      });
      this._registerDesignCleanup('typography', cleanup);
    }
  }

  // Lightweight refresh for the Typography section. Syncs select values +
  // active pairing highlight without rebuilding the DOM.
  _refreshTypographySection(scope = null) {
    if (!scope) return;
    const font = getCurrentFont();
    const headingFont = getCurrentHeadingFont();
    const headingWeight = getCurrentHeadingFontWeight();
    ensureUIFontsLoaded([font, headingFont]);

    const baseSelect = scope.querySelector('#pb-font-base');
    const headingSelect = scope.querySelector('#pb-font-heading');
    const weightSelect = scope.querySelector('#pb-font-heading-weight');

    // When an external mutation (theme preset apply, undo/redo, etc.) shifts
    // the value here, dispatch `change` so the font-combobox trigger label
    // re-syncs. Safe to dispatch: `updateFont` / `updateHeadingFont` are
    // idempotent — if current === new, they short-circuit and don't loop.
    if (baseSelect && document.activeElement !== baseSelect) {
      if (baseSelect.value !== font) {
        baseSelect.value = font;
        baseSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      baseSelect.style.fontFamily = `'${font}',sans-serif`;
    }
    if (headingSelect && document.activeElement !== headingSelect) {
      if (headingSelect.value !== headingFont) {
        headingSelect.value = headingFont;
        headingSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      headingSelect.style.fontFamily = `'${headingFont}',sans-serif`;
    }
    if (weightSelect && weightSelect.value !== headingWeight) {
      weightSelect.value = headingWeight;
      weightSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this._refreshPairingActiveStates(scope);
  }

  _refreshPairingActiveStates(scope = null) {
    if (!scope) return;
    const font = getCurrentFont();
    const headingFont = getCurrentHeadingFont();
    for (const card of scope.querySelectorAll('.pb-design-pairing-card')) {
      const isActive = card.dataset.body === font && card.dataset.heading === headingFont;
      card.classList.toggle('pb-design-pairing-card--active', isActive);
      card.classList.toggle('pb-preset-card--active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  // ── Buttons sub-panel ──

  // Phase 5 — Buttons section. Plain-language hover-effect labels (Bubble /
  // Stretch / Sweep / Shake replace Rubber / Wipe / Glitch). Stored
  // values are unchanged so save-load is a no-op. Hovering an effect
  // card toggles `.pb-effect-<name>` on the live preview button so the user
  // sees the effect family without committing.
  _renderButtonsSubpanel(scope = null) {
    if (!scope) return;
    const previewModel = this._getDesignButtonPreviewModel();
    const bg = previewModel.styles.backgroundColor;
    const textColor = previewModel.styles.textColor;
    const originalBg = this._normalizeDesignColor(previewModel.baseStyles.backgroundColor, bg);
    const originalText = this._normalizeDesignColor(previewModel.baseStyles.textColor, textColor);
    const committedEffect = this._getButtonStyle('hoverEffect');
    const previewEffectClass =
      committedEffect && committedEffect !== 'none' ? ` pb-effect-${committedEffect}` : '';

    scope.innerHTML = `
      <div class="pb-design-btn-preview-wrap">
        <div class="pb-design-btn-preview-stage">
          <button class="pb-design-btn-preview${previewEffectClass}" id="pb-btn-live-preview" data-template-class="${escapeHTML(previewModel.className)}" data-template-source="${escapeHTML(previewModel.sourceLabel)}" style="${this._buttonPreviewStyleAttribute(previewModel.styles)}">${escapeHTML(previewModel.text)}</button>
        </div>
      </div>
      <div class="pb-override-row">
        <label class="pb-props-label">Border Radius</label>
        <div class="pb-btn-preset-grid" id="pb-btn-radius-presets"></div>
      </div>
      <div class="pb-override-row">
        <label class="pb-props-label">Padding</label>
        <div class="pb-btn-preset-grid" id="pb-btn-padding-presets"></div>
      </div>
      <div class="pb-override-row">
        <label class="pb-props-label">Background Color</label>
        ${this._renderTokenFirstColorInput({
          colorId: 'pb-btn-bg',
          hexId: 'pb-btn-bg-hex',
          colorValue: bg,
          hexValue: this._getButtonStyle('backgroundColor') || '',
          placeholder: 'original',
          colorAriaLabel: 'Button background color',
          hexAriaLabel: 'Button background hex value',
          leadingSwatches: [
            {
              label: 'Original',
              value: originalBg,
              visibleLabel: true,
              clearValue: true
            }
          ],
          swatchSelectedValue: this._getButtonStyle('backgroundColor') || ''
        })}
      </div>
      <div class="pb-override-row">
        <label class="pb-props-label">Text Color</label>
        ${this._renderTokenFirstColorInput({
          colorId: 'pb-btn-text',
          hexId: 'pb-btn-text-hex',
          colorValue: textColor,
          hexValue: this._getButtonStyle('textColor') || '',
          placeholder: 'original',
          colorAriaLabel: 'Button text color',
          hexAriaLabel: 'Button text hex value',
          leadingSwatches: [
            {
              label: 'Original',
              value: originalText,
              visibleLabel: true,
              clearValue: true
            }
          ],
          swatchSelectedValue: this._getButtonStyle('textColor') || ''
        })}
      </div>
      <div class="pb-override-row">
        <label class="pb-props-label">Hover Effect</label>
        <div class="pb-btn-preset-grid pb-btn-preset-grid-wide" id="pb-btn-effect-presets"></div>
      </div>
    `;

    const mountSegment = (id, field, options, currentValue, ariaLabel) => {
      const mount = scope.querySelector(`#${id}`);
      if (!mount) return;
      mount.classList.add('pb-btn-preset-grid--segmented');
      const segment = renderSegmented({
        field,
        options,
        value: currentValue,
        ariaLabel,
        onChange: (nextValue) => {
          this._setButtonStyle(field, nextValue);
          this._syncButtonStyleInputs(scope);
          this._updateMiniPreviews(scope);
        }
      });
      segment.classList.add(`pb-segmented--${field}`);
      mount.appendChild(segment);
    };

    mountSegment(
      'pb-btn-radius-presets',
      'borderRadius',
      [
        { name: 'Default', value: '' },
        { name: 'Square', value: '0' },
        { name: 'Slight', value: '0.25rem' },
        { name: 'Medium', value: '0.5rem' },
        { name: 'Round', value: '1rem' },
        { name: 'Pill', value: '3rem' }
      ],
      this._getButtonStyle('borderRadius'),
      'Button border radius'
    );

    mountSegment(
      'pb-btn-padding-presets',
      'padding',
      [
        { name: 'Default', value: '' },
        { name: 'Compact', value: '0 1rem' },
        { name: 'Normal', value: '0 1.5rem' },
        { name: 'Wide', value: '0 2rem' },
        { name: 'X-Wide', value: '0 3rem' }
      ],
      this._getButtonStyle('padding'),
      'Button padding'
    );

    mountSegment(
      'pb-btn-effect-presets',
      'hoverEffect',
      [
        { name: 'Default', value: 'none' },
        { name: 'Bubble', value: 'bubble' },
        { name: 'Shine', value: 'shine' },
        { name: 'Stretch', value: 'rubber' },
        { name: 'Glow', value: 'glow' },
        { name: 'Magnetic', value: 'magnetic' },
        { name: 'Sweep', value: 'wipe' },
        { name: 'Ripple', value: 'ripple' },
        { name: 'Shake', value: 'glitch' },
        { name: '3D Tilt', value: 'tilt' },
        { name: 'Spotlight', value: 'spotlight' }
      ],
      this._getButtonStyle('hoverEffect') || 'none',
      'Button hover effect'
    );

    this._wireButtonsSubpanel(scope);
  }

  _wireButtonsSubpanel(scope = null) {
    if (!scope) return;
    const preview = scope.querySelector('#pb-btn-live-preview');

    // Sync `.pb-effect-<name>` on the preview button to a target value
    // (committed effect by default; candidate during hover-preview).
    const setPreviewEffectClass = (effectValue) => {
      if (!preview) return;
      // Strip any existing effect class.
      const stale = Array.from(preview.classList).filter((c) => c.startsWith('pb-effect-'));
      for (const c of stale) preview.classList.remove(c);
      if (effectValue && effectValue !== 'none') preview.classList.add(`pb-effect-${effectValue}`);
    };

    const updatePreview = () => {
      if (!preview) return;
      this._applyDesignButtonPreview(preview);
      setPreviewEffectClass(this._getButtonStyle('hoverEffect'));
      this._updateMiniPreviews(scope);
    };

    // Wire button style presets (both old .pb-spacing-preset and new .pb-btn-preset-card)
    for (const btn of scope.querySelectorAll(
      '.pb-btn-preset-card[data-field], .pb-spacing-preset[data-field]'
    )) {
      const field = btn.dataset.field;
      if (field !== 'borderRadius' && field !== 'padding' && field !== 'hoverEffect') continue;
      btn.addEventListener('click', () => {
        const val = btn.dataset.value;
        const current = this._getButtonStyle(field);
        const isActive = current === val && val !== '';
        const newVal = isActive ? '' : val;
        this._setButtonStyle(field, newVal);
        const parent = btn.parentElement;
        for (const b of parent.querySelectorAll('.pb-btn-preset-card, .pb-spacing-preset')) {
          b.classList.toggle('pb-active', b.dataset.value === newVal && newVal !== '');
        }
        // Update mini preview buttons to reflect new styles
        this._updateMiniPreviews(scope);
        updatePreview();
      });
    }

    // Hover an effect preset card -> apply the candidate
    // `.pb-effect-<name>` class to the live preview button so the user can
    // SEE what the effect family looks like without committing. Strictly
    // local to the panel preview button — no iframe broadcast, no store
    // mutation. Mouseleave restores the committed effect.
    if (preview) {
      this._teardownDesignBoardCleanups('buttons');
      const effectGrid = scope.querySelector('#pb-btn-effect-presets');
      if (effectGrid) {
        for (const card of effectGrid.querySelectorAll(
          '.pb-segmented-option[data-field="hoverEffect"], .pb-btn-preset-card[data-field="hoverEffect"], .pb-spacing-preset[data-field="hoverEffect"]'
        )) {
          const value = card.dataset.value;
          const onEnter = () => setPreviewEffectClass(value);
          const onLeave = () => setPreviewEffectClass(this._getButtonStyle('hoverEffect'));
          card.addEventListener('mouseenter', onEnter);
          card.addEventListener('mouseleave', onLeave);
          this._registerDesignCleanup('buttons', () => {
            card.removeEventListener('mouseenter', onEnter);
            card.removeEventListener('mouseleave', onLeave);
          });
        }
      }
    }

    // Wire button background color
    const btnBg = scope.querySelector('#pb-btn-bg');
    const btnBgHex = scope.querySelector('#pb-btn-bg-hex');
    let btnBgDragging = false;
    if (btnBg && btnBgHex) {
      const applyButtonBgValue = (nextValue) => {
        this._setButtonStyle('backgroundColor', nextValue);
        this._syncColorSwatches(scope, 'pb-btn-bg', nextValue);
        updatePreview();
      };

      btnBg.addEventListener('input', () => {
        btnBgHex.value = btnBg.value;
        if (!btnBgDragging) {
          store.beginTransaction('Change button color');
          btnBgDragging = true;
        }
        applyButtonBgValue(btnBg.value);
      });
      btnBg.addEventListener('change', () => {
        if (btnBgDragging) {
          store.commitTransaction();
          btnBgDragging = false;
        }
      });
      btnBgHex.addEventListener('change', () => {
        const val = btnBgHex.value.trim();
        if (!val) {
          applyButtonBgValue('');
          btnBg.value =
            normalizeHex(this._getDesignButtonPreviewModel().styles.backgroundColor) || '#ff6a3e';
        } else if (/^#[0-9a-fA-F]{3,8}$/.test(val)) {
          btnBg.value = val.length <= 7 ? val : val.slice(0, 7);
          applyButtonBgValue(val);
        } else {
          flashError(btnBgHex);
          btnBgHex.value = btnBg.value;
        }
      });

      for (const swatch of scope.querySelectorAll(
        '.pb-color-swatch-btn[data-color-target="pb-btn-bg"]'
      )) {
        swatch.addEventListener('click', () => {
          const storedValue = swatch.dataset.colorValue || '';
          const swatchValue = normalizeHex(swatch.dataset.colorHex || storedValue);
          if (!swatchValue && swatch.dataset.colorClear !== 'true') return;
          if (btnBgDragging) {
            store.commitTransaction();
            btnBgDragging = false;
          }
          if (swatchValue) btnBg.value = swatchValue;
          if (swatch.dataset.colorClear === 'true') {
            btnBgHex.value = '';
            applyButtonBgValue('');
          } else {
            btnBgHex.value = swatchValue;
            applyButtonBgValue(storedValue || swatchValue);
          }
        });
      }
    }

    // Wire button text color
    const btnText = scope.querySelector('#pb-btn-text');
    const btnTextHex = scope.querySelector('#pb-btn-text-hex');
    let btnTextDragging = false;
    if (btnText && btnTextHex) {
      const applyButtonTextValue = (nextValue) => {
        this._setButtonStyle('textColor', nextValue);
        this._syncColorSwatches(scope, 'pb-btn-text', nextValue);
        updatePreview();
      };

      btnText.addEventListener('input', () => {
        btnTextHex.value = btnText.value;
        if (!btnTextDragging) {
          store.beginTransaction('Change button text color');
          btnTextDragging = true;
        }
        applyButtonTextValue(btnText.value);
      });
      btnText.addEventListener('change', () => {
        if (btnTextDragging) {
          store.commitTransaction();
          btnTextDragging = false;
        }
      });
      btnTextHex.addEventListener('change', () => {
        const val = btnTextHex.value.trim();
        if (!val) {
          applyButtonTextValue('');
          btnText.value =
            normalizeHex(this._getDesignButtonPreviewModel().styles.textColor) || '#ffffff';
        } else if (/^#[0-9a-fA-F]{3,8}$/.test(val)) {
          btnText.value = val.length <= 7 ? val : val.slice(0, 7);
          applyButtonTextValue(val);
        } else {
          flashError(btnTextHex);
          btnTextHex.value = btnText.value;
        }
      });

      for (const swatch of scope.querySelectorAll(
        '.pb-color-swatch-btn[data-color-target="pb-btn-text"]'
      )) {
        swatch.addEventListener('click', () => {
          const storedValue = swatch.dataset.colorValue || '';
          const swatchValue = normalizeHex(swatch.dataset.colorHex || storedValue);
          if (!swatchValue && swatch.dataset.colorClear !== 'true') return;
          if (btnTextDragging) {
            store.commitTransaction();
            btnTextDragging = false;
          }
          if (swatchValue) btnText.value = swatchValue;
          if (swatch.dataset.colorClear === 'true') {
            btnTextHex.value = '';
            applyButtonTextValue('');
          } else {
            btnTextHex.value = swatchValue;
            applyButtonTextValue(storedValue || swatchValue);
          }
        });
      }
    }

    this._syncColorSwatches(scope, 'pb-btn-bg', this._getButtonStyle('backgroundColor') || '');
    this._syncColorSwatches(scope, 'pb-btn-text', this._getButtonStyle('textColor') || '');
  }

  // Phase 5 — lightweight refresh for the Buttons section. Reuses the
  // existing _syncButtonStyleInputs (which already accepts a container) so
  // dropdowns / preset highlights / live preview style + .pb-effect-<name>
  // class all stay in sync without a DOM rebuild.
  _refreshButtonsSection(scope = null) {
    if (!scope) return;
    this._syncButtonStyleInputs(scope);
    // Also refresh the live preview's effect class — _syncButtonStyleInputs
    // updates inline style + active highlights but doesn't touch the class
    // list. Mirror what updatePreview does at wire time.
    const preview = scope.querySelector('#pb-btn-live-preview');
    if (preview) {
      const stale = Array.from(preview.classList).filter((c) => c.startsWith('pb-effect-'));
      for (const c of stale) preview.classList.remove(c);
      const effect = this._getButtonStyle('hoverEffect');
      if (effect && effect !== 'none') preview.classList.add(`pb-effect-${effect}`);
    }
  }

  // ── Forms drawer ──

  // Phase 6 — Site Effects section. Each toggle row includes a
  // 60x40 CSS-only demo so the user can SEE what the effect does instead
  // of reading the description and guessing. Demo markup is inlined per
  // effect key; styling lives in property-panel.css under `.pb-effect-demo`.
  _renderEffectsSubpanel(scope = null) {
    if (!scope) return;
    const effects = this._getSiteEffectsState();
    const rows = [
      {
        key: 'themeWipe',
        label: 'Theme wipe',
        description: 'Radial ripple animation when toggling dark mode',
        demoHTML: `
          <span class="pb-effect-demo-bg-light"></span>
          <span class="pb-effect-demo-bg-dark"></span>
          <span class="pb-effect-demo-bg-wipe"></span>
        `
      },
      {
        key: 'floatingLabels',
        label: 'Floating labels',
        description: 'Form labels animate above the field on focus',
        demoHTML: `
          <span class="pb-effect-demo-input"></span>
          <span class="pb-effect-demo-label">Email</span>
        `
      },
      {
        key: 'goTopRing',
        label: 'Go-top + scroll ring',
        description: 'Fixed scroll-to-top button with progress ring around an up-arrow',
        demoHTML: `
          <svg viewBox="0 0 40 40" aria-hidden="true">
            <circle class="pb-effect-demo-ring-track" cx="20" cy="20" r="14" fill="none" stroke-width="2"/>
            <circle class="pb-effect-demo-ring-progress" cx="20" cy="20" r="14" fill="none" stroke-width="2"
                    stroke-dasharray="88" stroke-dashoffset="88" transform="rotate(-90 20 20)"/>
            <path d="M20 26 L20 14 M14 20 L20 14 L26 20" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `
      },
      {
        key: 'smoothScroll',
        label: 'Momentum scrolling',
        description: 'Wheel scrolling glides with easing instead of stepping',
        demoHTML: `
          <span class="pb-effect-demo-scroll-page"></span>
          <span class="pb-effect-demo-scroll-track"></span>
          <span class="pb-effect-demo-scroll-thumb"></span>
        `
      }
    ];

    scope.innerHTML = `
      <div class="pb-props-section">
        ${rows
          .map(
            (row) => `
          <label class="pb-toggle-row pb-export-toggle-row pb-design-effect-row" data-site-effect="${row.key}">
            <span class="pb-effect-demo" data-effect="${row.key}">${row.demoHTML}</span>
            <div class="pb-export-feature-copy">
              <span class="pb-props-label">${row.label}</span>
              <span class="pb-feature-desc">${row.description}</span>
            </div>
            <input type="checkbox" class="pb-toggle" data-site-effect-key="${row.key}" ${effects[row.key] ? 'checked' : ''}>
            <span class="pb-toggle-slider"></span>
          </label>
        `
          )
          .join('')}
      </div>
    `;

    for (const input of scope.querySelectorAll('[data-site-effect-key]')) {
      input.addEventListener('change', () => {
        const key = input.dataset.siteEffectKey;
        const row = rows.find((item) => item.key === key);
        store.update(`siteEffects.${key}`, input.checked, `Toggle ${row?.label || key}`);
        // A site effect is a script bound to the canvas window (and, for the
        // go-top ring, markup). Nothing surgical can add or remove that: the
        // document has to be rebuilt, and nothing else asks for it — toggling
        // an effect used to take hold only on the next page switch.
        eventBus.emit('preview:render-request', { preserveScroll: true });
      });
    }
  }

  // Phase 6 — Lightweight refresh for Site Effects. Syncs the three checkbox
  // states without rebuilding the row DOM (so demos don't restart each tick).
  _refreshEffectsSection(scope = null) {
    if (!scope) return;
    const effects = this._getSiteEffectsState();
    for (const input of scope.querySelectorAll('[data-site-effect-key]')) {
      const key = input.dataset.siteEffectKey;
      const wanted = !!effects[key];
      if (input.checked !== wanted) input.checked = wanted;
    }
  }

  _renderFormsDrawer(bodyEl) {
    if (!bodyEl) return;
    const current = store.getState()?.formStyles || {};
    const rows = [
      { kind: 'shape', label: 'Shape', presets: FORM_SHAPES, snapshot: formShapeSnapshot },
      { kind: 'style', label: 'Style', presets: FORM_STYLES, snapshot: formStyleSnapshot },
      {
        kind: 'submit',
        label: 'Submit button',
        presets: FORM_SUBMIT_STYLES,
        snapshot: formSubmitSnapshot
      }
    ];

    this._teardownDesignBoardCleanups('forms');
    bodyEl.innerHTML = '';

    for (const row of rows) {
      const label = document.createElement('div');
      label.className = 'pb-design-forms-section-label';
      label.textContent = row.label;
      bodyEl.appendChild(label);

      const grid = document.createElement('div');
      grid.className = 'pb-design-grid pb-design-forms-grid';
      grid.dataset.formPresetGrid = row.kind;

      for (const preset of row.presets) {
        const card = renderFormPresetCard({
          id: preset.id,
          name: preset.name,
          kind: row.kind,
          active: current[row.kind] === preset.id
        });
        const cleanup = wirePreviewOnHover(card, row.snapshot(preset.id), {
          chipLabel: `Apply ${preset.name || row.label}`
        });
        this._registerDesignCleanup('forms', cleanup);
        grid.appendChild(card);
      }

      bodyEl.appendChild(grid);
    }
  }

  _refreshFormsDrawer(bodyEl) {
    if (!bodyEl) return;
    const current = store.getState()?.formStyles || {};
    for (const card of bodyEl.querySelectorAll('[data-form-preset-kind][data-form-preset-id]')) {
      const kind = card.dataset.formPresetKind;
      const id = card.dataset.formPresetId;
      const active = !!kind && !!id && current[kind] === id;
      card.classList.toggle('pb-preset-card--active', active);
      card.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  _showContextualSectionUI() {
    // A live multi-selection owns the section view (16B2) — reaching this
    // via a tab switch must not fall back to the lead's single body.
    if (this._multiSelection) {
      this._showMultiSectionInfo();
      return;
    }
    if (!this._selectedInstance) return;
    const sharedType = this._getSharedType(this._selectedInstance);
    if (sharedType === 'navigation') {
      this._showNavLinkEditor();
    } else if (sharedType === 'footer') {
      this._showSharedSectionInfo('footer');
    } else {
      this._showSectionInfo({ instanceId: this._selectedInstance });
    }
  }

  /**
   * The left rail's explicit route into the selected element's glass panel.
   *
   * The row stays useful with auto-open enabled: it gives the persistent rail a
   * deterministic way back to the selected element's form after dismissal.
   *
   * It renders the section UI first and PREPENDS itself, so the row sits above
   * the section it belongs to rather than replacing it — the user is editing an
   * element inside a section and both are true at once.
   */
  _showElementSettingsRow(data) {
    // Driven by a canvas click, so route the tab but do not reveal — see
    // `nav-sidebar.js`'s `reveal` contract. The row is ready in the rail when the
    // user opens it; it does not drag the rail over their selection to announce
    // itself.
    this._requestPropertiesTab({ reveal: false });
    this._showContextualSectionUI();
    if (!this._sectionEl) return;

    this._sectionEl.querySelector('.pb-props-element-row')?.remove();

    const row = document.createElement('div');
    row.className = 'pb-props-element-row';

    const label = document.createElement('p');
    label.className = 'pb-props-element-label';
    const elementLabel = kindLabel(data.kind, { tagName: data.tagName });
    label.textContent = elementLabel;
    row.appendChild(label);

    row.appendChild(
      createButton({
        variant: 'ghost',
        size: 'sm',
        className: 'pb-props-element-open',
        icon: icon('sliders', { size: 'sm' }),
        label: 'Edit…',
        ariaLabel: `Edit this ${elementLabel.toLowerCase()}`,
        // The payload is resolved from the settled active-editable store by the
        // handler, NOT captured here: the rail may be acting several
        // interactions after the click that made the selection, and a captured
        // payload would open a panel for whatever was selected then.
        onClick: (e) =>
          eventBus.emit('props:open-element-settings', { triggerEl: e?.currentTarget || null })
      })
    );

    this._sectionEl.prepend(row);
  }

  _getSharedType(instanceId) {
    const state = store.getState();
    if (!state || !state.sharedSections) return null;
    if (
      state.sharedSections.navigation &&
      state.sharedSections.navigation.instanceId === instanceId
    )
      return 'navigation';
    if (state.sharedSections.footer && state.sharedSections.footer.instanceId === instanceId)
      return 'footer';
    return null;
  }

  /**
   * @param {{reveal?: boolean}} [opts] `reveal: false` routes the tab without
   *   forcing the rail open — for paths driven by a CANVAS selection, where
   *   opening the rail would cover the element the user just selected.
   */
  _requestPropertiesTab({ reveal = true } = {}) {
    this._activeTab = 'section';
    eventBus.emit('props:request-tab', { tab: 'properties', reveal });
  }

  _listen() {
    // Query computed spacing from iframe whenever it finishes loading
    eventBus.on('iframe:bridge-ready', () => {
      bridge.querySectionSpacing();
    });

    // When spacing response arrives, update placeholders if a section panel is showing
    eventBus.on('iframe:section-spacing-response', () => {
      this._updateSpacingPlaceholders();
    });

    // Sync internal tab state when SectionList switches tabs
    eventBus.on('sidebar:tab-changed', ({ tab }) => {
      this._activeTab = tab;
      if (tab === 'theme') {
        this._showThemeView();
        this._renderThemePanel();
      } else if (tab === 'section') {
        this._showSectionView();
        if (this._selectedInstance) {
          this._showContextualSectionUI();
        } else {
          this._showSeoPanel();
        }
      }
    });

    eventBus.on('iframe:section-clicked', (data) => {
      closeActivePopoverMenu();
      closeActiveSectionDrawer({ restoreFocus: false });
      // First click outside a glass panel should dismiss that panel only.
      // Do not auto-open the sidebar/properties on this click.
      if (hasOpenPanels()) return;

      this._selectedInstance = data.instanceId;
      if (this._activeTab === 'section') {
        this._showContextualSectionUI();
      }
    });

    eventBus.on('iframe:section-double-clicked', (data) => {
      closeActivePopoverMenu();
      closeActiveSectionDrawer({ restoreFocus: false });
      if (!data?.instanceId) return;
      this._selectedInstance = data.instanceId;
      this._requestPropertiesTab();
      this._showContextualSectionUI();
    });

    eventBus.on('iframe:element-clicked', (data) => {
      // Glass panels own registered types. The rail still records the owning
      // section and renders its explicit "open settings" route, so dismissing
      // the floating panel never strands the selected element.
      this._selectedInstance = data.instanceId;
      if (hasPanelFor(data)) {
        this._showElementSettingsRow(data);
        return;
      }
      // Legacy fallback for shells that have not mounted the glass registry.
      if (data.editableType === 'svg') {
        this._activeSvgData = data;
        // Canvas-driven selection — route, do not reveal (see nav-sidebar.js).
        this._requestPropertiesTab({ reveal: false });
        this._showSvgEditingInfo(data);
      }
    });

    /**
     * Open the glass panel for an editable the LEFT RAIL is pointing at. This
     * explicit route shares the same rect-matched request service as auto-open,
     * while bypassing only the click-specific phone/mode/kill-switch policy.
     *
     * The payload comes from the active-editable store rather than from a
     * remembered click, because the rail may be acting several interactions
     * after the click that made the selection.
     */
    // BC-6: row 2's Background / Spacing / Visible open the drawer they name.
    eventBus.on('props:open-section-drawer', ({ instanceId, drawer, triggerEl } = {}) => {
      this.openSectionDrawerFor({ instanceId, drawer, triggerEl });
    });

    eventBus.on('props:open-element-settings', async ({ triggerEl, focusField } = {}) => {
      const { readActiveEditable } = await import('../editing/active-editable-store.js');
      const payload = readActiveEditable();
      if (!payload) return;
      if (!hasPanelFor(payload)) return;
      const { requestPanelOpen } = await import('../editing/glass-panels.js');
      requestPanelOpen(payload, {
        triggerEl: triggerEl || null,
        focusField: focusField || null,
        claim: false,
        explicit: true,
        // "All settings" is the control that opened this panel, so pressing it
        // again closes it. Only the explicit route toggles — auto-open fires
        // from selection and rect settlement, and a toggling auto-open would
        // shut the panel the moment anything re-reported the active element.
        //
        // NOT when a `focusField` is asked for: row 2's Link and Alt buttons
        // come through this same event to land on a field INSIDE the panel, and
        // a second press of Alt means "put me back on Alt", never "close".
        toggle: !focusField
      });

      // Row 2's Link and Alt buttons land ON their field rather than
      // reimplementing its editor. `#pb-gp-img-alt` and `#pb-gp-img-link-type`
      // already exist in the image panel — the same anchors its own status
      // strip jumps to — so this is the panel's own affordance reached from a
      // second place, not a second affordance.
      //
      // Deferred a frame: the panel populates its tabs during construction and
      // the field may not exist yet. A missing field is a silent no-op, which
      // is the right degradation for a focus hint.
    });

    eventBus.on('section-deselected', () => {
      closeActivePopoverMenu();
      closeActiveSectionDrawer({ restoreFocus: false });
      this._selectedInstance = null;
      this._activeSvgData = null;
      this._multiSelection = null;
      if (this._activeTab === 'section') {
        this._activeTab = 'theme';
        this._showThemeView(false);
        this._renderThemePanel();
      }
    });

    // Multi-selection state enters via `selection:changed` (16B2) — NOT via
    // `iframe:section-clicked`, whose handler bails on hasOpenPanels():
    // correct for a canvas click, but it would silently drop a list-driven
    // shift-click range. At size > 1 the panel shows the multi block; when
    // the set collapses back to one, the ordinary single view returns for
    // the surviving primary.
    eventBus.on('selection:changed', (change) => {
      const wasMulti = !!this._multiSelection;
      const previousIds = wasMulti ? this._multiSelection.ids.slice() : null;
      if (change.size > 1) {
        this._multiSelection = { ids: change.ids.slice(), lead: change.lead };
        this._selectedInstance = change.primary;
        if (this._activeTab === 'section') {
          closeActivePopoverMenu();
          if (wasMulti) {
            // Membership changed under a live multi block: refresh the rows,
            // the header count and the Columns eligibility IN PLACE (an open
            // drawer must survive), then replay the drawer's render so it
            // edits the CURRENT selection — it would otherwise keep writing
            // the ids it captured when it opened, hitting deselected
            // sections and missing newly selected ones.
            this._refreshMultiInspectorRows();
            if (idsChanged(previousIds, change.ids)) {
              const openId = getActiveSectionDrawerId();
              if (openId) {
                setActiveSectionDrawerTitle(this._drawerTitleFor(openId));
                refreshActiveSectionDrawerBody();
              }
            }
          } else {
            closeActiveSectionDrawer({ restoreFocus: false });
            this._showMultiSectionInfo();
          }
        }
        return;
      }
      if (!wasMulti) return;
      this._multiSelection = null;
      if (this._activeTab === 'section') {
        closeActiveSectionDrawer({ restoreFocus: false });
        if (change.primary) {
          this._selectedInstance = change.primary;
          this._showContextualSectionUI();
        }
        // Empty selection unwinds via the section-deselected listener above.
      }
    });

    eventBus.on('iframe:canvas-clicked', () => {
      closeActivePopoverMenu();
      closeActiveSectionDrawer({ restoreFocus: false });
    });

    // Glass overlay spacing handles changed a value — sync our inputs
    eventBus.on('glass:spacing-changed', ({ instanceId, field, value }) => {
      if (!instanceId || instanceId !== this._selectedInstance) return;
      if (!this._sectionEl) return;
      const fieldMap = {
        paddingTop: 'pt',
        paddingBottom: 'pb',
        marginTop: 'mt',
        marginBottom: 'mb'
      };
      const inputKey = fieldMap[field];
      if (!inputKey) return;
      const input = this._sectionEl.querySelector(`[data-pb-override="${inputKey}"]`);
      if (input) input.value = value || '';
      // Sync preset pill active state
      const presetRow = this._sectionEl.querySelector(`.pb-spacing-presets[data-field="${field}"]`);
      if (presetRow) {
        for (const btn of presetRow.querySelectorAll('.pb-spacing-preset')) {
          btn.classList.toggle('pb-active', value !== '' && btn.dataset.value === value);
        }
      }
    });

    eventBus.on('section:request-swap', (data) => {
      // BC-5: ONE Replace browser — the variant picker — for page and shared
      // sections alike, beside the control that asked. The panel's own swap
      // grid searched the active page only, so Replace on the shared
      // navigation or footer did nothing. It stays behind `pb-replace-legacy`
      // until the manual checklist retires it.
      if (!replaceLegacyEnabled()) {
        void openReplaceBrowser({
          instanceId: data?.instanceId,
          triggerEl: data?.triggerEl || null
        });
        return;
      }
      this._selectedInstance = data.instanceId;
      this._requestPropertiesTab();
      this._showSwapUI(data.instanceId);
    });

    eventBus.on('editing:text-started', (data) => {
      if (hasPanelFor('text')) return;
      // Fallback: no-op (glass panels handle text editing)
    });

    eventBus.on('editing:text-ended', () => {
      if (this._selectedInstance && this._activeTab === 'section') {
        this._showContextualSectionUI();
      }
    });

    eventBus.on('editing:image-selected', (data) => {
      if (hasPanelFor('image')) return;
      // Fallback: no-op (glass panels handle image editing)
    });

    eventBus.on('editing:svg-selected', (data) => {
      this._selectedInstance = data.instanceId;
      // Canvas-driven selection — route, do not reveal (see nav-sidebar.js).
      this._requestPropertiesTab({ reveal: false });
      this._showSvgEditingInfo(data);
    });

    eventBus.on('editing:image-replaced', () => {
      if (this._selectedInstance && this._activeTab === 'section') {
        this._showContextualSectionUI();
      }
    });

    eventBus.on('editing:svg-replaced', () => {
      if (this._selectedInstance && this._activeTab === 'section') {
        this._showContextualSectionUI();
      }
    });

    // Re-sync panels when state changes (e.g., after undo/redo/page rename)
    eventBus.on('state-changed', () => {
      // Refresh live template CSS cache when domain set changes.
      this._refreshTemplateCSSCacheIfNeeded();
      if (this._activeTab === 'theme') {
        this._syncThemeInputs();
      } else if (this._activeTab === 'section' && this._selectedInstance) {
        const sharedType = this._getSharedType(this._selectedInstance);
        if (sharedType === 'navigation') {
          this._refreshNavLinkEditorIfNeeded();
        }
        // Only SVG refreshes through PropertyPanel now
        if (this._activeSvgData) {
          this._showSvgEditingInfo(this._activeSvgData);
        }
      }
    });
  }

  // State-changed handler. Fans out to every ported section in the board.
  // Adding new sections means appending one line in _refreshAllDesignSections,
  // not here.
  _syncThemeInputs() {
    if (!this._board) return;
    this._refreshAllDesignSections();
  }

  _syncButtonStyleInputs(scope) {
    const container = scope || this._themeEl;
    const previewModel = this._getDesignButtonPreviewModel();
    const bgFallback = colorValueToHex(previewModel.styles.backgroundColor) || '#ff6a3e';
    const textFallback = colorValueToHex(previewModel.styles.textColor) || '#ffffff';

    // Color inputs
    const btnBg = container.querySelector('#pb-btn-bg');
    const btnBgHex = container.querySelector('#pb-btn-bg-hex');
    const bgVal = this._getButtonStyle('backgroundColor');
    if (btnBg && btnBg.value !== (colorValueToHex(bgVal || '') || bgFallback))
      btnBg.value = colorValueToHex(bgVal || '') || bgFallback;
    if (btnBgHex && btnBgHex !== document.activeElement && btnBgHex.value !== bgVal)
      btnBgHex.value = bgVal;

    const btnText = container.querySelector('#pb-btn-text');
    const btnTextHex = container.querySelector('#pb-btn-text-hex');
    const textVal = this._getButtonStyle('textColor');
    if (btnText && btnText.value !== (colorValueToHex(textVal || '') || textFallback))
      btnText.value = colorValueToHex(textVal || '') || textFallback;
    if (btnTextHex && btnTextHex !== document.activeElement && btnTextHex.value !== textVal)
      btnTextHex.value = textVal;
    this._syncColorSwatches(container, 'pb-btn-bg', bgVal || '');
    this._syncColorSwatches(container, 'pb-btn-text', textVal || '');

    // Preset active states
    const presetIdMap = { borderRadius: 'radius', padding: 'padding', hoverEffect: 'effect' };
    for (const field of ['borderRadius', 'padding', 'hoverEffect']) {
      const current = this._getButtonStyle(field);
      const currentForDisplay = field === 'hoverEffect' ? current || 'none' : current;
      const row = container.querySelector(`#pb-btn-${presetIdMap[field]}-presets`);
      if (!row) continue;
      const segmented = row.querySelector('.pb-segmented');
      if (segmented && typeof segmented.updateValue === 'function') {
        segmented.updateValue(currentForDisplay);
      }
      for (const b of row.querySelectorAll(
        '.pb-segmented-option, .pb-btn-preset-card, .pb-spacing-preset'
      )) {
        const active = b.dataset.value === currentForDisplay;
        b.classList.toggle('pb-active', active);
        if (b.classList.contains('pb-segmented-option')) {
          b.setAttribute('aria-checked', active ? 'true' : 'false');
        }
      }
    }

    // Update live preview if present
    const preview = container.querySelector('#pb-btn-live-preview');
    if (preview) {
      this._applyDesignButtonPreview(preview, previewModel);
      const stale = Array.from(preview.classList).filter((c) => c.startsWith('pb-effect-'));
      for (const c of stale) preview.classList.remove(c);
      const effect = this._getButtonStyle('hoverEffect');
      if (effect && effect !== 'none') preview.classList.add(`pb-effect-${effect}`);
    }
  }

  _showSeoPanel() {
    this._destroySeoPanel();
    this._destroyFormBuilder();
    this._sectionEl.innerHTML = '';
    this._seoPanel = new SEOPanel(this._sectionEl);
  }

  _destroySeoPanel() {
    if (this._seoPanel) {
      this._seoPanel.destroy();
      this._seoPanel = null;
    }
  }

  _showSectionInfo(data) {
    this._destroySectionInfo();
    this._destroySeoPanel();
    this._destroyFormBuilder();
    this._sectionEl.innerHTML = '';

    const page = store.getActivePage();
    const section = page ? page.sections.find((s) => s.instanceId === data.instanceId) : null;
    const category = section ? section.category : data.category || 'Unknown';
    const catalogKey = section ? section.catalogKey : '';

    this._sectionEl.innerHTML = `
      ${this._renderScopeHeader({ section, data, category })}
      ${this._renderSectionBody()}
      ${this._renderSectionFooter({ section, data, catalogKey })}
    `;

    this._wireSectionHeaderActions(data);
    this._mountSectionInspectorRows({ section, data });
    this._wireDisclosureAria();
  }

  /**
   * Tear down per-render listeners + the active drawer (if any) that the
   * v2 inspector body installed. Called from `_showSectionInfo` BEFORE the
   * section element is reset.
   */
  _destroySectionInfo() {
    if (Array.isArray(this._sectionInfoCleanups)) {
      for (const fn of this._sectionInfoCleanups) {
        try {
          fn();
        } catch (err) {
          console.error('[property-panel] cleanup threw:', err);
        }
      }
    }
    this._sectionInfoCleanups = [];
    closeActiveSectionDrawer({ restoreFocus: false });
  }

  /**
   * Render the combined scope-back-chip + section-title header.
   *
   * Replaces the legacy split between `_renderBackButton()` ("‹ Back to Design")
   * and `_renderSectionHeader()` (separate title row), plus the top-zone
   * ScopeBreadcrumb. One row tells the user where they are AND how to back out:
   *
   *   ‹ Home · Hero  · 2 overrides              ⋮
   *
   * Parent label is the active page name for page-sections, or "Sitewide"
   * for shared sections (nav/footer). Clicking the back chip emits
   * `section-deselected` — the same single source of truth the old breadcrumb
   * used. The `· N overrides` suffix moves here from the breadcrumb (no more
   * cross-component sync). Variant + instance debug strings still live in the
   * footer Details disclosure.
   */
  _renderScopeHeader({ section, category }) {
    const categoryLabel = this._escapeAttr(String(category || 'Section'));
    const displayName = section?.displayName
      ? this._escapeAttr(section.displayName)
      : categoryLabel;
    const parentLabel = this._escapeAttr(this._resolveScopeParentLabel(section));
    const overrideCount = section ? getSectionOverrideCount(section) : 0;
    const overrideSuffix =
      overrideCount > 0
        ? `<span class="pb-spi-header-suffix" aria-label="${overrideCount} override${overrideCount === 1 ? '' : 's'}"> · ${overrideCount} override${overrideCount === 1 ? '' : 's'}</span>`
        : '';
    return `
      <div class="pb-spi-header" role="group" aria-label="Section actions">
        <div class="pb-spi-header-title-wrap">
          <button class="pb-spi-header-back" type="button" data-action="scope-back"
            aria-label="Back to ${parentLabel}" data-tooltip="Back to ${parentLabel}">
            <span class="pb-spi-header-back-arrow" aria-hidden="true">&#8249;</span>
            <span class="pb-spi-header-back-label">${parentLabel}</span>
          </button>
          <span class="pb-spi-header-sep" aria-hidden="true">·</span>
          <h2 class="pb-spi-header-title" data-tooltip="${displayName}">${displayName}</h2>
          ${overrideSuffix}
        </div>
        <button class="pb-spi-header-menu-btn" type="button" data-action="more"
          aria-haspopup="menu" aria-expanded="false" aria-label="Section actions">
          <span aria-hidden="true">⋮</span>
        </button>
      </div>
    `;
  }

  /**
   * Return the label shown in the scope back-chip. Mirrors the old breadcrumb's
   * parent-segment logic (scope-breadcrumb.js _computeSegments). Shared
   * sections back out to "Sitewide"; page sections back out to the active
   * page's name. Falls back to "Back" when neither resolves.
   */
  _resolveScopeParentLabel(section) {
    if (!section) return 'Back';
    const state = store.getState();
    const shared = state?.sharedSections || {};
    if (
      section.instanceId &&
      (shared.navigation?.instanceId === section.instanceId ||
        shared.footer?.instanceId === section.instanceId)
    ) {
      return 'Sitewide';
    }
    const page = state?.pages?.find?.((p) => p.id === state.activePage) || state?.pages?.[0];
    return page?.name || 'Back';
  }

  /**
   * Body content — the inspector mount slot. Inspector rows are appended
   * programmatically in `_mountSectionInspectorRows` so they can carry
   * click handlers + cross-fade swap state.
   */
  _renderSectionBody() {
    return `<div class="pb-spi-body" data-spi-body></div>`;
  }

  /**
   * Render the footer — collapsed `<details>` containing the variant catalog
   * key + the instance ID with copy-to-clipboard buttons, plus the
   * Reset-all-overrides pill (visibility driven by
   * `getOverrideState(section).total > 0`; decorative SVG and form edits do
   * not contribute).
   */
  _renderSectionFooter({ section, data, catalogKey }) {
    const variantLabel = this._escapeAttr(catalogKey || section?.catalogKey || 'Custom');
    const instanceLabel = this._escapeAttr(data.instanceId || '');
    const overrideTotal = getSectionOverrideCount(section);
    const resetPill =
      overrideTotal > 0
        ? `
        <div class="pb-spi-footer-actions">
          <button type="button" class="pb-spi-reset-all" data-action="reset-all-overrides">
            <span class="pb-spi-reset-all-icon" aria-hidden="true">⟲</span>
            <span>Reset all overrides</span>
            <span class="pb-spi-reset-all-count" aria-hidden="true">${overrideTotal}</span>
          </button>
        </div>
      `
        : '';
    return `
      <details class="pb-section-details">
        <summary class="pb-section-details-summary">Details</summary>
        <div class="pb-section-details-body">
          <div class="pb-section-details-row">
            <span class="pb-section-details-key">Variant</span>
            <code class="pb-section-details-value" data-tooltip="${variantLabel}">${variantLabel}</code>
            <button type="button" class="pb-section-details-copy" data-copy-value="${variantLabel}" aria-label="Copy variant catalog key">Copy</button>
          </div>
          <div class="pb-section-details-row">
            <span class="pb-section-details-key">Instance</span>
            <code class="pb-section-details-value" data-tooltip="${instanceLabel}">${instanceLabel}</code>
            <button type="button" class="pb-section-details-copy" data-copy-value="${instanceLabel}" aria-label="Copy instance ID">Copy</button>
          </div>
        </div>
      </details>
      ${resetPill}
    `;
  }

  /**
   * v2 inspector body mount (Phase 3 of pb-section-properties overhaul).
   *
   * Inserts inspector rows into the body container, wires drawer-open
   * handlers, registers a state-changed refresh listener for summary
   * glyphs / override dots, and wires the Reset-all-overrides pill.
   */
  _mountSectionInspectorRows({ section, data }) {
    const bodyEl = this._sectionEl.querySelector('[data-spi-body]');
    if (!bodyEl) return;

    const overrides = section ? section.overrides || {} : {};
    const state = getOverrideState(section);

    // Override row: Background
    const bgRow = renderInspectorRow({
      id: 'background',
      label: 'Background',
      summaryHtml: renderBackgroundSummary(overrides),
      hasOverride: state.background,
      onOpen: (e) =>
        this._openSectionInspectorDrawer({ id: 'background', triggerEl: e.currentTarget, data })
    });
    bodyEl.appendChild(bgRow);

    // Override row: Spacing
    const spacingRow = renderInspectorRow({
      id: 'spacing',
      label: 'Spacing',
      summaryHtml: renderSpacingSummary(overrides),
      hasOverride: state.spacing,
      onOpen: (e) =>
        this._openSectionInspectorDrawer({ id: 'spacing', triggerEl: e.currentTarget, data })
    });
    bodyEl.appendChild(spacingRow);

    // Override row: Custom CSS
    const cssRow = renderInspectorRow({
      id: 'custom-css',
      label: 'Custom CSS',
      summaryHtml: renderCustomCssSummary(overrides, data.instanceId),
      hasOverride: state.css,
      onOpen: (e) =>
        this._openSectionInspectorDrawer({ id: 'custom-css', triggerEl: e.currentTarget, data })
    });
    bodyEl.appendChild(cssRow);

    // Override row: Columns - only mounted when the section actually contains
    // a multi-item `cs-*-group > .cs-item` layout.
    const columnGroup = detectMultiItemGroups(section);
    if (columnGroup) {
      bodyEl.appendChild(this._createColumnsInspectorRow({ data, overrides, state, columnGroup }));
    }

    // Override row: Entrance (EA-4). Always mounted — every section can carry
    // an entrance, unlike Columns, which needs a multi-item group to mean
    // anything.
    bodyEl.appendChild(this._createEntranceInspectorRow({ data, overrides, state }));

    // Row: Schedule (SC-1). Page sections only (shared nav/footer are stripped
    // by normalization), behind the pb-scheduled-content escape hatch.
    if (scheduleDrawerEnabled() && !data.shared) {
      bodyEl.appendChild(this._createScheduleInspectorRow({ data, section }));
    }

    // Content rows live below a `─── Content ───` divider.
    bodyEl.appendChild(renderInspectorContentDivider('Content'));

    // Content row: Decorative SVG (always rendered; summary shows `None`
    // when empty so the row still surfaces an Insert affordance via its drawer).
    const svgRow = renderInspectorRow({
      id: 'decorative-svg',
      label: 'Decorative SVG',
      summaryHtml: renderDecorativeSvgSummary(section),
      isContent: true,
      onOpen: (e) =>
        this._openSectionInspectorDrawer({ id: 'decorative-svg', triggerEl: e.currentTarget, data })
    });
    bodyEl.appendChild(svgRow);

    // Content row: Form (only when the section actually contains a <form>).
    if (section && sectionHasForm(section.html)) {
      const formRow = renderInspectorRow({
        id: 'form',
        label: 'Form',
        summaryHtml: renderFormSummary(section),
        isContent: true,
        onOpen: (e) =>
          this._openSectionInspectorDrawer({ id: 'form', triggerEl: e.currentTarget, data })
      });
      bodyEl.appendChild(formRow);
    }

    // Wire footer Reset-all-overrides pill (rendered by _renderSectionFooter
    // when state.total > 0; nothing to wire when absent).
    const resetBtn = this._sectionEl.querySelector('[data-action="reset-all-overrides"]');
    if (resetBtn) {
      resetBtn.addEventListener('click', async () => {
        const total = getSectionOverrideCount(section);
        if (total <= 0) return;
        const confirmed = await showConfirmModal({
          title: 'Reset overrides',
          message: `Reset ${total} override${total === 1 ? '' : 's'} on this section?`,
          confirmLabel: 'Reset',
          cancelLabel: 'Cancel',
          danger: true
        });
        if (!confirmed) return;
        resetAllSectionOverrides(data.instanceId, { source: 'panel-footer-reset' });
      });
    }

    // Refresh inspector summaries + override dots whenever a state change
    // affects this section. We listen to two events:
    //   - `theme:override-changed` fires for every applySectionOverride().
    //   - `state-changed` covers undo/redo + decorative SVG mutations
    //     (those don't go through applySectionOverride but still need to
    //     refresh the SVG row count + svg chips).
    const refresh = () => this._refreshSectionInspectorRows(data.instanceId);
    eventBus.on('theme:override-changed', refresh);
    eventBus.on('state-changed', refresh);
    eventBus.on('section-properties:css-validity-changed', refresh);

    // Override-dot pulse — fires on every transaction-commit boundary that
    // touched a section-level override field for THIS section. We listen to
    // `history-changed` (not `theme:override-changed`) because the latter
    // fires per-update during a drag-scrub (~60Hz) and would strobe the dot.
    // `history-changed` fires once per commit OR once per non-transactional
    // update — the right "yes, that saved" boundary. Also fires on
    // undo/redo, which is useful: the pulse confirms the undo touched a row
    // the user can see.
    const pulse = (payload) => this._pulseOverrideDotsFromPatches(data.instanceId, payload);
    eventBus.on('history-changed', pulse);

    this._sectionInfoCleanups.push(() => {
      eventBus.off('theme:override-changed', refresh);
      eventBus.off('state-changed', refresh);
      eventBus.off('section-properties:css-validity-changed', refresh);
      eventBus.off('history-changed', pulse);
    });
  }

  _createEntranceInspectorRow({ data, overrides, state }) {
    return renderInspectorRow({
      id: 'entrance',
      label: 'Entrance',
      summaryHtml: escapeHTML(renderEntranceSummary(overrides)),
      hasOverride: !!state.entrance,
      onOpen: (e) =>
        this._openSectionInspectorDrawer({ id: 'entrance', triggerEl: e.currentTarget, data })
    });
  }

  _createScheduleInspectorRow({ data, section }) {
    const summary = renderScheduleSummary(section);
    return renderInspectorRow({
      id: 'schedule',
      label: 'Schedule',
      summaryHtml: escapeHTML(summary || 'Always visible'),
      hasOverride: !!summary,
      onOpen: (e) =>
        this._openSectionInspectorDrawer({ id: 'schedule', triggerEl: e.currentTarget, data })
    });
  }

  _createColumnsInspectorRow({ data, overrides, state, columnGroup }) {
    return renderInspectorRow({
      id: 'columns',
      label: 'Columns',
      summaryHtml: renderColumnsSummary(overrides, columnGroup.itemCount),
      hasOverride: !!state.columns,
      onOpen: (e) =>
        this._openSectionInspectorDrawer({ id: 'columns', triggerEl: e.currentTarget, data })
    });
  }

  // ── Multi-selection block (16B2) ─────────────────────────────────────────

  /** The selected sections in document order (missing ids dropped). */
  _resolveMultiSections() {
    const multi = this._multiSelection;
    const page = store.getActivePage();
    if (!multi || !page || !Array.isArray(page.sections)) return [];
    const byId = new Map(page.sections.map((s) => [s.instanceId, s]));
    return multi.ids.map((id) => byId.get(id)).filter(Boolean);
  }

  /** Mixed-value summary HTML for one row (the `—` / "Mixed values" contract). */
  _multiSummaryHtml(sections, fields, renderUniform) {
    let mixed = false;
    const common = {};
    for (const field of fields) {
      const summary = summarizeOverrideAcross(sections, field);
      if (summary.state === 'mixed') {
        mixed = true;
        break;
      }
      if (summary.state === 'uniform-set') common[field] = summary.value;
    }
    if (mixed) {
      return '<span class="pb-spi-mixed" data-mixed="true" aria-label="Mixed values">—</span>';
    }
    return renderUniform(common);
  }

  /**
   * The multi-selection section view: `N sections` header, mixed-value rows,
   * and NOTHING single-target. Rows follow the plan's verdict table:
   * Background ✅ · Spacing ✅ (the marquee) · Custom CSS ❌ disabled with a
   * tooltip (overwriting N sections' bespoke CSS from one textarea is
   * destructive and un-reviewable) · Columns ⚠️ only when every selected
   * section's PARSED group signature and item count agree · content rows
   * (Decorative SVG / Form) omitted — and with no surviving content row, no
   * `── Content ──` divider either.
   */
  _showMultiSectionInfo() {
    const multi = this._multiSelection;
    if (!multi) return;
    this._destroySectionInfo();
    this._destroySeoPanel();
    this._destroyFormBuilder();
    this._sectionEl.innerHTML = '';

    const count = multi.ids.length;
    const state = store.getState();
    const page = state?.pages?.find?.((p) => p.id === state.activePage) || state?.pages?.[0];
    const parentLabel = this._escapeAttr(page?.name || 'Back');

    this._sectionEl.innerHTML = `
      <div class="pb-spi-header" role="group" aria-label="Selection">
        <div class="pb-spi-header-title-wrap">
          <button class="pb-spi-header-back" type="button" data-action="scope-back"
            aria-label="Back to ${parentLabel}" data-tooltip="Back to ${parentLabel}">
            <span class="pb-spi-header-back-arrow" aria-hidden="true">&#8249;</span>
            <span class="pb-spi-header-back-label">${parentLabel}</span>
          </button>
          <span class="pb-spi-header-sep" aria-hidden="true">·</span>
          <h2 class="pb-spi-header-title">${count} sections</h2>
        </div>
      </div>
      <div class="pb-spi-body" data-spi-body data-spi-multi="${count}"></div>
      <div class="pb-spi-multi-footer" data-spi-multi-footer></div>
    `;

    const backBtn = this._sectionEl.querySelector('[data-action="scope-back"]');
    backBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      eventBus.emit('section-deselected');
    });

    this._mountMultiInspectorRows();

    const refresh = () => this._refreshMultiInspectorRows();
    eventBus.on('theme:override-changed', refresh);
    eventBus.on('state-changed', refresh);
    this._sectionInfoCleanups.push(() => {
      eventBus.off('theme:override-changed', refresh);
      eventBus.off('state-changed', refresh);
    });
  }

  _mountMultiInspectorRows() {
    const multi = this._multiSelection;
    const bodyEl = this._sectionEl?.querySelector('[data-spi-body]');
    if (!multi || !bodyEl) return;
    const sections = this._resolveMultiSections();
    const count = multi.ids.length;
    const data = {
      instanceId: multi.lead,
      instanceIds: multi.ids.slice(),
      leadId: multi.lead
    };

    bodyEl.appendChild(renderInspectorContentDivider(`${count} sections`));

    bodyEl.appendChild(
      renderInspectorRow({
        id: 'background',
        label: 'Background',
        summaryHtml: this._multiSummaryHtml(sections, ['backgroundColor'], (common) =>
          renderBackgroundSummary(common)
        ),
        hasOverride: sections.some((s) => getOverrideState(s).background),
        onOpen: (e) =>
          this._openSectionInspectorDrawer({ id: 'background', triggerEl: e.currentTarget, data })
      })
    );

    bodyEl.appendChild(
      renderInspectorRow({
        id: 'spacing',
        label: 'Spacing',
        summaryHtml: this._multiSummaryHtml(sections, SPACING_OVERRIDE_FIELDS, (common) =>
          renderSpacingSummary(common)
        ),
        hasOverride: sections.some((s) => getOverrideState(s).spacing),
        onOpen: (e) =>
          this._openSectionInspectorDrawer({ id: 'spacing', triggerEl: e.currentTarget, data })
      })
    );

    // Custom CSS is DISABLED at N > 1: one textarea overwriting N sections'
    // bespoke CSS is destructive and un-reviewable, and the CodeMirror txn
    // lifecycle assumes one instance.
    const cssRow = renderInspectorRow({
      id: 'custom-css',
      label: 'Custom CSS',
      summaryText: 'Select one section',
      hasOverride: sections.some((s) => getOverrideState(s).css),
      ariaLabel: 'Custom CSS — select a single section to edit'
    });
    cssRow.disabled = true;
    cssRow.classList.add('pb-spi-row-disabled');
    cssRow.title = 'Custom CSS can only be edited one section at a time';
    bodyEl.appendChild(cssRow);

    // Entrance (EA-4). Unlike Columns this needs NO structural agreement —
    // every section can carry an entrance — so it mounts unconditionally and
    // drives the whole selection through the existing bulk writer.
    bodyEl.appendChild(
      renderInspectorRow({
        id: 'entrance',
        label: 'Entrance',
        summaryHtml: this._multiSummaryHtml(sections, [ENTRANCE_OVERRIDE_FIELD], (common) =>
          renderEntranceSummary(common)
        ),
        hasOverride: sections.some((s) => getOverrideState(s).entrance),
        onOpen: (e) =>
          this._openSectionInspectorDrawer({ id: 'entrance', triggerEl: e.currentTarget, data })
      })
    );

    // Columns: equal catalogKey is NOT proof of compatible structure —
    // compare the PARSED group signature and item count across every
    // selected section, and mount the row only on full agreement.
    const groups = sections.map((s) => detectMultiItemGroups(s));
    const signaturesAgree =
      groups.length > 0 &&
      groups.every(
        (g) =>
          g && g.groupSelector === groups[0].groupSelector && g.itemCount === groups[0].itemCount
      );
    if (signaturesAgree) {
      bodyEl.appendChild(
        renderInspectorRow({
          id: 'columns',
          label: 'Columns',
          summaryHtml: this._multiSummaryHtml(sections, ['columnCount'], (common) =>
            renderColumnsSummary(common, groups[0].itemCount)
          ),
          hasOverride: sections.some((s) => getOverrideState(s).columns),
          onOpen: (e) =>
            this._openSectionInspectorDrawer({ id: 'columns', triggerEl: e.currentTarget, data })
        })
      );
    }

    this._renderMultiResetFooter(sections, data);
  }

  _renderMultiResetFooter(sections, data) {
    const footer = this._sectionEl?.querySelector('[data-spi-multi-footer]');
    if (!footer) return;
    const total = sections.reduce((n, s) => n + getSectionOverrideCount(s), 0);
    if (total <= 0) {
      footer.innerHTML = '';
      return;
    }
    footer.innerHTML = `
      <div class="pb-spi-footer-actions">
        <button type="button" class="pb-spi-reset-all" data-action="reset-all-overrides">
          <span class="pb-spi-reset-all-icon" aria-hidden="true">⟲</span>
          <span>Reset all overrides</span>
          <span class="pb-spi-reset-all-count" aria-hidden="true">${total}</span>
        </button>
      </div>
    `;
    footer
      .querySelector('[data-action="reset-all-overrides"]')
      ?.addEventListener('click', async () => {
        const liveTotal = this._resolveMultiSections().reduce(
          (n, s) => n + getSectionOverrideCount(s),
          0
        );
        if (liveTotal <= 0) return;
        const confirmed = await showConfirmModal({
          title: 'Reset overrides',
          message: `Reset ${liveTotal} override${liveTotal === 1 ? '' : 's'} across ${data.instanceIds.length} sections?`,
          confirmLabel: 'Reset',
          cancelLabel: 'Cancel',
          danger: true
        });
        if (!confirmed) return;
        resetAllSectionOverridesBulk(data.instanceIds, {
          source: 'panel-footer-reset',
          lead: data.leadId
        });
      });
  }

  /** In-place refresh of the multi rows — never re-renders (drawers survive). */
  _refreshMultiInspectorRows() {
    const multi = this._multiSelection;
    const bodyEl = this._sectionEl?.querySelector('[data-spi-body][data-spi-multi]');
    if (!multi || !bodyEl) return;
    const sections = this._resolveMultiSections();

    // The header count and the divider label are part of the block's state,
    // not decoration — a membership change must move them.
    const count = multi.ids.length;
    bodyEl.dataset.spiMulti = String(count);
    const headerTitle = this._sectionEl.querySelector('.pb-spi-header-title');
    if (headerTitle) headerTitle.textContent = `${count} sections`;
    const dividerLabel = bodyEl.querySelector('.pb-spi-divider-label');
    if (dividerLabel) dividerLabel.textContent = `${count} sections`;

    const updateRow = (id, summaryHtml, hasOverride) => {
      const row = bodyEl.querySelector(`[data-spi-row-id="${id}"]`);
      if (!row) return;
      const summary = row.querySelector('.pb-spi-row-summary');
      if (summary) summary.innerHTML = summaryHtml;
      row.classList.toggle('pb-spi-row-has-override', hasOverride);
      const gutter = row.querySelector('.pb-spi-row-gutter');
      if (gutter) {
        const dot = gutter.querySelector('.pb-spi-row-override-dot');
        if (hasOverride && !dot) {
          gutter.insertAdjacentHTML('beforeend', '<span class="pb-spi-row-override-dot"></span>');
        } else if (!hasOverride && dot) {
          dot.remove();
        }
      }
    };

    updateRow(
      'background',
      this._multiSummaryHtml(sections, ['backgroundColor'], (common) =>
        renderBackgroundSummary(common)
      ),
      sections.some((s) => getOverrideState(s).background)
    );
    updateRow(
      'spacing',
      this._multiSummaryHtml(sections, SPACING_OVERRIDE_FIELDS, (common) =>
        renderSpacingSummary(common)
      ),
      sections.some((s) => getOverrideState(s).spacing)
    );
    updateRow(
      'entrance',
      this._multiSummaryHtml(sections, [ENTRANCE_OVERRIDE_FIELD], (common) =>
        renderEntranceSummary(common)
      ),
      sections.some((s) => getOverrideState(s).entrance)
    );
    // Columns eligibility is a property of the CURRENT set: adding a section
    // whose parsed group signature disagrees must retire the row, and
    // narrowing back to an agreeing set must restore it. Re-evaluated here,
    // not just refreshed when already present.
    const groups = sections.map((s) => detectMultiItemGroups(s));
    const signaturesAgree =
      groups.length > 0 &&
      groups.every(
        (g) =>
          g && g.groupSelector === groups[0].groupSelector && g.itemCount === groups[0].itemCount
      );
    const columnsRow = bodyEl.querySelector('[data-spi-row-id="columns"]');
    if (signaturesAgree) {
      if (columnsRow) {
        updateRow(
          'columns',
          this._multiSummaryHtml(sections, ['columnCount'], (common) =>
            renderColumnsSummary(common, groups[0].itemCount)
          ),
          sections.some((s) => getOverrideState(s).columns)
        );
      } else {
        const data = {
          instanceId: multi.lead,
          instanceIds: multi.ids.slice(),
          leadId: multi.lead
        };
        bodyEl.appendChild(
          renderInspectorRow({
            id: 'columns',
            label: 'Columns',
            summaryHtml: this._multiSummaryHtml(sections, ['columnCount'], (common) =>
              renderColumnsSummary(common, groups[0].itemCount)
            ),
            hasOverride: sections.some((s) => getOverrideState(s).columns),
            onOpen: (e) =>
              this._openSectionInspectorDrawer({ id: 'columns', triggerEl: e.currentTarget, data })
          })
        );
      }
    } else if (columnsRow) {
      // The open Columns drawer would now be lying about what it drives.
      if (getActiveSectionDrawerId() === 'columns') {
        closeActiveSectionDrawer({ restoreFocus: false });
      }
      columnsRow.remove();
    }
    this._renderMultiResetFooter(sections, {
      instanceIds: multi.ids.slice(),
      leadId: multi.lead
    });
  }

  /**
   * Walk the patches in a `history-changed` event and pulse the override
   * dot of any inspector row whose field was touched on this section.
   *
   * Patch path shape: `pages.{p}.sections.{s}.overrides.{field}`. Resolved
   * against the post-commit state via the section index; we look up the
   * section at that index in the current store state and confirm
   * `instanceId` matches before pulsing — defends against the panel showing
   * one section while a different section was just edited elsewhere.
   *
   * Bulk-reset path (`...sections.S.overrides` written as a whole object)
   * pulses every override row so the user sees confirmation across the board.
   */
  _pulseOverrideDotsFromPatches(instanceId, payload) {
    const patches = Array.isArray(payload?.patches) ? payload.patches : null;
    if (!patches || patches.length === 0) return;
    if (!this._sectionEl) return;
    const bodyEl = this._sectionEl.querySelector('[data-spi-body]');
    if (!bodyEl) return;

    const FIELD_TO_ROW = {
      backgroundColor: 'background',
      marginTop: 'spacing',
      marginBottom: 'spacing',
      paddingTop: 'spacing',
      paddingBottom: 'spacing',
      columnCount: 'columns',
      customCSS: 'custom-css'
    };

    const rowsToPulse = new Set();
    const state = store.getState();
    const pages = state && Array.isArray(state.pages) ? state.pages : [];

    for (const patch of patches) {
      if (!patch || typeof patch.path !== 'string') continue;
      const match = patch.path.match(/^pages\.(\d+)\.sections\.(\d+)\.overrides(?:\.([^.]+))?$/);
      if (!match) continue;
      const pageIdx = Number(match[1]);
      const sectionIdx = Number(match[2]);
      const field = match[3] || null;
      const section = pages[pageIdx]?.sections?.[sectionIdx];
      if (!section || section.instanceId !== instanceId) continue;

      if (field) {
        const rowId = FIELD_TO_ROW[field];
        if (rowId) rowsToPulse.add(rowId);
      } else {
        rowsToPulse.add('background');
        rowsToPulse.add('spacing');
        rowsToPulse.add('columns');
        rowsToPulse.add('custom-css');
      }
    }

    for (const rowId of rowsToPulse) {
      const row = bodyEl.querySelector(`[data-spi-row-id="${rowId}"]`);
      if (!row) continue;
      const dot = row.querySelector('.pb-spi-row-override-dot');
      if (!dot) continue;
      // Restart-trick: remove + reflow + re-add so consecutive pulses
      // restart cleanly instead of being absorbed by the in-flight one.
      dot.classList.remove('pb-spi-row-override-dot-pulse');
      void dot.offsetWidth;
      dot.classList.add('pb-spi-row-override-dot-pulse');
    }
  }

  /** Re-render summary glyphs + override dots without rebuilding the rows. */
  _refreshSectionInspectorRows(instanceId) {
    if (!this._sectionEl) return;
    const bodyEl = this._sectionEl.querySelector('[data-spi-body]');
    if (!bodyEl) return;

    const page = store.getActivePage();
    const section = page ? page.sections.find((s) => s.instanceId === instanceId) : null;
    if (!section) return;
    const overrides = section.overrides || {};
    const state = getOverrideState(section);

    const updateRow = (id, summaryHtml, hasOverride, isContent = false) => {
      const row = bodyEl.querySelector(`[data-spi-row-id="${id}"]`);
      if (!row) return;
      const summary = row.querySelector('.pb-spi-row-summary');
      if (summary) summary.innerHTML = summaryHtml;
      if (!isContent) {
        row.classList.toggle('pb-spi-row-has-override', hasOverride);
        const gutter = row.querySelector('.pb-spi-row-gutter');
        if (gutter) {
          const dot = gutter.querySelector('.pb-spi-row-override-dot');
          if (hasOverride && !dot) {
            gutter.insertAdjacentHTML('beforeend', '<span class="pb-spi-row-override-dot"></span>');
          } else if (!hasOverride && dot) {
            dot.remove();
          }
        }
      }
    };

    updateRow('background', renderBackgroundSummary(overrides), state.background);
    updateRow('spacing', renderSpacingSummary(overrides), state.spacing);
    updateRow('custom-css', renderCustomCssSummary(overrides, instanceId), state.css);
    updateRow('entrance', escapeHTML(renderEntranceSummary(overrides)), state.entrance);

    const columnGroup = detectMultiItemGroups(section);
    const columnsRow = bodyEl.querySelector('[data-spi-row-id="columns"]');
    const columnsNotice = bodyEl.querySelector('[data-spi-columns-unavailable]');
    if (columnGroup) {
      columnsNotice?.remove();
      if (columnsRow) {
        updateRow('columns', renderColumnsSummary(overrides, columnGroup.itemCount), state.columns);
      } else {
        const row = this._createColumnsInspectorRow({
          data: { instanceId },
          overrides,
          state,
          columnGroup
        });
        const divider = bodyEl.querySelector('.pb-spi-divider');
        if (divider) bodyEl.insertBefore(row, divider);
        else bodyEl.appendChild(row);
      }
    } else if (columnsRow) {
      if (getActiveSectionDrawerId() === 'columns') {
        closeActiveSectionDrawer({ restoreFocus: false });
      }
      const notice = document.createElement('div');
      notice.className = 'pb-spi-capability-notice';
      notice.dataset.spiColumnsUnavailable = '';
      notice.setAttribute('role', 'status');
      notice.textContent =
        'Columns are unavailable because this section no longer has a repeated item layout.';
      const divider = bodyEl.querySelector('.pb-spi-divider');
      if (divider) bodyEl.insertBefore(notice, divider);
      else bodyEl.appendChild(notice);
      columnsRow.remove();
    }
    updateRow('decorative-svg', renderDecorativeSvgSummary(section), false, true);
    if (section && sectionHasForm(section.html)) {
      updateRow('form', renderFormSummary(section), false, true);
    }

    // Phase 2 — refresh the `· N overrides` suffix on the scope header.
    // (Previously the breadcrumb owned this suffix and watched state-changed
    // itself; the chip-in-header version doesn't have that auto-update path,
    // so we refresh inline whenever overrides change.)
    const headerTitleWrap = this._sectionEl.querySelector('.pb-spi-header-title-wrap');
    if (headerTitleWrap) {
      const existingSuffix = headerTitleWrap.querySelector('.pb-spi-header-suffix');
      const sectionOverrideCount = section ? getSectionOverrideCount(section) : 0;
      if (sectionOverrideCount > 0) {
        const labelText = ` · ${sectionOverrideCount} override${sectionOverrideCount === 1 ? '' : 's'}`;
        if (existingSuffix) {
          existingSuffix.textContent = labelText;
          existingSuffix.setAttribute(
            'aria-label',
            `${sectionOverrideCount} override${sectionOverrideCount === 1 ? '' : 's'}`
          );
        } else {
          const suffix = document.createElement('span');
          suffix.className = 'pb-spi-header-suffix';
          suffix.textContent = labelText;
          suffix.setAttribute(
            'aria-label',
            `${sectionOverrideCount} override${sectionOverrideCount === 1 ? '' : 's'}`
          );
          headerTitleWrap.appendChild(suffix);
        }
      } else if (existingSuffix) {
        existingSuffix.remove();
      }
    }

    // Refresh footer pill visibility / count.
    const footerActions = this._sectionEl.querySelector('.pb-spi-footer-actions');
    const detailsEl = this._sectionEl.querySelector('.pb-section-details');
    if (state.total > 0) {
      const countSpan = footerActions?.querySelector('.pb-spi-reset-all-count');
      if (countSpan) {
        countSpan.textContent = String(state.total);
      } else if (detailsEl) {
        const wrap = document.createElement('div');
        wrap.className = 'pb-spi-footer-actions';
        wrap.innerHTML = `
          <button type="button" class="pb-spi-reset-all" data-action="reset-all-overrides">
            <span class="pb-spi-reset-all-icon" aria-hidden="true">⟲</span>
            <span>Reset all overrides</span>
            <span class="pb-spi-reset-all-count" aria-hidden="true">${state.total}</span>
          </button>
        `;
        detailsEl.insertAdjacentElement('afterend', wrap);
        wrap
          .querySelector('[data-action="reset-all-overrides"]')
          .addEventListener('click', async () => {
            const total = getSectionOverrideCount(section);
            if (total <= 0) return;
            const confirmed = await showConfirmModal({
              title: 'Reset overrides',
              message: `Reset ${total} override${total === 1 ? '' : 's'} on this section?`,
              confirmLabel: 'Reset',
              cancelLabel: 'Cancel',
              danger: true
            });
            if (!confirmed) return;
            resetAllSectionOverrides(instanceId, { source: 'panel-footer-reset' });
          });
      }
    } else if (footerActions) {
      footerActions.remove();
    }
  }

  /**
   * Open (or swap to) the drawer for a given inspector row id.
   *
   * The drawer is anchored to the panel-left container that holds the
   * triggering row. Its body is filled with the existing edit UI for the
   * row's section property; `_wireOverrides(drawerBody, instanceId)` runs
   * inside the drawer DOM so all the existing handlers (preset pills,
   * nudges, color swatches, CSS editor) work unchanged.
   */
  /**
   * BC-6. The public route to one section drawer, for surfaces outside the
   * inspector (row 2 of the command bar). It anchors BELOW the control that
   * asked, because that control is in the top bar rather than beside a row,
   * and focus returns to it on close.
   * @returns {boolean} whether a drawer opened
   */
  openSectionDrawerFor({ instanceId, drawer, triggerEl } = {}) {
    if (!instanceId || !SECTION_DRAWER_IDS.has(drawer)) return false;
    // The shared nav/footer is on every page; a schedule would hide it site-wide
    // and the inspector never offers one there either.
    if (drawer === 'schedule' && (!scheduleDrawerEnabled() || this._getSharedType(instanceId))) {
      return false;
    }
    const anchor = triggerEl?.isConnected ? triggerEl : null;
    this._openSectionInspectorDrawer({
      id: drawer,
      triggerEl: anchor || this._resolveDrawerAnchor(),
      anchorEl: anchor,
      placement: anchor ? 'below' : 'beside',
      data: { instanceId }
    });
    return true;
  }

  _openSectionInspectorDrawer({ id, triggerEl, data, anchorEl: anchorOverride, placement }) {
    const anchorEl = anchorOverride || this._resolveDrawerAnchor() || triggerEl;
    const instanceId = data.instanceId;

    openSectionDrawer({
      id,
      title: this._drawerTitleFor(id),
      anchorEl,
      triggerEl,
      placement,
      // NOTHING about the selection is captured here: the render resolves
      // `this._multiSelection` every time it runs, so a membership change can
      // replay it (refreshActiveSectionDrawerBody) and the drawer edits the
      // CURRENT selection rather than the one it opened with (16B2 fix).
      render: (bodyEl) => this._renderSectionDrawerBody(bodyEl, id, instanceId),
      beforeClose: () => {
        // CSS textarea opens a `store.beginTransaction('Edit custom CSS')`
        // on focus; commit any open one before the drawer DOM disappears
        // so undo/redo stays clean. `commitTransaction()` is a safe no-op
        // when no transaction is active.
        try {
          store.commitTransaction();
        } catch {
          /* noop */
        }
      }
    });
  }

  _resolveDrawerAnchor() {
    // Anchor on the panel-left container that holds the section panel so the
    // drawer slides over the canvas and tracks the panel's right edge.
    return this._sectionEl?.closest('.pb-panel-left') || this._sectionEl;
  }

  /**
   * Fill a drawer body with the relevant edit UI for the given row id and
   * wire it via `_wireOverrides`. For Decorative SVG / Form rows we mount
   * the existing components verbatim per Phase 7 / Phase 8 of the plan.
   */
  /** Drawer title, with the live `· N sections` suffix while multi-selected. */
  _drawerTitleFor(id) {
    const titleMap = {
      background: 'Background',
      spacing: 'Spacing',
      'custom-css': 'Custom CSS',
      columns: 'Columns',
      'decorative-svg': 'Decorative SVG',
      form: 'Form',
      // BC-6: these two rendered their raw id ("schedule") as the drawer title.
      entrance: 'Entrance',
      schedule: 'Schedule'
    };
    const base = titleMap[id] || id;
    const size = this._multiSelection?.ids?.length || 0;
    return size > 1 ? `${base} · ${size} sections` : base;
  }

  _renderSectionDrawerBody(bodyEl, id, instanceId) {
    const page = store.getActivePage();
    const section = page ? page.sections.find((s) => s.instanceId === instanceId) : null;
    const overrides = section ? section.overrides || {} : {};

    bodyEl.innerHTML = '';
    // Resolved LIVE, never captured — see _openSectionInspectorDrawer.
    const multi = this._multiSelection;
    const ctx =
      multi && multi.ids.length > 1
        ? { instanceId: multi.lead, instanceIds: multi.ids.slice(), leadId: multi.lead }
        : { instanceId };

    if (id === 'background') {
      // Phase 4 — token-first picker + custom disclosure + reset pill, all
      // routed through `applySectionOverride('backgroundColor', ...)`.
      // Returns the teardown for drawer.js to invoke on swap/close.
      return renderBackgroundDrawer(bodyEl, { ...ctx, panel: this });
    }

    if (id === 'spacing') {
      // Phase 5 — vertical-slab diagram with scrubbable bands, click-to-preset,
      // inline edit, link top↔bottom toggle, and reset-all. Routes through
      // `applySectionOverride` exactly like the legacy preset+nudge stack so
      // every field still emits the five-event contract from Phase 1.
      return renderSectionSpacingDrawer(bodyEl, ctx);
    }

    if (id === 'custom-css') {
      // Phase 6 — adopt `mountCodeEditor()` from glass-panel-section.js
      // verbatim. CodeMirror with line numbers, theme-token completion, and
      // validity that natively handles both rules and bare declarations.
      // Returns the teardown for drawer.js to invoke on swap/close.
      // (Never reached at N > 1 — the multi row is disabled by design.)
      return renderCustomCssDrawer(bodyEl, { instanceId });
    }

    if (id === 'columns') {
      // Phase 4 of the four-editor-capabilities plan — segmented chip strip
      // for the `columnCount` override on sections with a multi-item group.
      return renderColumnsDrawer(bodyEl, ctx);
    }

    if (id === 'entrance') {
      // EA-4 — the per-section `revealPreset` override. Site default / None /
      // one of the presets, written through the same override writer as every
      // other row so the render request in D9 fires unchanged.
      return renderEntranceDrawer(bodyEl, ctx);
    }

    if (id === 'schedule') {
      // SC-1 — the per-section visibility window (scheduled-content-contracts §1).
      return renderScheduleDrawer(bodyEl, ctx);
    }

    if (id === 'decorative-svg') {
      // Phase 7 — Insert moves to the drawer header, count folds into the
      // drawer title, body becomes a clean list. The wrapper returns a
      // teardown that removes the injected Insert button on swap/close.
      return renderSvgDrawer(bodyEl, { instanceId });
    }

    if (id === 'form') {
      // Form drawer module owns the FormBuilder lifecycle and returns a
      // teardown so drawer.js can destroy() it on swap/close. Instance is
      // closure-scoped — `this._formBuilder` is no longer assigned anywhere
      // (the legacy v1 inline appendage path was removed in Phase 10).
      return renderFormDrawer(bodyEl, { instanceId });
    }
  }

  _wireSectionHeaderActions(data) {
    // Scope back-chip — same pop-scope action the old breadcrumb used.
    // Emits `section-deselected`; the property panel + sidebar both unwind
    // from that single event (same listeners that handled the old breadcrumb
    // click + the legacy "Back to Design" button).
    const backBtn = this._sectionEl.querySelector('[data-action="scope-back"]');
    if (backBtn) {
      backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        eventBus.emit('section-deselected');
      });
    }

    // ⋮ menu — Swap / Duplicate / Delete. Same `iframe:section-action`
    // events the legacy 3-button row emitted; no event names changed by
    // the v2 rewrite.
    const moreBtn = this._sectionEl.querySelector('[data-action="more"]');
    if (moreBtn) {
      moreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPopoverMenu(moreBtn, {
          menuLabel: 'Section actions',
          align: 'right',
          consumeCanvasDismiss: true,
          items: [
            {
              id: 'swap',
              label: '↔  Swap variant',
              onSelect: () => {
                eventBus.emit('iframe:section-action', {
                  action: 'swap',
                  instanceId: data.instanceId,
                  // BC-5: the picker anchors beside the control that asked.
                  triggerEl: moreBtn
                });
              }
            },
            {
              id: 'duplicate',
              label: '⧉  Duplicate',
              onSelect: () => {
                eventBus.emit('iframe:section-action', {
                  action: 'duplicate',
                  instanceId: data.instanceId
                });
              }
            },
            { type: 'separator', danger: true },
            {
              id: 'delete',
              label: '⌫  Delete',
              danger: true,
              onSelect: () => {
                eventBus.emit('iframe:section-action', {
                  action: 'delete',
                  instanceId: data.instanceId
                });
              }
            }
          ]
        });
      });
    }

    // Copy-to-clipboard buttons inside the footer Details disclosure
    // (variant + instance debug strings).
    for (const btn of this._sectionEl.querySelectorAll('.pb-section-details-copy')) {
      btn.addEventListener('click', async () => {
        const val = btn.dataset.copyValue || '';
        if (!val) return;
        try {
          await navigator.clipboard.writeText(val);
          const originalLabel = btn.textContent;
          btn.textContent = 'Copied';
          btn.classList.add('pb-section-details-copy-ok');
          setTimeout(() => {
            btn.textContent = originalLabel;
            btn.classList.remove('pb-section-details-copy-ok');
          }, 1200);
        } catch (err) {
          console.warn('[property-panel] clipboard write failed:', err);
        }
      });
    }
  }

  _destroyFormBuilder() {
    if (this._formBuilder) {
      this._formBuilder.destroy();
      this._formBuilder = null;
    }
  }

  /**
   * Thin delegate to the kit's setButtonBusy. This method WAS the reference
   * implementation; Polish 2 phase CK-1 lifted it into components.js so every
   * surface gets the same exact-restore semantics. Keeps the panel's own spinner
   * class so its visual treatment is unchanged.
   */
  _setActionButtonBusy(btn, busy, busyLabel = 'Loading…') {
    setButtonBusy(btn, busy, { busyLabel, className: 'pb-props-btn-loading' });
  }

  _setSpacingPlaceholdersLoading(container = this._sectionEl) {
    if (!container) return;
    for (const key of ['mt', 'mb', 'pt', 'pb']) {
      const input = container.querySelector(`[data-pb-override="${key}"]`);
      if (input && !input.value) input.placeholder = 'Loading…';
    }
  }

  /**
   * Wire all section-override controls inside `container` for the given
   * instance. Container-scoped + data-attribute-keyed so multiple drawers can
   * coexist (Phase 3+ of pb-section-properties overhaul).
   *
   * Mutation pipeline flows through `applySectionOverride()` from
   * section-properties/apply-section-override.js — single source of truth for
   * the five-emission contract (store update + theme:override-changed +
   * glass:spacing-changed + query-section-positions + querySectionSpacing).
   */
  _wireOverrides(container, instanceId) {
    if (!container) return;
    const bgColor = container.querySelector('[data-pb-override="bg"]');
    const bgHex = container.querySelector('[data-pb-override="bg-hex"]');
    const mtInput = container.querySelector('[data-pb-override="mt"]');
    const mbInput = container.querySelector('[data-pb-override="mb"]');
    const ptInput = container.querySelector('[data-pb-override="pt"]');
    const pbInput = container.querySelector('[data-pb-override="pb"]');
    const cssInput = container.querySelector('[data-pb-override="css"]');

    const SPACING_KEY_TO_FIELD = {
      mt: 'marginTop',
      mb: 'marginBottom',
      pt: 'paddingTop',
      pb: 'paddingBottom'
    };
    const SPACING_FIELD_NAMES = new Set(Object.values(SPACING_KEY_TO_FIELD));

    const applyOverride = (field, value) => {
      const ok = applySectionOverride(instanceId, field, value, { source: 'property-panel' });
      if (ok && SPACING_FIELD_NAMES.has(field)) {
        // Re-query computed spacing after override CSS is applied in iframe
        this._setSpacingPlaceholdersLoading(container);
      }
      return ok;
    };

    // Sync .pb-active state on preset pills for a given spacing field
    const syncPresets = (field, value) => {
      const row = container.querySelector(`.pb-spacing-presets[data-field="${field}"]`);
      if (!row) return;
      for (const b of row.querySelectorAll('.pb-spacing-preset')) {
        b.classList.toggle('pb-active', value !== '' && b.dataset.value === value);
      }
    };

    // Parse a CSS value like "2rem" or "-1.5rem" into a number (in rem). Returns 0 for empty/unparseable.
    const parseRem = (val) => {
      if (!val) return 0;
      const match = val.trim().match(/^(-?[\d.]+)\s*rem$/);
      return match ? parseFloat(match[1]) : 0;
    };

    // Nudge buttons: add delta (in rem) to the target input's current value
    for (const btn of container.querySelectorAll('.pb-nudge-btn[data-target-override]')) {
      btn.addEventListener('click', () => {
        const targetKey = btn.dataset.targetOverride;
        const delta = parseFloat(btn.dataset.delta);
        const input = container.querySelector(`[data-pb-override="${targetKey}"]`);
        if (!input) return;

        const current = parseRem(input.value);
        const next = Math.round((current + delta) * 10) / 10; // avoid float drift
        const newVal = next === 0 ? '' : `${next}rem`;
        input.value = newVal;

        const field = SPACING_KEY_TO_FIELD[targetKey];
        if (field) {
          applyOverride(field, newVal);
          syncPresets(field, newVal);
        }
      });
    }

    // Spacing preset pill buttons
    for (const row of container.querySelectorAll('.pb-spacing-presets[data-input-override]')) {
      for (const btn of row.querySelectorAll('.pb-spacing-preset')) {
        btn.addEventListener('click', () => {
          const field = row.dataset.field;
          const inputKey = row.dataset.inputOverride;
          const input = container.querySelector(`[data-pb-override="${inputKey}"]`);
          const val = btn.dataset.value;

          // Check actual current override value, not CSS class
          const currentVal = input ? input.value : '';
          const isActive = currentVal === val;

          // Toggle: clicking active preset clears the override
          const newVal = isActive ? '' : val;
          if (input) input.value = newVal;
          applyOverride(field, newVal);
          syncPresets(field, newVal);
        });
      }
    }

    if (bgColor && bgHex) {
      bgColor.addEventListener('input', () => {
        bgHex.value = bgColor.value;
        this._syncColorSwatches(container, 'pb-override-bg', bgColor.value);
      });

      bgColor.addEventListener('change', () => {
        applyOverride('backgroundColor', bgColor.value);
        this._syncColorSwatches(container, 'pb-override-bg', bgColor.value);
      });

      bgHex.addEventListener('change', () => {
        const val = bgHex.value.trim();
        if (!val) {
          applyOverride('backgroundColor', '');
          bgColor.value = '#ffffff';
          this._syncColorSwatches(container, 'pb-override-bg', '');
          return;
        }
        if (/^#[0-9a-fA-F]{3,8}$/.test(val)) {
          bgColor.value = val.length <= 7 ? val : val.slice(0, 7);
          applyOverride('backgroundColor', val);
          this._syncColorSwatches(container, 'pb-override-bg', val);
        } else {
          flashError(bgHex);
          bgHex.value = bgColor.value;
        }
      });

      for (const swatch of container.querySelectorAll(
        '.pb-color-swatch-btn[data-color-target="pb-override-bg"]'
      )) {
        swatch.addEventListener('click', () => {
          const storedValue = swatch.dataset.colorValue || '';
          const swatchValue = normalizeHex(swatch.dataset.colorHex || storedValue);
          if (!swatchValue) return;
          bgColor.value = swatchValue;
          bgHex.value = storedValue || swatchValue;
          applyOverride('backgroundColor', storedValue || swatchValue);
          this._syncColorSwatches(container, 'pb-override-bg', storedValue || swatchValue);
        });
      }
      this._syncColorSwatches(container, 'pb-override-bg', bgHex.value || '');
    }

    if (mtInput)
      mtInput.addEventListener('change', () => {
        const v = mtInput.value.trim();
        applyOverride('marginTop', v);
        syncPresets('marginTop', v);
      });

    if (mbInput)
      mbInput.addEventListener('change', () => {
        const v = mbInput.value.trim();
        applyOverride('marginBottom', v);
        syncPresets('marginBottom', v);
      });

    if (ptInput)
      ptInput.addEventListener('change', () => {
        const v = ptInput.value.trim();
        applyOverride('paddingTop', v);
        syncPresets('paddingTop', v);
      });

    if (pbInput)
      pbInput.addEventListener('change', () => {
        const v = pbInput.value.trim();
        applyOverride('paddingBottom', v);
        syncPresets('paddingBottom', v);
      });

    // CSS editor: debounced live preview + tab key support
    if (cssInput) {
      let cssDebounce = null;
      cssInput.addEventListener('focus', () => store.beginTransaction('Edit custom CSS'));
      cssInput.addEventListener('blur', () => {
        clearTimeout(cssDebounce);
        store.commitTransaction();
        applyOverride('customCSS', cssInput.value.trim());
      });
      cssInput.addEventListener('input', () => {
        clearTimeout(cssDebounce);
        cssDebounce = setTimeout(() => {
          applyOverride('customCSS', cssInput.value.trim());
        }, 300);
      });
      cssInput.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          const start = cssInput.selectionStart;
          const end = cssInput.selectionEnd;
          cssInput.value =
            cssInput.value.substring(0, start) + '  ' + cssInput.value.substring(end);
          cssInput.selectionStart = cssInput.selectionEnd = start + 2;
          cssInput.dispatchEvent(new Event('input'));
        }
      });
    }

    // Populate placeholders with computed spacing from iframe.
    // Show a loading placeholder while async spacing arrives.
    this._setSpacingPlaceholdersLoading(container);
    bridge.querySectionSpacing();
    this._updateSpacingPlaceholders(container);
  }

  _updateSpacingPlaceholders(container = this._sectionEl) {
    if (!this._selectedInstance) return;
    if (!container) return;
    const measurement = bridge.getSectionSpacing(this._selectedInstance);
    const spacing = measurement?.values;
    if (!spacing) return;

    const pxToRem = (px) => {
      const num = parseFloat(px);
      if (isNaN(num) || num === 0) return '0';
      return `${Math.round((num / measurement.rootPx) * 100) / 100}rem`;
    };

    const fields = [
      { key: 'mt', state: 'marginTop' },
      { key: 'mb', state: 'marginBottom' },
      { key: 'pt', state: 'paddingTop' },
      { key: 'pb', state: 'paddingBottom' }
    ];

    for (const { key, state } of fields) {
      const input = container.querySelector(`[data-pb-override="${key}"]`);
      if (input && !input.value) {
        input.placeholder = pxToRem(spacing[state]);
      }
    }
  }

  async _showSwapUI(instanceId) {
    const page = store.getActivePage();
    if (!page) return;
    const section = page.sections.find((s) => s.instanceId === instanceId);
    if (!section) return;
    const swapCategory = String(section.category || '').toLowerCase();
    const isNavigationSwap =
      swapCategory === 'navigation' ||
      swapCategory.includes('navigation') ||
      swapCategory.includes('nav');
    // PP-2: variants from other templates are folded until revealed for this
    // panel's lifetime — a reveal button, not a persisted checkbox (the
    // `variants.showTemplateSpecific` preference is gone everywhere).
    let showTemplateSpecificVariants = false;

    this._sectionEl.innerHTML = `
      <div class="pb-props-section-info">
        <span>Swap variant for: <strong>${isUnknownCategory(section.category) ? 'all categories (unknown)' : escapeHTML(section.category)}</strong></span>
        <button class="pb-props-btn pb-props-btn-small" id="pb-swap-back">&larr; Back</button>
      </div>
      <div class="pb-props-section">
        <div class="pb-props-section-title">Choose Variant</div>
        <div class="pb-swap-filter-row" id="pb-swap-template-row" hidden>
          <button type="button" class="pb-btn pb-btn-outline pb-btn-sm" id="pb-swap-template-toggle" aria-expanded="false">
            <span id="pb-swap-template-label">Show variants from other templates</span>
          </button>
        </div>
        <div class="pb-swap-grid${isNavigationSwap ? ' pb-swap-grid-nav' : ''}" id="pb-swap-grid">
          <div class="pb-swap-skeleton"></div>
          <div class="pb-swap-skeleton"></div>
          <div class="pb-swap-skeleton"></div>
        </div>
      </div>
    `;

    this._sectionEl.querySelector('#pb-swap-back').addEventListener('click', () => {
      this._showSectionInfo({ instanceId });
    });

    const variants = await getCategorySections(section.category);
    const grid = this._sectionEl.querySelector('#pb-swap-grid');
    const templateRow = this._sectionEl.querySelector('#pb-swap-template-row');
    const templateToggle = this._sectionEl.querySelector('#pb-swap-template-toggle');
    const templateLabel = this._sectionEl.querySelector('#pb-swap-template-label');

    if (!variants || variants.length === 0) {
      grid.innerHTML =
        '<p class="pb-props-hint">No other variants available for this category.</p>';
      return;
    }

    const renderVariantCards = () => {
      const visibleVariants = showTemplateSpecificVariants
        ? variants
        : variants.filter((variant) => !isTemplateSpecificSectionVariant(variant));

      grid.innerHTML = '';

      if (visibleVariants.length === 0) {
        grid.innerHTML =
          '<p class="pb-props-hint">Only variants from other templates are available here. Show them with the button above.</p>';
        return;
      }

      for (const variant of visibleVariants) {
        const isCurrent = variant.catalogKey === section.catalogKey;
        const card = document.createElement('div');
        card.className = `pb-swap-card${isCurrent ? ' pb-swap-current' : ''}`;
        card.setAttribute('role', 'button');
        card.tabIndex = isCurrent ? -1 : 0;
        card.setAttribute('aria-label', `${variant.description}${isCurrent ? ' (current)' : ''}`);
        if (isCurrent) card.setAttribute('aria-disabled', 'true');

        let thumbnailSrc = null;
        if (
          variant.templateSiteName &&
          variant.templateSectionId &&
          shouldUseTemplateSectionThumbnail(variant)
        ) {
          thumbnailSrc = templateSectionThumbnail(
            variant.templateSiteName,
            variant.templateSectionId,
            { sizes: SWAP_CARD_THUMB_SIZES }
          );
        } else if (variant.thumbnail) {
          thumbnailSrc = catalogSectionThumbnail(variant.thumbnail, {
            sizes: SWAP_CARD_THUMB_SIZES
          });
        }

        card.innerHTML = `
          ${thumbnailSrc ? `<img ${thumbnailAttrs(thumbnailSrc)} alt="${escapeHTML(variant.description)}" />` : ''}
          <span class="pb-swap-card-name">${escapeHTML(variant.description)}${isCurrent ? ' (current)' : ''}</span>
        `;
        const nameEl = card.querySelector('.pb-swap-card-name');
        if (nameEl) nameEl.dataset.tooltip = variant.description;
        // Was `onerror="this.remove()"` — one failure and the thumbnail was gone,
        // so a template variant with no per-section shot never tried the
        // homepage screenshot the ladder now offers it.
        installThumbnailFallback(card, (img) => img.remove());

        if (!isCurrent) {
          card.addEventListener('click', async () => {
            card.classList.add('pb-swap-loading');
            try {
              await swapSection(instanceId, variant.catalogKey, variant.category);
              this._showSectionInfo({ instanceId });
            } finally {
              card.classList.remove('pb-swap-loading');
            }
          });
          card.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            card.click();
          });
        }

        grid.appendChild(card);
      }
    };

    const templateSpecificCount = variants.filter((variant) =>
      isTemplateSpecificSectionVariant(variant)
    ).length;
    if (templateSpecificCount > 0 && templateRow && templateToggle && templateLabel) {
      templateRow.hidden = false;
      templateLabel.textContent = `Show ${templateSpecificCount} more from other templates`;
      templateToggle.addEventListener('click', () => {
        showTemplateSpecificVariants = true;
        templateToggle.setAttribute('aria-expanded', 'true');
        templateRow.hidden = true;
        renderVariantCards();
      });
    } else {
      showTemplateSpecificVariants = true;
    }

    renderVariantCards();
  }

  _showSharedSectionInfo(type) {
    this._destroySeoPanel();
    this._destroyFormBuilder();
    const state = store.getState();
    const section = state.sharedSections[type];
    if (!section) return;

    this._sectionEl.innerHTML = `
      ${this._renderBackButton()}
      <div class="pb-props-section-info">
        <span>Shared: <strong>${type}</strong></span>
        <span>Variant: <strong>${section.catalogKey || ''}</strong></span>
      </div>
      <div class="pb-props-section">
        <p class="pb-props-hint">This ${type} is shared across all pages. Edit its text and images directly in the preview.</p>
        ${this._renderSharedStylesNotice()}
      </div>
    `;
  }

  _renderSharedStylesNotice() {
    return `
      <div class="pb-shared-styles-notice">
        <span>Shared sections use sitewide colors, fonts, buttons, and effects.</span>
        <button type="button" class="pb-props-btn pb-props-btn-small" data-action="open-shared-design">Open Design</button>
      </div>
    `;
  }

  _showNavLinkEditor() {
    this._destroySeoPanel();
    this._destroyFormBuilder();
    const state = store.getState();
    const nav = state.sharedSections.navigation;
    if (!nav || !nav.html) {
      // illustration() is trusted static registry markup — never escapeHTML'd.
      this._sectionEl.innerHTML = `
        ${this._renderBackButton()}
        <div class="pb-empty-state">
          <div class="pb-illo pb-illo--sm">${illustration('sections')}</div>
          <span>No navigation section loaded</span>
        </div>
      `;
      return;
    }

    // Parse nav HTML to extract links. BC-3: STRUCTURAL discovery — the
    // CodeStitch classes are a fast path, not the definition — so an imported
    // template's navigation no longer opens an empty editor. Each entry is a
    // GROUP of anchors (a menu rendered twice, desktop + drawer, edits as one).
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${nav.html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    const discovered = discoverNavLinks(root);
    const topLinks = discovered.top;
    const dropLinks = discovered.drop;

    // BC-3: a page destination is the route the EXPORTERS resolve (`/` for the
    // home page, `/<slug>/` honouring the page's stored slug) — not a filename
    // guessed from the page's display name.
    const pages = state.pages;
    const destinations = pageDestinationOptions(pages);
    const pageOptions = destinations
      .map(
        (d) =>
          `<option value="${this._escapeAttr(d.href)}" data-page-id="${this._escapeAttr(d.id)}">${this._escapeAttr(d.label)}</option>`
      )
      .join('');
    const hrefOptions = `<option value="">Custom URL</option>${pageOptions}`;

    if (topLinks.length === 0 && dropLinks.length === 0) {
      this._sectionEl.innerHTML = `
        ${this._renderBackButton()}
        <div class="pb-props-section-info">
          <span>Shared: <strong>Navigation</strong></span>
        </div>
        ${this._renderMobileMenuSection(nav, root)}
        <div class="pb-props-section" data-role="nav-links-empty">
          <div class="pb-props-section-title">Nav Links</div>
          <p class="pb-props-hint">This navigation's links can't be listed here. Select a link on the page to edit its text and destination directly.</p>
          <button type="button" class="pb-props-btn pb-props-btn-small" data-action="nav-select-on-canvas">Select the navigation on the page</button>
        </div>
      `;
      this._sectionEl
        .querySelector('[data-action="nav-select-on-canvas"]')
        ?.addEventListener('click', () => {
          eventBus.emit('section:select', { instanceId: nav.instanceId });
        });
      this._wireMobileMenuSection();
      this._navEditorHtmlSignature = nav.html;
      this._navEditorRefreshPending = false;
      return;
    }

    this._sectionEl.innerHTML = `
      ${this._renderBackButton()}
      <div class="pb-props-section-info">
        <span>Shared: <strong>Navigation</strong></span>
        <span>Variant: <strong>${nav.catalogKey || ''}</strong></span>
      </div>
      ${this._renderMobileMenuSection(nav, root)}
      <div class="pb-props-section">
        <div class="pb-props-section-title">Nav Links</div>
        <p class="pb-props-hint">Edit navigation link labels and destinations.</p>
        ${this._renderSharedStylesNotice()}
        <div class="pb-nav-links" id="pb-nav-links">
          ${topLinks
            .map((link, i) => {
              const href = link.href;
              const clientKey = parseClientLinkMarker(href);
              return `
            <div class="pb-nav-link-row" data-link-index="${i}" data-link-type="top">
              <input type="text" class="pb-nav-link-label" value="${this._escapeAttr(link.label)}" placeholder="Label" aria-label="Nav link label" spellcheck="false" />
              <select class="pb-nav-link-href-select" aria-label="Nav link destination">
                ${hrefOptions}
              </select>
              <select class="pb-nav-link-client-select" aria-label="Nav link client field">
                ${this._clientLinkOptions(clientKey)}
              </select>
              <input type="text" class="pb-nav-link-href" value="${this._escapeAttr(href)}" placeholder="URL or page" aria-label="Nav link URL" spellcheck="false" />
            </div>
            `;
            })
            .join('')}
        </div>
      </div>
      ${
        dropLinks.length > 0
          ? `
      <div class="pb-props-section">
        <div class="pb-props-section-title">Dropdown Links</div>
        <div class="pb-nav-links" id="pb-nav-drop-links">
          ${dropLinks
            .map((link, i) => {
              const href = link.href;
              const clientKey = parseClientLinkMarker(href);
              return `
            <div class="pb-nav-link-row" data-link-index="${i}" data-link-type="drop">
              <input type="text" class="pb-nav-link-label" value="${this._escapeAttr(link.label)}" placeholder="Label" aria-label="Dropdown link label" spellcheck="false" />
              <select class="pb-nav-link-client-select" aria-label="Dropdown link client field">
                ${this._clientLinkOptions(clientKey)}
              </select>
              <input type="text" class="pb-nav-link-href" value="${this._escapeAttr(href)}" placeholder="URL" aria-label="Dropdown link URL" spellcheck="false" />
            </div>
            `;
            })
            .join('')}
        </div>
      </div>
      `
          : ''
      }
    `;

    // Set select values to match current hrefs
    const rows = this._sectionEl.querySelectorAll('.pb-nav-link-row[data-link-type="top"]');
    rows.forEach((row, i) => {
      const select = row.querySelector('.pb-nav-link-href-select');
      if (select) {
        // BC-3: select the page the href RESOLVES to, the way the exporter
        // resolves it — `about.html`, `/about/` and `about` are one page — not
        // only an option whose value is byte-identical.
        const currentHref = topLinks[i].href;
        const page = pageForInternalHref(currentHref, pages);
        const option = page
          ? Array.from(select.options).find((o) => o.dataset.pageId === page.id)
          : null;
        if (option) select.value = option.value;
      }
    });

    // Wire change handlers
    this._wireNavLinkEditors(topLinks, dropLinks);
    this._wireMobileMenuSection();
    this._navEditorHtmlSignature = nav.html;
    this._navEditorRefreshPending = false;
  }

  /**
   * MM-3: the Mobile Menu sub-section of the nav panel — current fragment,
   * Change/Preview/Reset actions (eligibility-gated with the specific
   * reason), and the chrome-text mini-editor. The drawer cannot be clicked on
   * canvas in edit mode (the editing CSS flattens fixed nav), so fragment
   * chrome text is edited here.
   */
  _renderMobileMenuSection(nav, navRootWrapper) {
    const eligibility = checkNavHtmlEligibility(nav.html);
    const mobileMenu = nav.mobileMenu || null;
    const currentName = mobileMenu
      ? this._formatFragmentName(mobileMenu.fragmentKey)
      : 'Stock drawer';

    const chromeTextEls = navRootWrapper
      ? Array.from(
          navRootWrapper.querySelectorAll(
            `[${MARKER_ATTRS.chrome}] [data-pb-editable="text"], [${MARKER_ATTRS.chrome}][data-pb-editable="text"]`
          )
        )
      : [];
    const chromeRows = chromeTextEls
      .filter((el) => el.getAttribute('data-pb-eid'))
      .map((el) => {
        const eid = el.getAttribute('data-pb-eid');
        const text = el.textContent.replace(/\s+/g, ' ').trim();
        return `
          <div class="pb-mm-chrome-row">
            <input type="text" class="pb-mm-chrome-input" data-mm-eid="${this._escapeAttr(eid)}"
              value="${this._escapeAttr(text)}" aria-label="Mobile menu text" spellcheck="false" />
          </div>`;
      })
      .join('');

    const hint = eligibility.eligible
      ? 'Swap the drawer’s look — links, hamburger, and desktop bar stay yours.'
      : this._escapeAttr(eligibility.reasonCopy || 'This navigation cannot host a mobile menu.');

    return `
      <div class="pb-props-section" data-role="mm-section">
        <div class="pb-props-section-title">Mobile Menu</div>
        <div class="pb-mm-current" data-testid="mm-current">
          <span class="pb-mm-current-name">${this._escapeAttr(currentName)}</span>
        </div>
        <p class="pb-props-hint">${hint}</p>
        <div class="pb-mm-actions">
          <button type="button" class="pb-props-btn pb-props-btn-small" data-action="mm-change"
            data-testid="mm-change" ${eligibility.eligible ? '' : 'disabled'}>Change…</button>
          <button type="button" class="pb-props-btn pb-props-btn-small" data-action="mm-preview"
            data-testid="mm-preview">Preview menu</button>
          <button type="button" class="pb-props-btn pb-props-btn-small" data-action="mm-reset"
            data-testid="mm-reset" ${mobileMenu ? '' : 'disabled'}>Reset to stock</button>
        </div>
        ${
          chromeRows
            ? `<div class="pb-mm-chrome-editor">
                 <div class="pb-props-subtitle">Menu text</div>
                 ${chromeRows}
               </div>`
            : ''
        }
      </div>`;
  }

  _formatFragmentName(fragmentKey) {
    const site = String(fragmentKey || '')
      .replace(/^mm-template-/, '')
      .replace(/-cs-navigation$/, '')
      .replace(/-(com|net|org|app|digital)$/, '');
    if (!site) return fragmentKey || 'Custom menu';
    return site.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  _wireMobileMenuSection() {
    const section = this._sectionEl.querySelector('[data-role="mm-section"]');
    if (!section) return;
    section.querySelector('[data-action="mm-change"]')?.addEventListener('click', () => {
      eventBus.emit('mobile-menu:open-picker');
    });
    section.querySelector('[data-action="mm-preview"]')?.addEventListener('click', () => {
      eventBus.emit('mobile-menu:preview');
    });
    const resetBtn = section.querySelector('[data-action="mm-reset"]');
    resetBtn?.addEventListener('click', async () => {
      setButtonBusy(resetBtn, true);
      try {
        await resetMobileMenuToStock();
      } finally {
        setButtonBusy(resetBtn, false);
      }
    });
    for (const input of section.querySelectorAll('.pb-mm-chrome-input')) {
      input.addEventListener('change', () => {
        this._updateMobileMenuChromeText(input.dataset.mmEid, input.value);
      });
    }
  }

  _updateMobileMenuChromeText(eid, value) {
    if (!eid) return;
    const nav = store.getState().sharedSections.navigation;
    if (!nav || !nav.html) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${nav.html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    const target = root.querySelector(`[data-pb-eid="${CSS.escape(eid)}"]`);
    if (!target) return;
    target.textContent = value;
    const updatedHtml = root.innerHTML;
    store.update('sharedSections.navigation.html', updatedHtml, 'Edit mobile menu text');
    this._navEditorHtmlSignature = updatedHtml;
    this._navEditorRefreshPending = false;
    eventBus.emit('nav:html-changed'); // drawer script listeners — full re-render
  }

  _refreshNavLinkEditorIfNeeded() {
    const html = store.getState()?.sharedSections?.navigation?.html || '';
    if (html === this._navEditorHtmlSignature) return;
    if (this._sectionEl.contains(document.activeElement)) {
      this._navEditorRefreshPending = true;
      return;
    }
    this._showNavLinkEditor();
  }

  _wireNavLinkEditors(topLinks, dropLinks) {
    const allRows = this._sectionEl.querySelectorAll('.pb-nav-link-row');

    for (const row of allRows) {
      const linkType = row.dataset.linkType;
      const linkIndex = parseInt(row.dataset.linkIndex, 10);
      const labelInput = row.querySelector('.pb-nav-link-label');
      const hrefInput = row.querySelector('.pb-nav-link-href');
      const hrefSelect = row.querySelector('.pb-nav-link-href-select');
      const clientSelect = row.querySelector('.pb-nav-link-client-select');

      const applyChange = () => {
        const clientKey = clientSelect?.value || '';
        const hrefValue = clientKey ? makeClientLinkMarker(clientKey) : hrefInput.value;
        this._updateNavLink(linkType, linkIndex, labelInput.value, hrefValue);
      };

      labelInput.addEventListener('change', applyChange);
      hrefInput.addEventListener('change', () => {
        const markerKey = parseClientLinkMarker(hrefInput.value.trim());
        if (clientSelect) {
          if (markerKey) clientSelect.value = markerKey;
          else if (clientSelect.value) clientSelect.value = '';
        }
        applyChange();
      });

      if (hrefSelect) {
        hrefSelect.addEventListener('change', () => {
          if (hrefSelect.value) {
            hrefInput.value = hrefSelect.value;
            if (clientSelect) clientSelect.value = '';
            applyChange();
          }
        });
      }

      if (clientSelect) {
        clientSelect.addEventListener('change', () => {
          const bindingKey = clientSelect.value;
          if (bindingKey) {
            const marker = makeClientLinkMarker(bindingKey);
            if (marker) hrefInput.value = marker;

            // Auto-update label if it looks like a placeholder
            const currentLabel = (labelInput.value || '').trim();
            if (currentLabel && isPlaceholderTextForBinding(currentLabel, bindingKey)) {
              const state = store.getState();
              const clientText = getDisplayTextForBinding(
                bindingKey,
                state.clientInfo,
                state.projectName
              );
              if (clientText) labelInput.value = clientText;
            }
          } else if (parseClientLinkMarker(hrefInput.value)) {
            hrefInput.value = '';
          }
          applyChange();
        });
      }
    }
  }

  _updateNavLink(linkType, linkIndex, newLabel, newHref) {
    const state = store.getState();
    const nav = state.sharedSections.navigation;
    if (!nav || !nav.html) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${nav.html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    // Same discovery as the render, over the same HTML, so group indexes agree.
    const discovered = discoverNavLinks(root);
    const group = (linkType === 'top' ? discovered.top : discovered.drop)[linkIndex];
    if (!group) return;

    const trimmedLabel = newLabel.trim();
    for (const link of group.links) {
      if (trimmedLabel) link.textContent = trimmedLabel;
      link.setAttribute('href', newHref);
    }

    const updatedHtml = root.innerHTML;
    store.update('sharedSections.navigation.html', updatedHtml, 'Edit nav link');
    this._navEditorHtmlSignature = updatedHtml;
    this._navEditorRefreshPending = false;
    eventBus.emit('nav:html-changed'); // Structural change — needs full re-render
  }

  _escapeAttr(str) {
    return escapeAttr(str);
  }

  _clientLinkOptions(selectedKey = '') {
    return [
      '<option value="">No client binding</option>',
      ...CLIENT_LINK_BINDINGS.map(
        (binding) =>
          `<option value="${binding.key}" ${selectedKey === binding.key ? 'selected' : ''}>${this._escapeAttr(binding.label)}</option>`
      )
    ].join('');
  }

  _svgIcon(name) {
    const icons = {
      eye: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2.1 12s3.6-7 9.9-7 9.9 7 9.9 7-3.6 7-9.9 7-9.9-7-9.9-7Z"/><circle cx="12" cy="12" r="3"/></svg>',
      eyeOff:
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.9 17.9A10.4 10.4 0 0 1 12 20C5 20 1 12 1 12a18.7 18.7 0 0 1 5.1-5.9"/><path d="M9.9 4.2A10.8 10.8 0 0 1 12 4c7 0 11 8 11 8a18.2 18.2 0 0 1-2.2 3.2"/><path d="M14.1 14.1a3 3 0 0 1-4.2-4.2"/><path d="m1 1 22 22"/></svg>',
      trash:
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>',
      library:
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>',
      image:
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/></svg>'
    };
    return icons[name] || '';
  }

  _renderSvgPreview(svgEl) {
    if (!svgEl) return '<span>SVG</span>';
    const clone = svgEl.cloneNode(true);
    for (const unsafe of clone.querySelectorAll('script, foreignObject, iframe, object, embed')) {
      unsafe.remove();
    }
    clone.removeAttribute('data-pb-editable');
    clone.removeAttribute('data-pb-eid');
    clone.removeAttribute('style');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('focusable', 'false');
    clone.setAttribute('width', '72');
    clone.setAttribute('height', '72');
    return clone.outerHTML;
  }

  _isWideDecorativeSvg(svgEl) {
    if (!svgEl) return false;
    const viewBox = (svgEl.getAttribute('viewBox') || '')
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (
      viewBox.length >= 4 &&
      Number.isFinite(viewBox[2]) &&
      Number.isFinite(viewBox[3]) &&
      viewBox[3] > 0
    ) {
      return viewBox[2] / viewBox[3] >= 2.5;
    }

    return false;
  }

  _showSvgEditingInfo(data) {
    this._activeSvgData = data;

    // Resolve section and extract SVG element
    const svgEl = this._getSvgFromSection(data.instanceId, data.editableIndex, data.eid);
    const colorContext = this._buildSvgColorContext(data);
    const colors = svgEl ? this._extractSvgColors(svgEl, colorContext) : [];
    const originalColors = svgEl
      ? this._getSvgOriginalColors(data, svgEl, colors, colorContext)
      : [];
    const isWideDecorativeSvg = this._isWideDecorativeSvg(svgEl);
    const svgWidthPresets = isWideDecorativeSvg
      ? [
          { name: 'Default', value: '' },
          { name: 'Full', value: '100%' },
          { name: 'Wide', value: '115%' },
          { name: 'Wider', value: '130%' }
        ]
      : [
          { name: 'Default', value: '' },
          { name: 'Small', value: '48px' },
          { name: 'Medium', value: '96px' },
          { name: 'Large', value: '150px' },
          { name: 'Full', value: '100%' }
        ];
    const svgHeightPresets = isWideDecorativeSvg
      ? [
          { name: 'Default', value: '' },
          { name: 'Slim', value: '120px' },
          { name: 'Medium', value: '220px' },
          { name: 'Tall', value: '340px' },
          { name: 'Auto', value: 'auto' }
        ]
      : [
          { name: 'Default', value: '' },
          { name: 'Small', value: '48px' },
          { name: 'Medium', value: '96px' },
          { name: 'Large', value: '150px' },
          { name: 'Auto', value: 'auto' }
        ];
    const svgAspectPresets = [
      { name: 'Default', value: '' },
      { name: 'Meet', value: 'xMidYMid meet' },
      { name: 'Slice', value: 'xMidYMid slice' },
      { name: 'None', value: 'none' }
    ];
    const svgWidthFallback = this._closestPresetLengthValue(
      svgWidthPresets,
      data.computedWidth,
      data.computedRootFontSize
    );
    const svgHeightFallback = this._normalizeSvgHeightPreset(
      data.computedHeight,
      svgHeightPresets,
      data.computedRootFontSize
    );
    const svgAspectFallback = this._normalizeSvgAspectPreset(data.svgPreserveAspectRatio);

    const colorRows = colors
      .map((c, index) => {
        const tokenAttr = c.token ? ` data-color-token="${this._escapeAttr(c.token)}"` : '';
        const tokenLabel = c.token ? ` (${this._escapeAttr(c.token)})` : '';
        return `
      <div class="pb-svg-color-row">
        <div class="pb-svg-color-meta">
          <span class="pb-svg-color-name">Color ${index + 1}${tokenLabel}</span>
          <span class="pb-svg-color-usage">${c.count > 1 ? `Used ${c.count} times` : 'Used once'}</span>
        </div>
        ${this._renderTokenFirstColorInput({
          colorId: `pb-svg-color-${index}`,
          hexId: `pb-svg-color-hex-${index}`,
          colorValue: c.hex,
          hexValue: c.hex,
          placeholder: '#000000',
          colorAriaLabel: `SVG color ${c.hex}`,
          hexAriaLabel: 'SVG color hex value',
          colorInputClass: 'pb-svg-color',
          hexInputClass: 'pb-svg-color-hex',
          extraColorAttrs: `data-old-color="${c.hex}"${tokenAttr}`,
          extraHexAttrs: `data-old-color="${c.hex}"${tokenAttr}`,
          leadingSwatches: [
            {
              label: 'Original',
              value: originalColors[index] || c.hex,
              visibleLabel: true
            }
          ]
        })}
      </div>
    `;
      })
      .join('');

    const isSharedSvg = !!this._getSharedType(data.instanceId);
    const canEditElement = !isSharedSvg && !!data.eid;
    const isHidden =
      canEditElement && this._getElementOverride(data.instanceId, data.eid, 'hidden') === true;
    const previewMarkup = this._renderSvgPreview(svgEl);
    const colorSummary =
      colors.length === 0
        ? 'No solid colors'
        : `${colors.length} editable color${colors.length === 1 ? '' : 's'}`;
    const selectedKind = data.kind || (isWideDecorativeSvg ? 'decoration' : 'icon');
    const selectedLabel = kindLabel(selectedKind);

    this._sectionEl.innerHTML = `
      ${this._renderBackButton()}
      <div class="pb-svg-editor">
        <div class="pb-svg-hero">
          <div class="pb-svg-preview pb-checker" aria-hidden="true">${previewMarkup}</div>
          <div class="pb-svg-hero-copy">
            <span class="pb-svg-eyebrow">Selected Element</span>
            <h2 class="pb-svg-title">${selectedLabel}</h2>
            <p class="pb-svg-subtitle">${colorSummary}</p>
          </div>
          ${
            canEditElement
              ? `
          <div class="pb-svg-icon-actions" aria-label="${selectedLabel} actions">
            <button type="button" class="pb-svg-icon-btn${isHidden ? ' pb-active' : ''}" id="pb-svg-hide" data-tooltip="${isHidden ? 'Show element' : 'Hide element'}" aria-label="${isHidden ? 'Show element' : 'Hide element'}">
              ${this._svgIcon(isHidden ? 'eye' : 'eyeOff')}
            </button>
            <button type="button" class="pb-svg-icon-btn pb-danger" id="pb-svg-delete" data-tooltip="Delete element" aria-label="Delete element">
              ${this._svgIcon('trash')}
            </button>
          </div>
          `
              : ''
          }
        </div>

        <div class="pb-svg-panel-section">
          <div class="pb-svg-section-heading">Replace</div>
          <div class="pb-svg-command-grid">
            <button type="button" class="pb-svg-command" id="pb-open-svg-library">
              ${this._svgIcon('library')}
              <span>
                <strong>${selectedKind === 'decoration' ? 'Browse Decoration Library' : 'Browse Icon Library'}</strong>
                <small>${selectedKind === 'decoration' ? 'Pick a wave, divider, mask, or graphic' : 'Pick another icon'}</small>
              </span>
            </button>
            <button type="button" class="pb-svg-command" id="pb-swap-to-image">
              ${this._svgIcon('image')}
              <span>
                <strong>Replace with Raster</strong>
                <small>Upload or choose raster art</small>
              </span>
            </button>
          </div>
        </div>

        <div class="pb-svg-panel-section">
          <div class="pb-svg-section-heading">Colors</div>
          ${
            colors.length > 0
              ? `<div class="pb-svg-colors">${colorRows}</div>`
              : '<div class="pb-svg-empty-state">This SVG has no solid fill or stroke colors that can be edited from the panel.</div>'
          }
        </div>

      ${
        canEditElement
          ? `
        <div class="pb-svg-panel-section">
        <div class="pb-svg-section-heading">Size</div>
        ${this._elementPresetRow('Width', 'width', svgWidthPresets, data.instanceId, data.eid, svgWidthFallback, isWideDecorativeSvg ? 'data-svg-wide="true"' : '')}
        ${this._elementPresetRow('Height', 'height', svgHeightPresets, data.instanceId, data.eid, svgHeightFallback, isWideDecorativeSvg ? 'data-svg-wide="true"' : '')}
        ${this._elementPresetRow('Aspect Ratio', 'preserveAspectRatio', svgAspectPresets, data.instanceId, data.eid, svgAspectFallback)}
        </div>
      `
          : ''
      }
      </div>
    `;

    const openSvgBtn = this._sectionEl.querySelector('#pb-open-svg-library');
    openSvgBtn.addEventListener('click', async () => {
      this._setActionButtonBusy(openSvgBtn, true, 'Loading SVGs…');
      try {
        await openReplaceDialog({
          target: {
            ...data,
            kind: data.kind || (isWideDecorativeSvg ? 'decoration' : 'icon')
          }
        });
      } finally {
        this._setActionButtonBusy(openSvgBtn, false);
      }
    });
    this._sectionEl.querySelector('#pb-swap-to-image').addEventListener('click', () => {
      swapSvgToImage();
    });

    this._wireSvgHeaderActions(data);
    // Wire SVG color pickers
    this._wireSvgColorPickers(data);
    this._wireElementPresets();
  }

  _wireSvgHeaderActions(data) {
    const hideBtn = this._sectionEl.querySelector('#pb-svg-hide');
    const deleteBtn = this._sectionEl.querySelector('#pb-svg-delete');

    if (hideBtn && data.eid) {
      hideBtn.addEventListener('click', () => {
        const hidden = this._toggleElementVisibility(data.instanceId, data.eid);
        hideBtn.classList.toggle('pb-active', hidden);
        hideBtn.dataset.tooltip = hidden ? 'Show element' : 'Hide element';
        hideBtn.setAttribute('aria-label', hidden ? 'Show element' : 'Hide element');
        hideBtn.innerHTML = this._svgIcon(hidden ? 'eye' : 'eyeOff');
      });
    }

    if (deleteBtn && data.eid) {
      deleteBtn.addEventListener('click', async () => {
        const deleted = await this._deleteElementByEid(data.instanceId, data.eid);
        if (deleted) {
          this._activeSvgData = null;
          this._showSectionInfo({ instanceId: data.instanceId });
        }
      });
    }
  }

  /**
   * Wire live preview (input) and commit (change) handlers for SVG color pickers.
   */
  _wireSvgColorPickers(data) {
    const colorInputs = this._sectionEl.querySelectorAll('.pb-svg-color');
    const hexInputs = this._sectionEl.querySelectorAll('.pb-svg-color-hex');

    for (let i = 0; i < colorInputs.length; i++) {
      const colorInput = colorInputs[i];
      const hexInput = hexInputs[i];
      const oldColor = colorInput.dataset.oldColor;
      const colorToken = colorInput.dataset.colorToken || '';
      const targetId = colorInput.id;

      // Live preview while dragging (no state commit)
      colorInput.addEventListener('input', () => {
        hexInput.value = colorInput.value;
        this._syncColorSwatches(this._sectionEl, targetId, colorInput.value);
        this._previewSvgColor(data, oldColor, colorInput.value, colorToken);
      });

      // Commit on picker close
      colorInput.addEventListener('change', () => {
        this._syncColorSwatches(this._sectionEl, targetId, colorInput.value);
        this._commitSvgColor(data, oldColor, colorInput.value, colorToken);
      });

      // Hex text input commit
      hexInput.addEventListener('change', () => {
        const val = hexInput.value.trim();
        if (isValidCssColor(val, { variables: this._buildSvgColorContext(data).vars })) {
          colorInput.value = colorToPickerHex(val, { fallback: colorInput.value });
          this._syncColorSwatches(this._sectionEl, targetId, val);
          this._commitSvgColor(data, oldColor, val, colorToken);
        } else {
          flashError(hexInput);
          hexInput.value = colorInput.value;
        }
      });

      for (const swatch of this._sectionEl.querySelectorAll(
        `.pb-color-swatch-btn[data-color-target="${targetId}"]`
      )) {
        swatch.addEventListener('click', () => {
          const swatchValue = normalizeHex(
            swatch.dataset.colorHex || swatch.dataset.colorValue || ''
          );
          if (!swatchValue) return;
          colorInput.value = swatchValue;
          hexInput.value = swatchValue;
          this._syncColorSwatches(this._sectionEl, targetId, swatchValue);
          this._commitSvgColor(data, oldColor, swatchValue, colorToken);
        });
      }
      this._syncColorSwatches(this._sectionEl, targetId, colorInput.value);
    }
  }

  /**
   * Send updated SVG to iframe for live preview without committing to state.
   */
  _previewSvgColor(data, oldColor, newColor, colorToken = '') {
    const svgEl = this._getSvgFromSection(data.instanceId, data.editableIndex, data.eid);
    if (!svgEl) return;

    const colorContext = this._buildSvgColorContext(data);
    this._ensureSvgOriginalColors(
      svgEl,
      this._getSvgOriginalColors(
        data,
        svgEl,
        this._extractSvgColors(svgEl, colorContext),
        colorContext
      ),
      colorContext
    );
    this._replaceSvgColor(svgEl, oldColor, newColor, colorToken);
    bridge.send('update-svg', {
      instanceId: data.instanceId,
      eid: data.eid || '',
      editableIndex: data.editableIndex,
      svgMarkup: svgEl.outerHTML
    });
  }

  /**
   * Commit SVG color change to state and refresh the color editor.
   */
  _commitSvgColor(data, oldColor, newColor, colorToken = '') {
    const resolved = resolveSectionByInstance(data.instanceId);
    if (!resolved) return;

    const svgEl = this._getSvgFromSection(data.instanceId, data.editableIndex, data.eid);
    if (!svgEl) return;

    const colorContext = this._buildSvgColorContext(data);
    const originalColors = this._getSvgOriginalColors(
      data,
      svgEl,
      this._extractSvgColors(svgEl, colorContext),
      colorContext
    );
    this._ensureSvgOriginalColors(svgEl, originalColors, colorContext);
    this._replaceSvgColor(svgEl, oldColor, newColor, colorToken);

    // Strip injected editable attributes before storing
    const updatedSvg = svgEl.cloneNode(true);
    for (const el of updatedSvg.querySelectorAll('[data-pb-editable]')) {
      el.removeAttribute('data-pb-editable');
    }
    updatedSvg.removeAttribute('data-pb-editable');

    // Replace SVG in section HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${resolved.section.html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    injectEditableAttrs(root);
    let target = null;
    if (data.eid) {
      target = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(data.eid)}"]`);
    }
    if (!target) {
      const allEditables = root.querySelectorAll('[data-pb-editable]');
      target = allEditables[data.editableIndex];
    }
    if (!target) return;

    target.parentNode.replaceChild(doc.importNode(updatedSvg, true), target);

    stripTempAttrs(root);
    store.update(resolved.path, root.innerHTML, 'Change SVG color');
    bridge.send('update-svg', {
      instanceId: data.instanceId,
      eid: data.eid || '',
      editableIndex: data.editableIndex,
      svgMarkup: svgEl.outerHTML
    });

    // Refresh color editor with new colors
    this._showSvgEditingInfo(data);
  }

  /**
   * Get the SVG element from a section's HTML by instanceId and editableIndex.
   */
  _getSvgFromHTML(html, editableIndex, eid = '') {
    if (!html) return null;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return null;
    injectEditableAttrs(root);

    let el = null;
    if (eid) {
      el = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
    }
    if (!el) {
      const allEditables = root.querySelectorAll('[data-pb-editable]');
      if (editableIndex >= allEditables.length) return null;
      el = allEditables[editableIndex];
    }
    if (el.tagName.toLowerCase() !== 'svg') return null;
    return el;
  }

  _getSvgFromSection(instanceId, editableIndex, eid = '') {
    const resolved = resolveSectionByInstance(instanceId);
    if (!resolved) return null;
    return this._getSvgFromHTML(resolved.section.html, editableIndex, eid);
  }

  _getOriginalSvgFromSection(data) {
    const resolved = resolveSectionByInstance(data?.instanceId);
    const originalHtml = resolved?.section?.originalHtml;
    if (typeof originalHtml !== 'string' || !originalHtml.trim()) return null;
    return this._getSvgFromHTML(originalHtml, data?.editableIndex, data?.eid || '');
  }

  _getRenderableSectionsForButtonSample() {
    const state = store.getState();
    if (!state) return [];
    const activePage = state.pages?.find((p) => p.id === state.activePage) || state.pages?.[0];
    const pageSections = Array.isArray(activePage?.sections) ? activePage.sections : [];
    const shared = state.sharedSections || {};
    return [...pageSections, shared.navigation, shared.footer].filter(Boolean);
  }

  _resolveDesignButtonValue(value, fallback = '') {
    const raw = String(value || '').trim();
    if (!raw) return fallback;
    const theme = getCurrentTheme() || {};
    return raw
      .replace(/var\(\s*(--[A-Za-z0-9_-]+)\s*(?:,\s*([^)]+))?\)/g, (_match, name, varFallback) => {
        return theme[name] || String(varFallback || '').trim() || fallback || raw;
      })
      .trim();
  }

  _normalizeDesignColor(value, fallback = '') {
    const resolved = this._resolveDesignButtonValue(value, fallback);
    const hex = normalizeHex(resolved);
    return hex || resolved || fallback;
  }

  _buttonSelectorHasBaseClass(selectorText, className) {
    if (!selectorText || !className) return false;
    return selectorText.split(',').some((part) => {
      const selector = part.trim();
      if (!selector.includes(`.${className}`)) return false;
      return !/:(before|after|hover|focus|active)\b/i.test(selector);
    });
  }

  _extractButtonStylesFromCSS(css, className = 'cs-button-solid') {
    const styles = {};
    const source = String(css || '');
    if (!source.includes(className)) return styles;
    const props = [
      ['backgroundColor', 'background-color'],
      ['backgroundColor', 'background'],
      ['textColor', 'color'],
      ['borderRadius', 'border-radius'],
      ['padding', 'padding'],
      ['lineHeight', 'line-height'],
      ['fontSize', 'font-size'],
      ['fontWeight', 'font-weight'],
      ['textTransform', 'text-transform'],
      ['letterSpacing', 'letter-spacing'],
      ['border', 'border'],
      ['boxShadow', 'box-shadow']
    ];

    for (const chunk of source.split('}')) {
      if (!chunk.includes(className)) continue;
      if (isDisplayNoneRule(chunk)) continue;
      const brace = chunk.lastIndexOf('{');
      if (brace === -1) continue;
      const selectorText = chunk.slice(0, brace);
      if (!this._buttonSelectorHasBaseClass(selectorText, className)) continue;
      const declarations = chunk.slice(brace + 1);
      for (const [field, cssProp] of props) {
        if (styles[field]) continue;
        const value = matchCSSDeclaration(declarations, cssProp);
        if (!value) continue;
        if (field === 'backgroundColor' && cssProp === 'background' && /gradient\(/i.test(value))
          continue;
        styles[field] = value;
      }
    }
    return styles;
  }

  _findTemplateButtonSample() {
    const sections = this._getRenderableSectionsForButtonSample();
    const parser = typeof DOMParser !== 'undefined' ? new DOMParser() : null;
    if (!parser) return null;

    for (const section of sections) {
      const html = String(section?.html || '');
      if (!html.includes('cs-button-solid') && !html.includes('cs-button-transparent')) continue;
      const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
      const candidates = Array.from(
        doc.querySelectorAll(
          'a.cs-button-solid, button.cs-button-solid, a.cs-button-transparent, button.cs-button-transparent'
        )
      );
      const candidate =
        candidates.find((el) => !el.classList.contains('cs-nav-button')) || candidates[0];
      if (!candidate) continue;
      const className = candidate.classList.contains('cs-button-transparent')
        ? 'cs-button-transparent'
        : 'cs-button-solid';
      const text = candidate.textContent.replace(/\s+/g, ' ').trim();
      return {
        text: text || 'Button',
        className,
        sourceLabel: section.label || section.category || 'Template button'
      };
    }
    return null;
  }

  _getTemplateButtonCSS() {
    const state = store.getState();
    if (!state) return '';
    const sections = this._getRenderableSectionsForButtonSample();
    return [this._templateCSSCache.css || '', ...sections.map((section) => section.css || '')].join(
      '\n'
    );
  }

  _getDesignButtonPreviewModel() {
    const sample = this._findTemplateButtonSample() || {
      text: 'Button',
      className: 'cs-button-solid',
      sourceLabel: 'Default button'
    };
    const templateStyles = this._extractButtonStylesFromCSS(
      this._getTemplateButtonCSS(),
      sample.className
    );
    const buttonStyles = store.getState()?.buttonStyles || {};
    const base = {
      backgroundColor: templateStyles.backgroundColor || 'var(--primary)',
      textColor: templateStyles.textColor || 'var(--bodyTextColorWhite)',
      borderRadius: templateStyles.borderRadius || '0',
      padding: templateStyles.padding || '0 1.5rem',
      lineHeight: templateStyles.lineHeight || '3rem',
      fontSize: templateStyles.fontSize || '1rem',
      fontWeight: templateStyles.fontWeight || '700',
      textTransform: templateStyles.textTransform || '',
      letterSpacing: templateStyles.letterSpacing || '',
      border: templateStyles.border || '0',
      boxShadow: templateStyles.boxShadow || ''
    };
    const styles = {
      ...base,
      backgroundColor: this._normalizeDesignColor(
        buttonStyles.backgroundColor || base.backgroundColor,
        '#ff6a3e'
      ),
      textColor: this._normalizeDesignColor(buttonStyles.textColor || base.textColor, '#ffffff'),
      borderRadius: buttonStyles.borderRadius || base.borderRadius,
      padding: buttonStyles.padding || base.padding
    };
    return {
      ...sample,
      baseStyles: base,
      styles,
      hoverEffect: buttonStyles.hoverEffect || DESIGN_BUTTON_DEFAULTS.hoverEffect
    };
  }

  _buttonPreviewStyleAttribute(styles = {}) {
    const borderValue =
      String(styles.border || '')
        .trim()
        .toLowerCase() === 'none'
        ? '0'
        : styles.border;
    const declarations = [
      ['background', styles.backgroundColor],
      ['color', styles.textColor],
      ['border-radius', styles.borderRadius],
      ['padding', styles.padding],
      ['line-height', styles.lineHeight],
      ['font-size', styles.fontSize],
      ['font-weight', styles.fontWeight],
      ['text-transform', styles.textTransform],
      ['letter-spacing', styles.letterSpacing],
      ['border', borderValue],
      ['box-shadow', styles.boxShadow]
    ].filter(([, value]) => value);
    return declarations
      .map(([prop, value]) => `${prop}:${String(value).replace(/["<>]/g, '')}`)
      .join(';');
  }

  _applyDesignButtonPreview(preview, model = this._getDesignButtonPreviewModel()) {
    if (!preview) return;
    preview.textContent = model.text;
    preview.dataset.templateClass = model.className;
    preview.dataset.templateSource = model.sourceLabel;
    preview.style.background = model.styles.backgroundColor;
    preview.style.color = model.styles.textColor;
    preview.style.borderRadius = model.styles.borderRadius;
    preview.style.padding = model.styles.padding;
    preview.style.lineHeight = model.styles.lineHeight;
    preview.style.fontSize = model.styles.fontSize;
    preview.style.fontWeight = model.styles.fontWeight;
    preview.style.textTransform = model.styles.textTransform || '';
    preview.style.letterSpacing = model.styles.letterSpacing || '';
    preview.style.border =
      String(model.styles.border || '')
        .trim()
        .toLowerCase() === 'none'
        ? '0'
        : model.styles.border || '0';
    preview.style.boxShadow = model.styles.boxShadow || '';
  }

  _getButtonStyle(field) {
    const state = store.getState();
    if (!state) return '';
    return state.buttonStyles ? state.buttonStyles[field] || '' : '';
  }

  _setButtonStyle(field, value) {
    const state = store.getState();
    const buttonStyles = { ...(state.buttonStyles || {}) };
    // Drop any prior origin marker so it doesn't count toward emptiness; a panel
    // edit is always an explicit user gesture, so re-tag origin:'user' below.
    delete buttonStyles.origin;
    if (value) buttonStyles[field] = value;
    else delete buttonStyles[field];
    const result =
      Object.keys(buttonStyles).length > 0 ? { ...buttonStyles, origin: 'user' } : null;
    store.update('buttonStyles', result, `Change button ${field}`);
    eventBus.emit('theme:override-changed');
  }

  // Phase 7b — repaint the mini "Aa" preview inside each preset card so
  // the user sees how the candidate value looks composed with the other
  // committed button styles. The design board passes the Buttons section body
  // as an explicit scope.
  _updateMiniPreviews(scope) {
    const root = scope || (this._board ? this._activeDesignScope('buttons') : null);
    if (!root) return;
    const buttonModel = this._getDesignButtonPreviewModel();
    const bg = buttonModel.styles.backgroundColor;
    const textColor = buttonModel.styles.textColor;
    const radius = buttonModel.styles.borderRadius;
    const padding = buttonModel.styles.padding;
    const baseRadius = buttonModel.baseStyles.borderRadius || '0';
    const basePadding = buttonModel.baseStyles.padding || '0 1.5rem';

    for (const card of root.querySelectorAll('.pb-btn-preset-card')) {
      const mini = card.querySelector('.pb-btn-preset-mini');
      if (!mini) continue;
      const field = card.dataset.field;
      const val = card.dataset.value;
      mini.style.background = bg;
      mini.style.color = textColor;
      mini.style.borderRadius = field === 'borderRadius' ? val || baseRadius : radius;
      mini.style.padding = field === 'padding' ? val || basePadding : padding;
    }
  }

  _getElementOverride(instanceId, eid, field) {
    const resolved = resolveSectionByInstance(instanceId);
    const section = resolved?.section;
    if (!section) return '';
    const viewport = field === 'fontSize' ? getActiveViewportName() : null;
    const el = getElementOverridesForViewport(section, eid, viewport);
    return el ? el[field] || '' : '';
  }

  _toPixels(value, rootPx = 16) {
    if (!value) return NaN;
    const v = String(value).trim().toLowerCase();
    if (!v || v === 'none' || v === 'normal' || v === 'auto') return NaN;
    if (v.endsWith('px')) return parseFloat(v);
    if (v.endsWith('rem')) return parseFloat(v) * rootPx;
    if (v.endsWith('em')) return parseFloat(v) * rootPx;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  }

  _closestPresetLengthValue(presets, computedValue, rootFontSize = '16px') {
    const raw = String(computedValue || '')
      .trim()
      .toLowerCase();
    if (!raw || raw === 'none' || raw === 'normal' || raw === 'auto') return '';
    if (raw === '100%' && presets.some((p) => p.value === '100%')) return '100%';

    const rootPx = this._toPixels(rootFontSize, 16) || 16;
    const targetPx = this._toPixels(raw, rootPx);
    if (!Number.isFinite(targetPx) || targetPx <= 0) return '';

    let best = '';
    let bestDiff = Infinity;
    for (const preset of presets) {
      const value = String(preset.value || '')
        .trim()
        .toLowerCase();
      if (!value || value === 'auto' || value === '100%') continue;
      const px = this._toPixels(value, rootPx);
      if (!Number.isFinite(px)) continue;
      const diff = Math.abs(px - targetPx);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = preset.value;
      }
    }
    return best;
  }

  _normalizeWeightPreset(weightValue) {
    const n = parseInt(String(weightValue || '').trim(), 10);
    if (!Number.isFinite(n)) return '';
    return n >= 600 ? '700' : '400';
  }

  _normalizeObjectFitPreset(fitValue) {
    const v = String(fitValue || '')
      .trim()
      .toLowerCase();
    if (v === 'cover' || v === 'contain' || v === 'fill') return v;
    return '';
  }

  _normalizeSvgAspectPreset(value) {
    const v = String(value || '')
      .trim()
      .toLowerCase();
    if (v === 'xmidymid meet') return 'xMidYMid meet';
    if (v === 'xmidymid slice') return 'xMidYMid slice';
    if (v === 'none') return 'none';
    return '';
  }

  _normalizeSvgHeightPreset(computedHeight, presets, rootFontSize = '16px') {
    const raw = String(computedHeight || '')
      .trim()
      .toLowerCase();
    if (raw === 'auto' && presets.some((p) => p.value === 'auto')) return 'auto';
    return this._closestPresetLengthValue(presets, computedHeight, rootFontSize);
  }

  _resolveImageSizeDetectionSource(data = {}, imageInfo = null, isIconClass = false) {
    const iconAttrWidth = parseFloat(String(imageInfo?.widthAttr || '').trim());
    if (isIconClass && Number.isFinite(iconAttrWidth) && iconAttrWidth > 0) {
      return `${iconAttrWidth}px`;
    }

    const computedWidth = String(data?.computedWidth || '')
      .trim()
      .toLowerCase();
    if (
      computedWidth &&
      computedWidth !== 'auto' &&
      computedWidth !== 'none' &&
      computedWidth !== '0' &&
      computedWidth !== '0px'
    ) {
      return data.computedWidth;
    }

    const pictureWidth = String(data?.pictureComputedWidth || '')
      .trim()
      .toLowerCase();
    if (
      pictureWidth &&
      pictureWidth !== 'auto' &&
      pictureWidth !== 'none' &&
      pictureWidth !== '0' &&
      pictureWidth !== '0px'
    ) {
      return data.pictureComputedWidth;
    }

    const computedMax = String(data?.computedMaxWidth || '')
      .trim()
      .toLowerCase();
    if (
      computedMax &&
      computedMax !== 'none' &&
      computedMax !== 'auto' &&
      computedMax !== '100%' &&
      computedMax !== '100vw'
    ) {
      return data.computedMaxWidth;
    }

    const pictureMax = String(data?.pictureComputedMaxWidth || '')
      .trim()
      .toLowerCase();
    if (
      pictureMax &&
      pictureMax !== 'none' &&
      pictureMax !== 'auto' &&
      pictureMax !== '100%' &&
      pictureMax !== '100vw'
    ) {
      return data.pictureComputedMaxWidth;
    }

    return (
      data?.computedMaxWidth ||
      data?.pictureComputedMaxWidth ||
      data?.computedWidth ||
      data?.pictureComputedWidth ||
      ''
    );
  }

  _elementPresetRow(
    label,
    field,
    presets,
    instanceId,
    eid,
    fallbackValue = '',
    extraButtonAttrs = ''
  ) {
    if (this._getSharedType(instanceId)) return '';
    const current = this._getElementOverride(instanceId, eid, field);
    const activeValue = current || '';
    const attrs = extraButtonAttrs ? ` ${extraButtonAttrs}` : '';
    const buttons = presets
      .map(
        (p) =>
          `<button class="pb-spacing-preset pb-el-preset${activeValue === p.value ? ' pb-active' : ''}" data-eid="${eid}" data-instance="${instanceId}" data-field="${field}" data-value="${p.value}"${attrs}>${p.name}</button>`
      )
      .join('');
    return `<div class="pb-override-row">
      <label class="pb-props-label">${label}</label>
      <div class="pb-spacing-presets">${buttons}</div>
    </div>`;
  }

  _wireElementPresets() {
    for (const btn of this._sectionEl.querySelectorAll('.pb-el-preset')) {
      btn.addEventListener('click', () => {
        const { instance, eid, field, value } = btn.dataset;
        const current = this._getElementOverride(instance, eid, field);
        const isActive = current === value && value !== '';
        const newVal = isActive ? null : value;
        const changes = { [field]: newVal };
        if (btn.dataset.svgWide === 'true' && field === 'height' && newVal && newVal !== 'auto') {
          const currentWidth = this._getElementOverride(instance, eid, 'width');
          if (!currentWidth || /^\d+(?:\.\d+)?px$/i.test(currentWidth)) {
            changes.width = '100%';
          }
        }
        this._applyElementOverrides(instance, eid, changes, `Change element ${field}`);
        const row = btn.parentElement;
        for (const b of row.querySelectorAll('.pb-el-preset')) {
          b.classList.toggle('pb-active', b.dataset.value === (newVal || ''));
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'width')) {
          const widthRow = this._sectionEl.querySelector(
            '.pb-el-preset[data-field="width"]'
          )?.parentElement;
          if (widthRow) {
            for (const b of widthRow.querySelectorAll('.pb-el-preset')) {
              b.classList.toggle('pb-active', b.dataset.value === changes.width);
            }
          }
        }
      });
    }
  }

  _elementActionsHTML(instanceId, eid) {
    if (!eid || this._getSharedType(instanceId)) return '';
    const isHidden = this._getElementOverride(instanceId, eid, 'hidden') === true;
    return `
      <div class="pb-props-section">
        <div class="pb-props-section-title">Element</div>
        <div class="pb-props-actions">
          <button class="pb-props-btn" id="pb-toggle-visibility">${isHidden ? 'Show' : 'Hide'}</button>
          <button class="pb-props-btn pb-props-btn-danger" id="pb-delete-element" data-tooltip="Delete element">Delete</button>
        </div>
      </div>`;
  }

  _wireElementActions(instanceId, eid) {
    if (!eid) return;
    const hideBtn = this._sectionEl.querySelector('#pb-toggle-visibility');
    const deleteBtn = this._sectionEl.querySelector('#pb-delete-element');

    if (hideBtn) {
      hideBtn.addEventListener('click', () => {
        const hidden = this._toggleElementVisibility(instanceId, eid);
        hideBtn.textContent = hidden ? 'Show' : 'Hide';
      });
    }

    if (deleteBtn) {
      deleteBtn.addEventListener('click', async () => {
        const deleted = await this._deleteElementByEid(instanceId, eid);
        if (deleted) this._showSectionInfo({ instanceId });
      });
    }
  }

  _toggleElementVisibility(instanceId, eid) {
    const currentlyHidden = this._getElementOverride(instanceId, eid, 'hidden') === true;
    const nextHidden = !currentlyHidden;
    this._applyElementOverride(instanceId, eid, 'hidden', nextHidden ? true : null);
    return nextHidden;
  }

  async _deleteElementByEid(instanceId, eid) {
    const confirmed = await showConfirmModal({
      title: 'Delete Element',
      message: 'Delete this element?',
      details: 'You can undo this action with Ctrl+Z (Cmd+Z on Mac).',
      confirmLabel: 'Delete Element',
      cancelLabel: 'Cancel',
      danger: true
    });
    if (!confirmed) return false;

    const resolved = resolveSectionByInstance(instanceId);
    if (!resolved) return false;

    const updatedHtml = deleteEditableFromHTML(resolved.section.html, eid);
    if (updatedHtml === null) return false;

    const section = resolved.section;
    let prunedOverrides = section.overrides ? { ...section.overrides } : null;
    if (prunedOverrides && prunedOverrides.elements) {
      const prunedElements = pruneOrphanOverrides(updatedHtml, prunedOverrides.elements);
      if (prunedElements) {
        prunedOverrides.elements = prunedElements;
      } else {
        delete prunedOverrides.elements;
      }
      if (Object.keys(prunedOverrides).length === 0) prunedOverrides = null;
    }
    if (prunedOverrides?.responsive) {
      const responsive = structuredClone(prunedOverrides.responsive);
      for (const viewport of RESPONSIVE_VIEWPORTS) {
        const bucket = responsive[viewport];
        if (!bucket?.elements) continue;
        const elements = pruneOrphanOverrides(updatedHtml, bucket.elements);
        if (elements) bucket.elements = elements;
        else delete bucket.elements;
        if (!Object.keys(bucket).length) delete responsive[viewport];
      }
      if (Object.keys(responsive).length) prunedOverrides.responsive = responsive;
      else delete prunedOverrides.responsive;
      if (!Object.keys(prunedOverrides).length) prunedOverrides = null;
    }

    store.beginTransaction('Delete element');
    store.update(resolved.path, updatedHtml);
    if (section.overrides) {
      store.update(resolved.overridesPath, prunedOverrides);
    }
    store.commitTransaction();

    showUndoToast('Element deleted');
    eventBus.emit('preview:render-request', { preserveScroll: true });
    return true;
  }

  _spacingPresetButtons(currentValue) {
    return SPACING_PRESETS.map(
      (p) =>
        `<button class="pb-spacing-preset${currentValue === p.value ? ' pb-active' : ''}" data-value="${p.value}">${p.name}</button>`
    ).join('');
  }

  /**
   * Apply a per-element override (image size, text weight, hidden, etc.).
   * Stores in section.overrides.elements[eid][field] = value.
   * Passing null/undefined for value clears that field.
   */
  _applyElementOverride(instanceId, eid, field, value) {
    this._applyElementOverrides(instanceId, eid, { [field]: value }, `Change element ${field}`);
  }

  _applyElementOverrides(instanceId, eid, changes, label = 'Change element') {
    const fields = Object.keys(changes || {});
    applyElementOverrides(instanceId, eid, changes, {
      label,
      source: 'property-panel',
      viewport:
        fields.length > 0 && fields.every((field) => field === 'fontSize')
          ? getResponsiveOverrideViewport()
          : null
    });

    // For preserveAspectRatio, send live update to iframe (non-CSS attribute)
    if (
      Object.prototype.hasOwnProperty.call(changes || {}, 'preserveAspectRatio') &&
      this._activeSvgData
    ) {
      bridge.send('update-svg-attr', {
        instanceId,
        eid,
        editableIndex: this._activeSvgData.editableIndex,
        attr: 'preserveAspectRatio',
        value: changes.preserveAspectRatio || ''
      });
    }
  }

  /**
   * Extract unique solid fill/stroke colors from an SVG element.
   * Skips non-solid values: none, currentColor, url(#...), var(--...), transparent, inherit.
   */
  _collectSvgStyleColors(styleText, processValue) {
    if (styleText) collectStylesheetPaintValues(styleText, processValue);
  }

  _buildSvgColorContext(data = null, options = {}) {
    const vars = {};
    const state = store.getState() || {};

    for (const [key, value] of Object.entries(state.theme || {})) {
      if (key.startsWith('--') && typeof value === 'string') {
        vars[key] = value.trim();
      }
    }

    const liveTemplateCSS = this._templateCSSCache?.css || '';
    if (liveTemplateCSS) {
      this._collectCssCustomProperties(liveTemplateCSS, vars);
    }

    const resolved = resolveSectionByInstance(data?.instanceId);
    if (typeof resolved?.section?.css === 'string') {
      this._collectCssCustomProperties(resolved.section.css, vars);
    }
    const htmlSource = Object.prototype.hasOwnProperty.call(options, 'html')
      ? options.html
      : resolved?.section?.html;
    if (typeof htmlSource === 'string') {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${htmlSource}</div>`, 'text/html');
        for (const el of doc.body.querySelectorAll('[style]')) {
          this._collectInlineCssCustomProperties(el.getAttribute('style') || '', vars);
        }
      } catch {
        // Best-effort only; section CSS still covers the CodeStitch divider cases.
      }
    }

    return { vars };
  }

  _collectInlineCssCustomProperties(styleText, vars) {
    if (styleText && vars) collectInlineCustomProperties(styleText, vars);
  }

  _collectCssCustomProperties(cssText, vars) {
    if (cssText && vars) collectStylesheetCustomProperties(cssText, vars);
  }

  _resolveCssVariableColor(value, colorContext, _ctx, depth = 0) {
    if (!value || depth > 8) return null;
    const trimmed = String(value).trim();
    const ref = parseCssVarReference(trimmed);
    if (!ref) return null;
    const token = ref.token;
    const vars = colorContext?.vars || {};
    const resolved = resolveCssColorValue(trimmed, vars);
    if (!resolved) return null;

    const nested = this._normalizeSvgColorValue(resolved, colorContext, null, depth + 1);
    return nested?.hex ? { hex: nested.hex, token } : null;
  }

  _normalizeSvgColorValue(value, colorContext, ctx, depth = 0) {
    const SKIP = /^(none|currentcolor|transparent|inherit|initial|unset)$/i;
    const NON_SOLID = /^url\(/i;
    if (!value) return null;

    const trimmed = String(value).trim();
    if (!trimmed || SKIP.test(trimmed) || NON_SOLID.test(trimmed)) return null;

    if (/^var\(/i.test(trimmed)) {
      return this._resolveCssVariableColor(trimmed, colorContext, ctx, depth);
    }

    const hex = colorToHex(trimmed, { includeAlpha: 'auto' });
    return hex ? { hex } : null;
  }

  _extractSvgColors(svgEl, colorContext = null) {
    const SKIP = /^(none|currentcolor|transparent|inherit|initial|unset)$/i;
    const colorMap = new Map();
    const scopedContext = { vars: { ...(colorContext?.vars || {}) } };
    const allEls = [svgEl, ...svgEl.querySelectorAll('*')];

    for (const el of allEls) {
      this._collectInlineCssCustomProperties(el.getAttribute('style') || '', scopedContext.vars);
    }

    const processValue = (val) => {
      if (!val || SKIP.test(String(val).trim())) return;
      const parsed = this._normalizeSvgColorValue(val, scopedContext, null);
      if (!parsed?.hex) return;
      const key = parsed.hex.toLowerCase();
      if (!colorMap.has(key)) colorMap.set(key, { hex: parsed.hex, count: 0, tokens: new Set() });
      const entry = colorMap.get(key);
      entry.count++;
      if (parsed.token) entry.tokens.add(parsed.token);
    };

    for (const el of allEls) {
      processValue(el.getAttribute('fill'));
      processValue(el.getAttribute('stroke'));

      const style = el.getAttribute('style');
      if (style) {
        processValue(el.style?.getPropertyValue('fill'));
        processValue(el.style?.getPropertyValue('stroke'));
      }
    }
    for (const styleEl of svgEl.querySelectorAll('style')) {
      this._collectSvgStyleColors(styleEl.textContent || '', processValue);
    }

    return Array.from(colorMap.values()).map((entry) => ({
      hex: entry.hex,
      count: entry.count,
      token: Array.from(entry.tokens)[0] || ''
    }));
  }

  _getStoredSvgOriginalColors(svgEl) {
    if (!svgEl) return [];
    const raw = svgEl.getAttribute('data-pb-original-colors') || '';
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const normalized = parsed
            .map((value) => colorToHex(value || '', { includeAlpha: 'auto' }))
            .filter(Boolean);
          if (normalized.length) return normalized;
        }
      } catch {
        // Fall through to current colors if the metadata is malformed.
      }
    }
    return [];
  }

  _getSvgOriginalColors(data, svgEl, currentColors = [], colorContext = null) {
    const originalSvg = this._getOriginalSvgFromSection(data);
    if (originalSvg) {
      const resolved = resolveSectionByInstance(data?.instanceId);
      const originalHtml = resolved?.section?.originalHtml || '';
      const sourceContext = this._buildSvgColorContext(data, { html: originalHtml });
      const sourceColors = this._extractSvgColors(originalSvg, sourceContext)
        .map((color) => colorToHex(color?.hex || '', { includeAlpha: 'auto' }))
        .filter(Boolean);
      if (sourceColors.length) return sourceColors;
    }

    const stored = this._getStoredSvgOriginalColors(svgEl);
    if (stored.length) return stored;

    return currentColors
      .map((color) => colorToHex(color?.hex || '', { includeAlpha: 'auto' }))
      .filter(Boolean);
  }

  _ensureSvgOriginalColors(svgEl, originalColors = null, colorContext = null) {
    if (!svgEl) return;
    const supplied = Array.isArray(originalColors)
      ? originalColors
          .map((value) => colorToHex(value || '', { includeAlpha: 'auto' }))
          .filter(Boolean)
      : [];
    if (supplied.length) {
      svgEl.setAttribute('data-pb-original-colors', JSON.stringify(supplied));
      return;
    }

    const existing = this._getStoredSvgOriginalColors(svgEl);
    if (existing.length) return;

    const currentColors = this._extractSvgColors(svgEl, colorContext)
      .map((color) => colorToHex(color.hex || '', { includeAlpha: 'auto' }))
      .filter(Boolean);
    if (currentColors.length) {
      svgEl.setAttribute('data-pb-original-colors', JSON.stringify(currentColors));
    }
  }

  /**
   * Replace all occurrences of oldColor with newColor in SVG fill/stroke attributes and inline styles.
   * Compares normalized hex values. Skips non-solid values.
   */
  _replaceSvgStyleText(styleText, oldNorm, newColor, normalize) {
    if (!styleText || !oldNorm) return styleText;
    return replaceStylesheetPaintValues(
      styleText,
      (value) => normalize(value) === oldNorm,
      newColor
    ).css;
  }

  _replaceSvgColor(svgEl, oldColor, newColor, colorToken = '') {
    const SKIP = /^(none|currentcolor|transparent|inherit|initial|unset)$/i;
    const NON_SOLID = /^(url\(|var\()/i;
    const normalize = (val) => {
      if (!val || SKIP.test(val.trim()) || NON_SOLID.test(val.trim())) return null;
      return colorToHex(val.trim(), { includeAlpha: 'auto' }) || null;
    };

    const oldNorm = normalize(oldColor);
    if (!oldNorm) return;

    if (
      colorToken &&
      colorToken.startsWith('--') &&
      svgEl.style &&
      typeof svgEl.style.setProperty === 'function'
    ) {
      svgEl.style.setProperty(colorToken, newColor);
    }

    const allEls = [svgEl, ...svgEl.querySelectorAll('*')];
    for (const el of allEls) {
      for (const attr of ['fill', 'stroke']) {
        const val = el.getAttribute(attr);
        if (val && normalize(val) === oldNorm) {
          el.setAttribute(attr, newColor);
          if (el.style && typeof el.style.setProperty === 'function') {
            el.style.setProperty(attr, newColor);
          }
        }
      }

      const style = el.getAttribute('style');
      if (style) {
        for (const property of ['fill', 'stroke']) {
          const value = el.style?.getPropertyValue(property);
          if (value && normalize(value) === oldNorm) {
            const priority = el.style.getPropertyPriority(property);
            el.style.setProperty(property, newColor, priority);
          }
        }
      }
    }

    for (const styleEl of svgEl.querySelectorAll('style')) {
      const current = styleEl.textContent || '';
      const updated = this._replaceSvgStyleText(current, oldNorm, newColor, normalize);
      if (updated !== current) styleEl.textContent = updated;
    }
  }

  _wireDisclosureAria(root = this._sectionEl) {
    for (const details of root.querySelectorAll('details')) {
      const summary = details.querySelector('summary');
      if (!summary) continue;

      const body = summary.nextElementSibling;
      if (body) {
        if (!body.id) {
          body.id = `pb-disclosure-${crypto.randomUUID().slice(0, 8)}`;
        }
        summary.setAttribute('aria-controls', body.id);
      }

      const syncExpanded = () => summary.setAttribute('aria-expanded', String(details.open));
      syncExpanded();
      details.addEventListener('toggle', syncExpanded);
    }
  }

  get selectedInstance() {
    return this._selectedInstance;
  }
}
